/**
 * GENERATED FILE — DO NOT HAND-EDIT.
 * Produced by context/scripts/publish-glossary.mjs from docs/glossary.json.
 * Run: node context/scripts/publish-glossary.mjs
 */

export const GLOSSARY_ENTRIES_INTERNAL = [
  {
    "id": "activation",
    "term": "Activation",
    "aliases": [
      "activations",
      "activation event"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "One certificate becoming active. This is the unit Vera bills for, counted in the billing period it happens in.",
    "long": "A certificate is charged once, when it activates — not when it was created and not when its tag was made. What causes that is the [activation trigger](term:activation-trigger) that applies to it — the company default, or an override on its product or on the certificate itself, so a brand on On sale is charged as items sell while a brand on On creation is charged as certificates are minted; a brand can also activate a certificate directly from the platform, which counts the same. Activations covered by your [included](term:included-activations) or [granted](term:granted-activations) balance are not billed; the rest are priced on the [graduated ladder](term:graduated-activation-rate). One activation is one certificate everywhere except an [indivisible certificate group](term:indivisible-group), which goes live together and is [charged as a single activation](term:group-activation) however many certificates that is. [Sample data](term:intro) and demo certificates are never billed, even when they sit in a company that is.",
    "seeAlso": [
      "activation-trigger",
      "certificate-status-active",
      "included-activations",
      "activation-total-cost",
      "group-activation"
    ],
    "docs": [
      "company-settings/settings-billing-overview",
      "company-settings/settings-features"
    ]
  },
  {
    "id": "activation-price-tier",
    "term": "Activation price tier",
    "aliases": [
      "price tier",
      "next price tier",
      "price break",
      "graduated activation pricing",
      "activation rate tier"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "A volume threshold on the activation pricing ladder. Cross it and every further activation this period costs the next rate down, from $25 to $5.",
    "long": "Pricing is [graduated](term:graduated-activation-rate), meaning each band prices only the [activations](term:activation) that fall inside it. Crossing a threshold makes the next activation cheaper and never re-prices the ones you have already used, which is deliberate: under whole-volume pricing a brand just past a threshold would owe less in total than one just below it, and a bill that falls as usage rises is both unfair and gameable. The practical effect is that your average cost per activation falls as you grow, while any given month's bill still rises with use. It is the rate ladder, not your subscription [plan](term:plan) — the same schedule applies to every plan.",
    "seeAlso": [
      "activation",
      "included-activations",
      "granted-activations",
      "activation-total-cost"
    ],
    "docs": [
      "company-settings/settings-billing-overview"
    ]
  },
  {
    "id": "activation-total-cost",
    "term": "Total activation cost",
    "aliases": [
      "activation charge",
      "activation cost",
      "total activation charge"
    ],
    "kind": "formula",
    "audience": "brand",
    "formula": "all activations this period priced through the rate ladder, less your included allowance credited at the entry rate",
    "unit": "USD",
    "short": "What this period's activations cost. Every activation is priced on the volume ladder, then your plan's included allowance is credited back at the $25 entry rate.",
    "long": "Read it as two steps. First the period's whole [activation](term:activation) count is priced through the [graduated ladder](term:graduated-activation-rate), which gives the gross value; then your [plan's allowance](term:included-activations) is credited back at the ladder's entry rate, capped at what you actually used — consuming 2 of 12 credits saves the value of 2, not of 12. Pricing the ladder on the billable count instead, as though the included ones had never happened, is a different and larger figure. The number covers activations only; your plan's base fee is billed separately.",
    "seeAlso": [
      "activation",
      "activation-price-tier",
      "included-activations",
      "granted-activations"
    ],
    "docs": [
      "company-settings/settings-billing-overview"
    ]
  },
  {
    "id": "activation-trigger",
    "term": "Activation trigger",
    "aliases": [
      "activation triggers",
      "certificate activation"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "The event that makes a certificate Active, and therefore billable. Set for the whole company, or overridden for one product or one certificate.",
    "long": "This is a billing decision as much as a workflow one, because a certificate is charged as an [activation](term:activation) at the moment it activates. On creation activates every certificate as soon as it exists — including a whole [box of tags](term:package) the instant you assign it to a product, which commits the spend for the box in one click. On sale, the default, keeps certificates [inactive](term:certificate-status-inactive) until the customer claims ownership, so cost follows revenue. The company default is set under Settings, and a single product or a single certificate can override it — useful when some items go to retail and others ship direct. The most specific setting wins: certificate, then product, then the company default. On location arrival activates when one of your own locations confirms an item arrived — a shop, a production site, anywhere in your address book. Send an item to a location from its certificate, and confirm arrival there; confirming is what activates it, so nothing is charged while stock is in transit.",
    "seeAlso": [
      "activation",
      "certificate",
      "certificate-status-active",
      "package"
    ],
    "docs": [
      "company-settings/settings-features",
      "company-settings/settings-billing-overview"
    ]
  },
  {
    "id": "ai-query",
    "term": "AI query",
    "aliases": [
      "ask query"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "One question put to Ask, the panel that answers in plain language from your own product and scan data. Each plan lists a monthly number.",
    "long": "Ask answers from your data rather than from the internet, so what it can tell you is bounded by what Vera holds: your products, your certificates, your scans. A question that would need something Vera does not record is one it will decline rather than guess at.",
    "seeAlso": [
      "plan",
      "scan"
    ]
  },
  {
    "id": "allowlist",
    "term": "Allowlist",
    "aliases": [
      "signup allowlist"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "The list of email addresses permitted to create an account. An address that is not on it cannot complete signup.",
    "long": "The allowlist is the gate on self-service signup while access is still being handled by hand, so it fails closed: an address that is not on it gets no account, rather than a pending one for someone to approve. Adding an address does not notify the person, so an entry is only half of an invitation — someone still has to tell them they can now sign up.",
    "seeAlso": [
      "role"
    ]
  },
  {
    "id": "authenticator-app",
    "term": "Authenticator app",
    "aliases": [
      "totp",
      "authenticator",
      "time-based one-time password"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "An app on your phone that generates a new six-digit sign-in code every 30 seconds. It works offline, and the codes are never sent over text.",
    "long": "Setting one up means scanning a QR code once. From then on the app and Vera independently compute the same six-digit code from a shared secret and the current time, so nothing has to be sent anywhere when you sign in.\n\nThat is why it is the stronger of the two options: a text message can be intercepted or redirected to a swapped SIM, while an authenticator app has nothing in transit to intercept. Any standard app works — Google Authenticator, 1Password, Authy and others.",
    "seeAlso": [
      "two-factor-authentication",
      "recovery-code"
    ],
    "docs": [
      "settings/settings-security"
    ]
  },
  {
    "id": "authenticity-verification-rate",
    "term": "Authenticity verification rate",
    "aliases": [
      "authenticity rate"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "The share of scans that proved the item genuine, rather than only showing that a link was opened. Not computed yet; the card shows a dash.",
    "long": "Not every tag can prove itself on each tap, and opening a certificate's web link is recorded as a scan without proving anything — so this is as much a measure of what your tags are capable of as of what people encountered. [Verified](term:scan-status-verified) is what a proof establishes; [opened](term:scan-status-opened) is the outcome that establishes nothing beyond a visit.\n\n⚠️ The card is a placeholder today and renders a dash. The definition is here so that the figure means the same thing on the day it arrives as it does in this sentence.",
    "formula": "Verified scans ÷ all scans in range.",
    "seeAlso": [
      "scan-status-verified",
      "scan-status-opened",
      "verify",
      "invalid-scan-rate"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "average-resale-price",
    "term": "Average resale price",
    "aliases": [
      "avg resale price"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "The average declared price of a completed resale in the period. Resales with no declared price are left out, not counted as zero.",
    "long": "The price is what the seller declared at the time — not a figure Vera verified, and not a payment it processed — so it is only as good as sellers' willingness to state one.\n\nExcluding unpriced resales rather than zeroing them keeps the average honest, but it does mean the figure describes a subset of [Resales](term:resales) and the two counts do not match. Resales declared in a currency other than your reporting currency are left out as well: converting them at today's rate would silently relabel one currency as another.",
    "formula": "Mean declared price over completed resales in range that carry a price.",
    "seeAlso": [
      "resales",
      "sell",
      "resales-per-resold-item"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "brand",
    "term": "Brand",
    "aliases": [
      "brands",
      "company",
      "maker"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "The company that made the item and issued its certificate. Every provenance ends at the brand, because that is where the item started.",
    "long": "The brand is the origin of the record, not merely a name attached to it: the [certificate](term:certificate) exists because the brand created it and paired it to a [tag](term:tag) before the item was sold. That is what makes the [provenance](term:provenance) a chain rather than a claim — read it backwards and every completed handover leads to the company that made the thing. A brand can see that its certificates exist and how often they are scanned; it does not gain any right to the item, and ownership only ever moves by [transfer](term:transfer).",
    "seeAlso": [
      "certificate",
      "product",
      "provenance",
      "owner"
    ]
  },
  {
    "id": "certificate",
    "term": "Certificate",
    "aliases": [
      "certificates",
      "digital certificate"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "The digital record of authenticity for one physical item, owned by a person and paired with a tag.",
    "long": "A certificate stands for one individual item, not for the [product line](term:product) it belongs to: a hundred units of the same handbag are a hundred certificates against one product. It usually exists before the item is finished — certificates are minted for a whole box of [tags](term:tag) when that box is assigned to a product — and it stays [inactive](term:certificate-status-inactive) until the brand makes it live, which for most brands is the moment the item is sold. From then on it is what a scan of its tag resolves to, and what carries the item's owner, its [provenance](term:provenance) and its scan history.",
    "seeAlso": [
      "tag",
      "owner",
      "provenance",
      "activation-trigger"
    ],
    "docs": [
      "certificates/certificate-scans",
      "tags/assign-product-to-package"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "certificate-coverage",
    "term": "Certificate coverage",
    "aliases": [
      "catalogue coverage"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "The share of the products in your catalogue carrying at least one Vera certificate. Not computed yet; the card shows a dash.",
    "long": "Counted over [products](term:product) — designs — and not over units made, so a product with one certificate against a run of ten thousand counts as covered exactly like a fully certified one. It is a measure of catalogue reach, not of how much stock is protected.\n\nIt is also the factor [Return fraud prevented](term:return-fraud-prevented) is scaled by, on the reasoning that there was nothing to verify on goods that were never certified.",
    "formula": "Products with at least one certificate ÷ all products.",
    "seeAlso": [
      "product",
      "certificate",
      "return-fraud-prevented"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "certificate-group",
    "term": "Certificate group",
    "aliases": [
      "certificate groups",
      "grouped certificates",
      "group of certificates"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Several certificates linked under one parent certificate, so an object made of separately tagged parts is tracked and sold as one set.",
    "long": "Some objects are one thing to a customer and several tagged parts to you: a two-piece luggage set, a watch with a separately certified strap, a chair sold with its cushion. A group is how those are held together. Each part keeps its own certificate and its own tag, and a parent certificate is created above them to stand for the whole. Every group is either divisible or indivisible, and that choice is fixed when the group is created. You can build a group as you create the certificates, or group certificates you already hold, as long as none of them has ever been transferred to an owner.",
    "seeAlso": [
      "parent-certificate",
      "child-certificate",
      "divisible-group",
      "indivisible-group",
      "certificate"
    ],
    "docs": [
      "certificates/certificates"
    ]
  },
  {
    "id": "certificate-status-active",
    "term": "Active",
    "aliases": [
      "active status"
    ],
    "kind": "status",
    "audience": "consumer",
    "short": "A certificate that is live for its owner. A certificate reaches this state once the item is sold, or when the brand activates it directly.",
    "long": "Active is what a certificate becomes once it is out in the world with an [owner](term:owner). Before that it sits [inactive](term:certificate-status-inactive) with the [brand](term:brand) that made it, and for most brands the change happens at the moment the item is sold and its first owner claims it. A brand can also make a certificate active directly, without waiting for a sale. It only ever happens once — a certificate that is already active is not activated again.",
    "seeAlso": [
      "certificate-status-inactive",
      "owner",
      "activation",
      "activation-trigger",
      "certificate-status-created"
    ],
    "docs": [
      "company-settings/settings-features"
    ]
  },
  {
    "id": "certificate-status-created",
    "term": "Created",
    "aliases": [
      "created status"
    ],
    "kind": "status",
    "audience": "brand",
    "short": "A certificate that exists but has not yet met its activation trigger, so it is not yet live for an owner.",
    "long": "Created is the resting state for stock made ahead of demand. The item may already be tagged and sitting in a box — certificates are minted with the tags when a [package](term:package) is assigned to a product — but nothing has met the [trigger](term:activation-trigger) yet, so no owner sees it and nothing has been billed. It becomes [Active](term:certificate-status-active) the moment its trigger fires, which for most companies is the customer claiming ownership.",
    "seeAlso": [
      "activation-trigger",
      "certificate-status-active",
      "package"
    ],
    "docs": [
      "company-settings/settings-features"
    ]
  },
  {
    "id": "certificate-status-inactive",
    "term": "Inactive",
    "aliases": [
      "inactive status"
    ],
    "kind": "status",
    "audience": "consumer",
    "short": "A certificate that exists but is not yet live for an owner, normally because the item has not been sold yet.",
    "long": "Inactive is the ordinary resting state of stock, not a fault. Certificates are created ahead of demand — in a batch, when a box of [tags](term:tag) is assigned to a [product](term:product) — so an item can be tagged, boxed and sitting in a warehouse with its certificate already waiting. Nothing about the item is wrong; it simply has no [owner](term:owner) yet. Scanning the tag of an inactive item shows the product it belongs to but no ownership, because there is none to show.",
    "seeAlso": [
      "certificate-status-active",
      "certificate-status-created",
      "certificate"
    ]
  },
  {
    "id": "certificate-status-lost",
    "term": "Lost",
    "aliases": [
      "lost status"
    ],
    "kind": "status",
    "audience": "consumer",
    "short": "An item its owner has reported missing. It stays theirs, and anyone who scans the tag is shown that it has been reported lost.",
    "long": "Reporting an item lost does not give it up: ownership is unchanged and the certificate stays in your account, which is the point — you keep the claim to it while making the loss visible to whoever finds it. Because the report is attached to the [certificate](term:certificate) rather than to the [tag](term:tag), it is seen by anyone who taps the item, so a finder who scans it learns immediately that it is looked for. Marking the item found returns it to its previous state.",
    "seeAlso": [
      "certificate-status-stolen",
      "owner",
      "scan"
    ]
  },
  {
    "id": "certificate-status-stolen",
    "term": "Stolen",
    "aliases": [
      "stolen status"
    ],
    "kind": "status",
    "audience": "consumer",
    "short": "An item its owner has reported stolen. It stays theirs, and anyone who scans the tag is shown that it has been reported stolen.",
    "long": "Stolen is kept separate from [Lost](term:certificate-status-lost) rather than folded into one 'missing' state, because the two mean different things to whoever is holding the item. Lost invites a finder to return it; stolen tells a prospective buyer that the person offering it has no title to it, which is the moment a resale is most likely to be stopped. Ownership does not move either way — an item cannot be [transferred](term:transfer) away from you by being taken — so the certificate remains yours until you transfer it yourself.",
    "seeAlso": [
      "certificate-status-lost",
      "owner",
      "provenance"
    ]
  },
  {
    "id": "certificate-status-transfer",
    "term": "Transfer",
    "aliases": [
      "transfer status",
      "in transfer",
      "pending transfer"
    ],
    "kind": "status",
    "audience": "consumer",
    "short": "A certificate part-way through changing hands. It stays with the current owner until the person receiving it accepts.",
    "long": "This one status covers every stage of a handover in flight — sent and unopened, waiting on the recipient, or with a cancellation requested — because from the outside they amount to the same thing: the item is spoken for but has not moved. Nothing about ownership changes while a certificate sits here, which is what makes a [transfer](term:transfer) safe to send. It resolves in one of two directions: the recipient accepts and becomes the [owner](term:owner), or the transfer is cancelled and the certificate stays exactly where it was.",
    "seeAlso": [
      "transfer",
      "gift",
      "owner",
      "provenance"
    ]
  },
  {
    "id": "certificate-view",
    "term": "Certificate view",
    "aliases": [
      "view",
      "views",
      "share link",
      "custom view",
      "public view"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "A shareable link to one of your certificates, with its own setting for who may open it and how much of the item's detail it shows.",
    "long": "A view exists so that showing someone an item is not the same as giving them everything about it. Each one carries two independent controls: who can open it — anyone holding the link, or only people you have invited by email — and what it reveals, since ownership, its [history](term:provenance), sale prices and scans can each be hidden. You can keep several at once, which is the point: a link for a prospective buyer that shows provenance but not what you paid, and a private one that shows everything.",
    "seeAlso": [
      "certificate",
      "provenance",
      "owner"
    ]
  },
  {
    "id": "child-certificate",
    "term": "Child certificate",
    "aliases": [
      "child certificates"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "A tagged certificate that belongs to a group. It is one of the physical parts the group's object is made of, and it is what a customer actually scans.",
    "long": "Every member of a group other than the parent is a child. A child keeps its own tag, its own product and its own page, and it carries its group's divisibility so any surface showing it knows which kind of group it is in. What a scan of its tag opens depends on that: a divisible child is independently sellable, so its tag opens its own certificate, while an indivisible child's tag opens the parent's certificate instead, because the set is what was sold.",
    "seeAlso": [
      "parent-certificate",
      "group-member",
      "certificate-group",
      "tag"
    ],
    "docs": [
      "certificates/certificates"
    ]
  },
  {
    "id": "comp",
    "term": "Comp",
    "aliases": [
      "comped",
      "complimentary subscription"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "Waiving a company's plan fee for a fixed number of months. It covers the base fee only: activations and tags are still billed.",
    "long": "This is the fact most often got wrong when a comp is offered. Comping the [plan](term:plan) does NOT make the account free — a comped company still pays for every [activation](term:activation) beyond its allowance and for every tag it orders. What it grants for free is the tier itself, including that tier's [included activations](term:included-activations) for the window. A comp always carries a written reason, and the history is append-only, so a company can be comped more than once and the record of why survives.",
    "seeAlso": [
      "subscription",
      "plan",
      "included-activations"
    ]
  },
  {
    "id": "company-kind",
    "term": "Company kind",
    "aliases": [
      "kind"
    ],
    "kind": "column",
    "audience": "internal",
    "short": "What a company IS: standard, demo, or internal. The three are mutually exclusive, and this is separate from is_example, which says whether its data counts.",
    "long": "Kind answers what a company is; is_example answers whether its records should be counted or charged. They are deliberately orthogonal, because an acquisition demo can be provisioned into a company that is otherwise real. Kind is an enum rather than a third boolean beside is_example and is_test precisely because the values are mutually exclusive by construction, so a fourth flavour extends cleanly where a fourth boolean would not.",
    "seeAlso": [
      "demo",
      "company-kind-internal"
    ]
  },
  {
    "id": "company-kind-internal",
    "term": "Internal company",
    "aliases": [
      "Vera company row"
    ],
    "kind": "status",
    "audience": "internal",
    "short": "Vera itself, as a company row. Exactly one exists, enforced by a partial unique index.",
    "long": "It exists because rows that belong to Vera rather than to a brand still need a company: support_requests.company_id is NOT NULL on purpose, since a conversation belongs to a company, so a staffer with no company could not file a bug report at all. Giving Vera its own row preserves that invariant instead of making the column nullable, and gives the internal queue a clean predicate for separating staff-authored reports from the brand queue staff use to serve customers.",
    "seeAlso": [
      "company-kind"
    ]
  },
  {
    "id": "company-activated",
    "term": "Activated",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "activated company",
      "first activation",
      "first activated"
    ],
    "short": "A company's first billable activation: the first certificate it was charged for. A product milestone, not a deal stage; it can come before or after Won.",
    "long": "It is read from the same rows billing charges, by the same rule, so Activated always means the first [activation](term:activation) that was billable: [demo](term:demo) and sample certificates never count, and neither does any certificate of a company marked as example data. It is worked out from those rows rather than stored, so it agrees with billing by construction; the one way it can move is if the certificate behind it is deleted, which takes its activation record with it.\n\nIt is deliberately not a stage of the sales pipeline. [Won](term:sales-won) is about money (a first paid invoice); Activated is about use. A brand can pay a plan fee or order tags and only activate weeks later, or activate certificates covered by its [included activations](term:included-activations) or a [comp](term:comp) before it has paid anything. Read the two together: Won without Activated is a customer not yet using the product; Activated without Won is a customer using it on credit.",
    "seeAlso": [
      "company-activations",
      "activation",
      "sales-won",
      "company-segment"
    ]
  },
  {
    "id": "company-activations",
    "term": "Activations (company)",
    "kind": "formula",
    "audience": "internal",
    "aliases": [
      "activation count",
      "activations count"
    ],
    "formula": "Count of the company's billable activations, all time; beside it, the count in the last 30 days.",
    "short": "How many certificates the company has been charged an activation for, all time, with the last 30 days beside it. Demo and sample certificates are not counted.",
    "seeAlso": [
      "company-activated",
      "activation"
    ]
  },
  {
    "id": "company-segment",
    "term": "Company segment",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "signed up company",
      "signed-up companies",
      "company type"
    ],
    "short": "Which slice of Accounts → Companies a company is in: Signed up, Demo, Example, Test, Internal or Archived. Each company is in exactly one; Signed up is the default.",
    "long": "Signed up means a real company: standard [kind](term:company-kind), not test, not example data, not archived. The others are, in this order of precedence: Archived (whatever else it is), Internal (Vera itself), [Demo](term:demo), Test, and Example (sample tenants such as Maison Vera and the monitoring canary). Because every company is in exactly one, the chips add up to the whole list and selecting all of them is the same as no filter. The badge in a row says what a company is, so an archived demo still reads Demo while it sits under Archived.",
    "seeAlso": [
      "company-kind",
      "demo",
      "company-activated"
    ]
  },
  {
    "id": "console-trial",
    "term": "Console trial",
    "aliases": [
      "trial"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "The clock on a demo company's access to the platform. It starts when the first invited admin actually signs in, not when the invite is sent.",
    "long": "It starts on sign-in deliberately: an invite that is never opened should not burn the window. Expiry blocks the console only, and only while there is no active [subscription](term:subscription) — paying always overrides an expired trial, and the endpoints needed to pay stay open so an expired company is never locked out of the one action that fixes it. It is a different clock from the one on the public [certificate](term:certificate), and conflating the two is the usual mistake.",
    "seeAlso": [
      "demo",
      "subscription",
      "takeover"
    ]
  },
  {
    "id": "counterfeit-deflections",
    "term": "Counterfeit deflections",
    "aliases": [
      "deflections"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "An estimate of how many counterfeit encounters your tags caught, inferred from verification failures. Not computed yet; the card shows a dash.",
    "long": "⚠️ An estimate rather than a measurement, and NOT the same thing as [Invalid scans](term:invalid-scans). Most rejections are not counterfeits — a damaged tag, a replayed read, an ordinary misread — so turning a rejection count into an encounter count needs a rate that only you can supply.\n\n⚠️ Note also that there is a similarly named CURRENCY metric, Counterfeit deflection value, which contributes to [Value delivered](term:value-delivered). This one is a count of incidents and does not. They are one letter apart and are deliberately never matched to each other by name.",
    "formula": "Verification failures judged to represent a genuine counterfeit encounter.",
    "seeAlso": [
      "invalid-scans",
      "unattributed-invalid-scans",
      "value-delivered"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "countries-reached",
    "term": "Countries reached",
    "aliases": [
      "geographic reach"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "How many distinct countries your scans came from in the period, counting only scans whose location resolved to a country.",
    "long": "A scan can carry coordinates and still have no country attached to them, so this can read lower than the number of pins on the map beside it. The card names how many located scans are in that state rather than dropping them quietly — \"0 countries\" next to a visible pin reads as a bug unless the page accounts for it. Scans with no location at all are not part of the measure in the first place; [Scan location accuracy](term:scan-location-accuracy) is how many of those there are.",
    "formula": "Count of distinct resolved countries across scans in range.",
    "seeAlso": [
      "scan",
      "scan-location-accuracy",
      "repeat-scan-rate"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "demo",
    "term": "Demo",
    "aliases": [
      "demo company",
      "example company"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "A company Vera provisions to show the product to a prospect. Its records are not a customer's data and it is never billed.",
    "long": "A demo is marked as one on the record, not implied by which environment it lives in — which is why demos are built on production alongside real companies rather than on a test environment. That is a deliberate choice: a company is a graph of users, products, certificates and tags, and nothing migrates between environments, so a demo built anywhere else could never graduate into a real one. Anything a prospect will be shown is therefore real from the start, and only its billing and its badge treat it differently. When the brand takes the demo over and starts paying, its real certificates bill and the demo certificates it keeps never do: billing judges each certificate, not only the company. A demo is provisioned from a prospect's public website for cold outbound, so its logo and product imagery are scraped from that site and are not Vera's to publish. It lives at <brand>-demo.app.vera.inc until the brand takes it over, and it is purgeable in a single action.",
    "seeAlso": [
      "space"
    ]
  },
  {
    "id": "outreach-certificate",
    "term": "Outreach certificate",
    "aliases": [
      "outreach image",
      "demo outreach certificate"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "The one certificate in a demo that an AE sends the prospect: its link, and an image of it for the email. It is the demo's only active certificate.",
    "long": "Chosen when the demo is created (the first selected product unless the AE picks another) and changeable later from the Demos page or the [sales company](term:sales-company) panel. Changing it creates a certificate for the new product if it has none; the old one stays active, so a link already sent keeps working. The downloaded image is always light, shows the product photo whole with room around it, and carries a \"Demo certificate\" label so a screenshot is never mistaken for a live record.",
    "seeAlso": [
      "demo",
      "sales-company"
    ]
  },
  {
    "id": "import-field-report",
    "term": "Import field report",
    "aliases": [
      "not pulled in",
      "fields not imported"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "The list, after a product or brand is read from a website, of the fields Vera could not pull in (photos, price, SKU, description, logo, colors) and why.",
    "long": "Shown per product on its card and per brand in Create demo. Each line names the reason in the site's terms: the page refused automated readers, robots.txt asks bots not to read it, the product data had no SKU, the photo came from the page's share image rather than the product data. A gap disappears once the field is filled by hand. Manufacture date is always listed, because storefronts rarely publish one.",
    "seeAlso": [
      "demo"
    ]
  },
  {
    "id": "divisible-group",
    "term": "Divisible group",
    "aliases": [
      "divisible",
      "divisible certificate group"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "A group whose parts can be sold separately. Each member activates and is billed on its own, and the group can be broken up at any time.",
    "long": "Divisible describes how the object is sold, not how it is stored: the parts are related but each one can leave on its own. Nothing cascades, so a divisible group of three activated one at a time is three activations, at the ordinary rate for each. Because the parts are what sell, each keeps its own page and its own tag opens it. The parent is only a container, so it needs no product of its own, and a divisible group may hold as few as one child. It can be split or have members removed at any point, whatever their status.",
    "seeAlso": [
      "indivisible-group",
      "certificate-group",
      "split-group",
      "group-activation"
    ],
    "docs": [
      "certificates/certificates"
    ]
  },
  {
    "id": "encode",
    "term": "Encode",
    "aliases": [
      "encoding",
      "encoded"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Writing a tag's identity and verification material onto the chip, which is what makes it readable and checkable.",
    "long": "Encoding prepares the chip itself: it writes the [tag](term:tag)'s own identity and, on families that can prove identity per tap, the secret material a later scan verifies against. It happens once, in Vera's facility or on a desk [reader](term:reader), and it is what makes a tag verifiable at all. It does not decide which item the tag belongs to — that pairing is a separate record in Vera — which is why a tag can be encoded and stocked long before anyone knows what it will end up on.",
    "seeAlso": [
      "tag",
      "reader",
      "certificate",
      "package"
    ]
  },
  {
    "id": "field-mapping",
    "term": "Field mapping",
    "aliases": [
      "field map",
      "field mappings"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "The correspondence you define between your store's product fields and Vera's, so synced products land in the right places.",
    "long": "Your store and Vera describe a product differently, so an import has to be told which of your fields fills which of Vera's — title, images, price, and any metafields you keep. Mapping is set per connected store and grouped by what it fills: Product, Owner or Tag. A field your store offers but Vera's importer cannot yet read is shown greyed out rather than hidden, because mapping to it would quietly do nothing. Mapping decides what a field is worth on arrival; [sync mode](term:sync-mode) decides whether later edits keep overwriting it.",
    "seeAlso": [
      "sync-mode"
    ],
    "docs": [
      "integrations/settings-field-mapping",
      "integrations/field-mapping-catalog"
    ]
  },
  {
    "id": "fulfillment-visibility",
    "term": "Fulfillment visibility",
    "aliases": [
      "scans per certificate"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "The average number of scans recorded per certificate — how often a typical item is seen. Not computed yet; the card shows a dash.",
    "long": "A blunt engagement measure rather than a logistics one, despite the name: it divides all scans by all certificates, so certificates never scanned pull it down and a single heavily scanned item pulls it up.\n\n[Repeat scan rate](term:repeat-scan-rate) answers the neighbouring question more precisely, because it counts over items rather than over scans. ⚠️ The card is a placeholder today and renders a dash.",
    "formula": "Total scans ÷ total certificates.",
    "seeAlso": [
      "repeat-scan-rate",
      "scan",
      "certificate"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "gift",
    "term": "Gift",
    "aliases": [
      "gifting",
      "give"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "Handing an item to someone without a sale. The certificate moves to them once they accept, and the handover joins the item's provenance.",
    "long": "A gift is a [transfer](term:transfer) with no price attached, and it behaves like every other transfer: the item stays with you until the person receiving it accepts, so an unclaimed gift can be cancelled and a mistyped address never costs anyone the item. Once accepted it becomes a permanent entry on the [provenance](term:provenance), recorded as a gift rather than a sale, which is what lets a later reader tell the difference between an item that was sold on and one that was passed to family.",
    "seeAlso": [
      "transfer",
      "sell",
      "provenance",
      "owner"
    ]
  },
  {
    "id": "graduated-activation-rate",
    "term": "Graduated activation rate",
    "aliases": [
      "graduated pricing",
      "activation ladder",
      "rate ladder"
    ],
    "kind": "formula",
    "audience": "brand",
    "formula": "each band of the period's activations priced at that band's own rate, then summed",
    "unit": "USD",
    "short": "Activations are priced in bands, from $25 each down to $5 as your total for the period grows. Each band bills at its own rate, like tax brackets.",
    "long": "Graduated is the load-bearing word, and it is the opposite of a bulk discount that reprices everything. Crossing into a cheaper band never makes your earlier [activations](term:activation) cheaper: they stay billed at the rate of the band they fell in. Six hundred activations are therefore the first hundred at $25, the next four hundred at $20 and the last hundred at $16 — a blend, not six hundred at the cheapest rate reached. The bands are the same on every [plan](term:plan); plans differ in how many activations are [included](term:included-activations) before the ladder applies.",
    "seeAlso": [
      "activation",
      "activation-price-tier",
      "activation-total-cost",
      "included-activations"
    ],
    "docs": [
      "company-settings/settings-billing-overview"
    ]
  },
  {
    "id": "granted-activations",
    "term": "Granted activations",
    "aliases": [
      "earned activations",
      "granted / earned activations",
      "activation credits"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Extra activation credits added to your account by Vera, on top of your plan's allowance. They do not expire and share one balance with your included activations.",
    "long": "Vera adds these by hand — service recovery, a trial before a subscription starts, an expansion incentive — and they are independent of any [plan](term:plan), so a company with no [subscription](term:subscription) at all can hold them. They go into the same balance as the allowance your plan accrues each period, and an [activation](term:activation) simply draws that balance down: there is no separate pool spent first or last. The figure shown is the lifetime total granted, not the amount still available.",
    "seeAlso": [
      "activation",
      "included-activations",
      "activation-price-tier",
      "activation-total-cost"
    ],
    "docs": [
      "company-settings/settings-billing-overview"
    ]
  },
  {
    "id": "group-activation",
    "term": "Group activation",
    "aliases": [
      "activating a group",
      "group activation charge"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "What one Activate covers when the certificate is in a group. An indivisible group goes live together for one charge. A divisible one bills per member.",
    "long": "On an indivisible group, activating spreads to every member that is not already live, in both directions: from the parent down to its children, and from any child up to the parent and across to the rest. However many certificates that takes live, it is charged as a single activation, so the price does not depend on which row you clicked. A divisible group does not spread at all, and each member is charged as its own activation. The confirmation dialog states both numbers before you commit, and it also warns you when this is the activation that makes an indivisible group permanent.",
    "seeAlso": [
      "activation",
      "indivisible-group",
      "divisible-group",
      "group-member"
    ],
    "docs": [
      "certificates/certificates",
      "company-settings/settings-billing-overview"
    ]
  },
  {
    "id": "group-member",
    "term": "Group member",
    "aliases": [
      "group members",
      "member of a group"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Any certificate in a group, the parent included. Counts of how many members will activate, or must be unowned, always count the parent as one of them.",
    "long": "Member and child are not the same word. A child is a tagged part; a member is any certificate in the group, so the members are the parent plus all of its children. The difference is worth knowing because it is the members, not the children, that every group-wide rule is counted over. Activating an indivisible group takes every member live, and the confirmation count includes the parent. Whether an indivisible group may still be broken up is decided by checking that every member, parent included, is still unactivated and unowned.",
    "seeAlso": [
      "certificate-group",
      "parent-certificate",
      "child-certificate",
      "group-activation"
    ],
    "docs": [
      "certificates/certificates"
    ]
  },
  {
    "id": "impersonation",
    "term": "Impersonation",
    "kind": "term",
    "audience": "internal",
    "short": "Staff acting as a company's own user rather than as Vera. Every session is recorded against the staff member who started it.",
    "long": "Two mechanisms answer to this word and they are not the same code path. Impersonation proper mints a token scoped to one user and writes an event that is closed when the session ends. [Space mode](term:space) instead grants staff a company's own [permissions](term:permission) while they are inside it, which is what the platform actually uses today. Both narrow what staff can do to what the brand can do, and neither is silent: a suspended company refuses entry, and a manager can only enter companies they are assigned.",
    "seeAlso": [
      "space",
      "role"
    ]
  },
  {
    "id": "in-transit-rate",
    "term": "In-transit rate",
    "aliases": [
      "in transit rate"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "The share of your certificates sitting in an unfinished ownership transfer right now. A snapshot, not a figure for the period. Not computed yet.",
    "long": "A live proportion rather than a period measure: a certificate is either mid-transfer now or it is not, and \"in transit during March\" is not a state it has.\n\nClose to [In progress transfers](term:transfers-in-progress) but not the same figure — that one counts transfers started inside the date range, this one counts certificates regardless of when their transfer began. ⚠️ The card is a placeholder today and renders a dash.",
    "formula": "Certificates currently in a transfer state ÷ all certificates.",
    "seeAlso": [
      "certificate-status-transfer",
      "transfers-in-progress",
      "certificate"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "included-activations",
    "term": "Included activations",
    "aliases": [
      "plan included activations",
      "included activations / mo",
      "activation allowance"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "The certificate activations your plan covers each billing period at no extra charge. Anything past them is billed at the graduated rate. Their value usually exceeds the plan fee.",
    "long": "Each [plan](term:plan)'s allowance is calibrated to be worth at least its base fee, so the subscription pays for itself before you spend anything on top. The allowance accrues once per billing period, and unused ones [roll over](term:rollover) and are kept for as long as you stay subscribed, so a quiet month is not forfeited. On the bill they are credited at the [ladder](term:graduated-activation-rate)'s entry rate rather than at the cheaper marginal rate a high-volume month reaches, which means a credit is worth the same in every month whatever your volume.",
    "seeAlso": [
      "activation",
      "granted-activations",
      "activation-price-tier",
      "activation-total-cost",
      "activation-trigger"
    ],
    "docs": [
      "company-settings/settings-billing-overview"
    ]
  },
  {
    "id": "indivisible-group",
    "term": "Indivisible group",
    "aliases": [
      "indivisible",
      "indivisible certificate group"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "A group sold only as one unit. Activating any member activates them all for a single activation charge, and once live the group can never be broken up.",
    "long": "Indivisible is a claim about the physical object: the parts are not separately sellable, so the group is treated as one certificate everywhere it counts. Activating any member activates the whole group, and the whole group is charged as one activation, whichever member you started from. A scan of any part opens the parent's certificate, because the set is what the customer bought. It needs at least two members and the parent must have a product, since the parent is the unit that billing and inventory follow. It can only be broken up while every member is still unactivated and unowned. After that it is permanent.",
    "seeAlso": [
      "divisible-group",
      "certificate-group",
      "group-activation",
      "parent-certificate"
    ],
    "docs": [
      "certificates/certificates"
    ]
  },
  {
    "id": "installation",
    "term": "Installation",
    "aliases": [
      "installations",
      "install",
      "embed",
      "snippet"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "The code you embed in your own site so certificates open on your pages, under your domain, instead of on a Vera address.",
    "long": "An installation changes where a [scan](term:scan) lands, not what it resolves to: the [certificate](term:certificate), its provenance and its scan history are the same either way. What it buys is continuity — a customer who taps an item stays on your site rather than being handed to a domain they do not recognise, at the exact moment they are checking whether something is genuine. Until an installation is in place, scans resolve to your Vera [subdomain](term:subdomain), which works identically and simply is not yours.",
    "seeAlso": [
      "subdomain",
      "scan",
      "certificate"
    ],
    "docs": [
      "integrations/settings-installation"
    ]
  },
  {
    "id": "integration",
    "term": "Integration",
    "aliases": [
      "integrations",
      "connection"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "A connection between Vera and a system you already run, such as your online store, so products and orders arrive without being entered twice.",
    "long": "An integration is a standing link rather than a one-off import: once connected, changes on the other side keep arriving. Two settings decide what it actually does. The [field mapping](term:field-mapping) says which of their fields becomes which of yours, and the [sync mode](term:sync-mode) says whether a later change on their side is allowed to overwrite what is in Vera. Connecting a system is therefore the smallest part of the job — the mapping is what determines whether the data lands where you expect.",
    "seeAlso": [
      "field-mapping",
      "sync-mode",
      "product"
    ],
    "docs": [
      "integrations/settings-field-mapping"
    ]
  },
  {
    "id": "intro",
    "term": "Sample data",
    "aliases": [
      "intro data"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Records Vera added so a new company has something to explore. They are not your data, they are never billed, and you can delete them.",
    "long": "Sample data sits beside real records rather than in a separate space, because a company starts using the product before it has any records of its own and a walled-off sandbox would teach the wrong thing. Everything seeded this way is badged, so nothing is ambiguous about which rows are yours.",
    "seeAlso": [
      "product",
      "certificate"
    ]
  },
  {
    "id": "invalid-scan-rate",
    "term": "Invalid scan rate",
    "aliases": [
      "invalid rate"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "The share of your scans that were rejected as invalid. Both halves are counted over the same population, so it describes that population.",
    "long": "⚠️ The denominator is the scan record itself — the very rows the numerator is drawn from, under the same filters — and NOT the wider certificate action log, which also records link openings that were never a tag read. Both can reasonably be called \"total scans\", the two denominators differ substantially, and picking the wrong one moves this number a long way while looking entirely correct.\n\nBecause both halves come from one population under one set of filters, this is a property of that population rather than a ratio between two differently scoped counts. The card prints its base for the same reason [Repeat scan rate](term:repeat-scan-rate) does: 100% over a single scan is correct and meaningless.\n\n⚠️ Filtering the page to invalid scans makes every figure here exactly 100% — correctly, since the filter makes the two halves the same rows, and uselessly.",
    "formula": "Invalid scans ÷ all scans in range, both counted from the scan record under the same filters.",
    "seeAlso": [
      "invalid-scans",
      "scan-status-invalid",
      "unattributed-invalid-scans",
      "repeat-scan-rate"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "invalid-scans",
    "term": "Invalid scans",
    "aliases": [
      "rejected scans"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "Scans Vera rejected in the period — a failed cryptographic proof, a replayed counter, or a tag it does not recognise.",
    "long": "Counted only over scans that reached one of your [certificates](term:certificate). A scan of a tag Vera never issued matches no certificate at all, and every filter on this figure reaches through that link, so those cannot appear here — they are reported alongside as [unattributed invalid scans](term:unattributed-invalid-scans), and they are the more interesting of the two.\n\nWhat a rejection means depends on the tag and on how the link was reached; [invalid](term:scan-status-invalid) is the outcome itself, and the neighbouring scan statuses separate a tampered read from a merely stale link.",
    "formula": "Count of scans in range flagged invalid, among scans matched to one of your certificates.",
    "seeAlso": [
      "scan-status-invalid",
      "invalid-scan-rate",
      "unattributed-invalid-scans",
      "scan"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "inventory-available",
    "term": "Available",
    "aliases": [
      "available tags",
      "on hand"
    ],
    "kind": "column",
    "audience": "brand",
    "short": "Tags in your hands right now, received and ready to apply. Of the three counts, this is the only one you can actually use today.",
    "long": "A tag counts here once you have scanned its [package](term:package) in, which is why Available can lag a delivery that has physically arrived — the count follows the receipt scan, not the courier. Everything downstream is built on this number and not on the other two: [Capacity](term:inventory-capacity) is Available minus demand, and the stock warning fires on Capacity. Note that this column once meant spare capacity rather than possession, so older screenshots and notes may use the word differently.",
    "seeAlso": [
      "inventory-ordered",
      "inventory-incoming",
      "inventory-capacity"
    ]
  },
  {
    "id": "inventory-capacity",
    "term": "Capacity",
    "aliases": [
      "spare capacity",
      "tag capacity"
    ],
    "kind": "formula",
    "audience": "brand",
    "formula": "Available − Uncertified products",
    "unit": "product units",
    "short": "How many more units you could certify with the tags already in hand. It is signed: a negative number is a shortfall of that many tags, not a maximum of zero.",
    "long": "The sign is the whole value of the figure. A row reading −26 says you are twenty-six tags short of covering the stock you hold, which is the number you would order on — flooring it at zero would collapse every short line into one indistinguishable bucket. [Ordered](term:inventory-ordered) and [Incoming](term:inventory-incoming) are excluded on purpose, because neither can be applied to a product today. One consequence to expect: the Capacity card above the table is not the column added up. The card floors each tag type at zero first, because spare tags of one type cannot cover a shortfall in another.",
    "seeAlso": [
      "inventory-available",
      "inventory-uncertified-products",
      "inventory-threshold"
    ]
  },
  {
    "id": "inventory-incoming",
    "term": "Incoming",
    "aliases": [
      "in transit",
      "incoming tags"
    ],
    "kind": "column",
    "audience": "brand",
    "short": "Tags Vera has shipped that have not reached you yet. They become Available when you scan the package in on arrival.",
    "long": "Incoming is deliberately excluded from [Capacity](term:inventory-capacity) and from the stock warning, and the reason is the same in both cases: a tag in a van cannot be applied to a product today, so counting it as readiness would overstate what you can actually do. It is shown because it changes what you should do about a shortfall — a gap that is already in transit needs patience rather than another order. Scanning the [package](term:package) in on receipt is what moves these into [Available](term:inventory-available); until that happens they stay here regardless of where the parcel physically is.",
    "seeAlso": [
      "inventory-ordered",
      "inventory-available",
      "package"
    ]
  },
  {
    "id": "inventory-ordered",
    "term": "Ordered",
    "aliases": [
      "at vera",
      "ordered tags"
    ],
    "kind": "column",
    "audience": "brand",
    "short": "Tags you have paid for that Vera is still preparing. Nothing has shipped yet, so none of these can be applied to a product today.",
    "long": "Ordered, [Incoming](term:inventory-incoming) and [Available](term:inventory-available) are three stages of the same journey, and a tag is in exactly one of them, so the three never double-count. This is a live count rather than a running total of what you have ever bought: it falls as tags ship, and reaching zero means everything you ordered has left Vera, not that you never ordered anything. Read together with Available it answers the question that matters when stock is low — zero available against a hundred ordered means stock is coming; zero against zero means you need to buy tags.",
    "seeAlso": [
      "inventory-incoming",
      "inventory-available",
      "package"
    ]
  },
  {
    "id": "inventory-threshold",
    "term": "Threshold",
    "aliases": [
      "reorder threshold",
      "low stock threshold",
      "alert threshold"
    ],
    "kind": "column",
    "audience": "brand",
    "short": "The level your spare capacity has to fall to before Vera warns you. Zero means no level has been set, not “warn me at zero”.",
    "long": "The threshold is compared against [Capacity](term:inventory-capacity) rather than against [Available](term:inventory-available), so it asks whether you can still cover the stock you hold — not merely how many tags are in the drawer. It can be set as a flat number of tags or as a percentage, and the percentage resolves against your [uncertified demand](term:inventory-uncertified-products), so a moving target as your stock grows. Where no threshold has been set the row is only flagged once Capacity actually reaches zero or goes negative, which is a floor rather than a warning.",
    "seeAlso": [
      "inventory-capacity",
      "inventory-available"
    ]
  },
  {
    "id": "inventory-uncertified-products",
    "term": "Uncertified products",
    "aliases": [
      "uncertified",
      "uncertified demand"
    ],
    "kind": "column",
    "audience": "brand",
    "short": "In-stock product units assigned to this tag that still have no certificate. It counts units rather than products, and it is this tag's demand.",
    "long": "The word to hold onto is units. A product with forty in stock and ten certified contributes thirty here, not one — counting it as a single product would understate a large line as badly as a small one and make the figure useless for ordering. Every other column on this table counts tags, so this is the one that counts the other side of the equation: what the tags are for. It is the number subtracted from [Available](term:inventory-available) to give [Capacity](term:inventory-capacity).",
    "seeAlso": [
      "inventory-capacity",
      "inventory-units-in-stock",
      "certificate"
    ]
  },
  {
    "id": "inventory-units-in-stock",
    "term": "Units in stock",
    "aliases": [
      "product stock"
    ],
    "kind": "column",
    "audience": "brand",
    "short": "Total stock across the products this tag is assigned to. Uncertified products is the part of it still waiting for a certificate.",
    "long": "This is the only column on the table that counts products rather than tags, which is why it is hidden by default — on a table about tag supply it is context, not a figure to act on. Its use is as a denominator: [Uncertified products](term:inventory-uncertified-products) read against it tells you how far through certifying a line you are, where the same shortfall means something different against a run of fifty and a run of five thousand.",
    "seeAlso": [
      "inventory-uncertified-products",
      "product"
    ]
  },
  {
    "id": "invoice",
    "term": "Invoice",
    "aliases": [
      "bill"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "A bill Vera has issued you, for a subscription period or for an order of tags. Each one shows what it covers and whether it is paid.",
    "long": "Invoices carry a type as well as a payment state, and the two answer different questions: the type says whether this is your recurring [plan](term:plan) or a one-off purchase of [tags](term:tag), the payment state says where it has got to. An invoice for a [subscription](term:subscription) period includes any [activations](term:activation) that period billed for, which is why the amount is not simply your plan price.",
    "seeAlso": [
      "subscription",
      "order",
      "next-invoice-total"
    ]
  },
  {
    "id": "items-per-owner",
    "term": "Items per owner",
    "aliases": [
      "certificates per owner"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "The average number of certificates held per owner. It counts items, not designs — six identical watches are six items.",
    "long": "An [owner](term:owner) is a person, and this counts what they hold rather than how many different [products](term:product) they hold, so a collector with six of the same watch reads as six.\n\nCertificates that have not yet reached a first owner still sit with the brand and are not part of the average. It is a mean rather than a median, so one large holder moves it noticeably on a small owner base.",
    "formula": "Certificates with an owner ÷ distinct owners.",
    "seeAlso": [
      "owner",
      "certificate",
      "product"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "lead",
    "term": "Lead",
    "aliases": [
      "leads"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Someone who scanned one of your certificates and gave an email that is not one of your owners' — a stranger showing interest, not an existing customer.",
    "long": "Vera identifies a lead by the email collected at the scan, so a scan with no email is not counted and one person using two addresses counts twice. The test is membership: the address is compared against the owners already linked to your company, and anyone not on that list is a lead. The same test backs the Leads list, the Top Influencers leaderboard on Analytics and Ask Vera, so the three never disagree about who counts. It records exposure rather than intent — a lead scanned something of yours, which is not the same as having been referred to you.",
    "seeAlso": [
      "owner",
      "scan",
      "certificate"
    ]
  },
  {
    "id": "leads-captured",
    "term": "Leads captured",
    "aliases": [
      "captured leads"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "How many distinct people gave you an email through a scan in the period, counting only those who are not already one of your owners.",
    "long": "A [lead](term:lead) is identified by the email left at the scan, so a scan with no email is not counted and one person using two addresses counts twice. The deduplication matches the Leads list exactly, which is what stops this figure and that page disagreeing about who counts.\n\nIt records exposure rather than intent: someone scanned something of yours, which is not the same as having asked to hear from you.",
    "formula": "Distinct lowercased emails collected by scans in range, excluding your existing owners.",
    "seeAlso": [
      "lead",
      "owner",
      "scan"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "next-invoice-total",
    "term": "Next invoice total",
    "aliases": [
      "upcoming invoice",
      "next invoice"
    ],
    "kind": "formula",
    "audience": "brand",
    "formula": "your plan fee plus the activations billed so far this period, as currently drafted",
    "unit": "USD",
    "short": "What your next invoice is drafted at right now: your plan fee plus this period's billable activations. It rises as the period runs.",
    "long": "This is a draft rather than a forecast — it reflects what has already happened this period, not a projection of the whole of it, so expect it to climb until the period closes. [Activations](term:activation) covered by your [included allowance](term:included-activations) or your [rollover](term:rollover) balance are absent from it entirely rather than shown and credited, because a covered activation is never billed in the first place. Any tax or discount is applied at the point the invoice is issued.",
    "seeAlso": [
      "activation-total-cost",
      "included-activations",
      "rollover"
    ],
    "docs": [
      "company-settings/settings-billing-overview"
    ]
  },
  {
    "id": "order",
    "term": "Order",
    "aliases": [
      "tag order",
      "tag shop order"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "A purchase you place with Vera for physical tags. It is fulfilled as one or more boxes, and each box arrives as a package.",
    "long": "An order is for TAGS, not for anything your own customers buy. Vera does not hold your storefront's orders, so if you connect a store, what arrives in Vera is its [products](term:product); the sales stay where they are. An order's own state comes from the fulfilment side and is separate from whether it has been paid for, which is why the two are shown as different columns rather than one.",
    "seeAlso": [
      "package",
      "invoice"
    ]
  },
  {
    "id": "order-status-fulfilled",
    "term": "Fulfilled",
    "kind": "status",
    "audience": "brand",
    "short": "Every box in this order has shipped. It says nothing about whether they have arrived, which the tracking does.",
    "seeAlso": [
      "order"
    ]
  },
  {
    "id": "order-status-partially-fulfilled",
    "term": "Partially fulfilled",
    "kind": "status",
    "audience": "brand",
    "short": "Some of this order has shipped and some has not. A large order can leave in more than one box, and each box ships when it is ready.",
    "long": "Partially fulfilled is a normal state rather than a problem: splitting a large [order](term:order) lets the first boxes reach you while the rest are still being [encoded](term:encode). Tracking is per box, so an order in this state can have one number that is moving and another that does not exist yet.",
    "seeAlso": [
      "order",
      "package"
    ]
  },
  {
    "id": "order-status-unfulfilled",
    "term": "Unfulfilled",
    "kind": "status",
    "audience": "brand",
    "short": "Nothing in this order has shipped yet. It is the state every order starts in, including one already paid for.",
    "seeAlso": [
      "order"
    ]
  },
  {
    "id": "owner",
    "term": "Owner",
    "aliases": [
      "owners",
      "certificate owner"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "The person a certificate currently belongs to, which changes only through a completed transfer.",
    "long": "An owner is a person, not necessarily an account: ownership is recorded when an item changes hands, so a [certificate](term:certificate) can have an owner who has never signed in — for instance one created from a store's customer list. Because ownership only moves by [transfer](term:transfer), an owner is never edited in place, and every previous owner stays on the item's [provenance](term:provenance). A certificate that has not yet reached its first owner still sits with the brand.",
    "seeAlso": [
      "transfer",
      "certificate",
      "provenance"
    ]
  },
  {
    "id": "package",
    "term": "Package",
    "aliases": [
      "packages",
      "box of tags"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "A box of tags Vera has filled and linked to your order. Assigning it to one of your products creates a certificate for every tag in it.",
    "long": "A package does not exist when you place an order. It comes into being once Vera has [encoded](term:encode) the tags, boxed them and linked that box to your order, which is when it appears on the order ready to be assigned. Assigning is whole-box and one product, and the point of it is what happens at the other end: every tag already stands for a [certificate](term:certificate), so whoever applies them needs no Vera account, no app and no training. A package can be reassigned to a different product while none of its certificates has been activated, and is frozen once any of them has. The package is also the unit your stock moves in: scanning one in on arrival is what turns its tags from [Incoming](term:inventory-incoming) into [Available](term:inventory-available).",
    "seeAlso": [
      "tag",
      "certificate",
      "encode",
      "activation-trigger",
      "inventory-available"
    ],
    "docs": [
      "tags/assign-product-to-package"
    ]
  },
  {
    "id": "parent-certificate",
    "term": "Parent certificate",
    "aliases": [
      "parent certificates",
      "group parent",
      "tagless parent"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "The certificate that stands for the whole group. It carries no tag of its own, so it is reached through a member's tag rather than scanned directly.",
    "long": "A parent is created for you whenever a group is created, and it is tagless by design: the group's tags are on its parts, so there is nothing to fix a separate tag to. On an indivisible group the parent is the sellable unit, which is why it must be given a product of its own. On a divisible group it is only a container, because billing and inventory already follow each part's own product, so its product is optional. A parent is otherwise an ordinary certificate: it activates, it carries an activation date, and it has its own page. When the last member is removed the parent is archived rather than deleted, so the record that the group existed survives.",
    "seeAlso": [
      "certificate-group",
      "child-certificate",
      "group-member",
      "split-group"
    ],
    "docs": [
      "certificates/certificates"
    ]
  },
  {
    "id": "permission",
    "term": "Permission",
    "aliases": [
      "permissions"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "A single thing a user may do, such as creating certificates. Permissions are granted through roles, never to a person directly.",
    "long": "Each permission names one capability on one area, which is what makes access reviewable: you can read a [role](term:role) as a list of the things it allows rather than inferring it from a job title. Where a user lacks one, the control it gates usually stays visible and disabled, with the missing permission named — so the path to getting access is discoverable rather than a dead end. The check also runs on the server, so hiding or showing a button is a convenience and never the boundary itself.",
    "seeAlso": [
      "role"
    ]
  },
  {
    "id": "plan",
    "term": "Plan",
    "aliases": [
      "tier",
      "pricing tier"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "The named tier your subscription is on. It sets your monthly price, your included activations, and which features are switched on.",
    "long": "Plans differ along four axes: how many [activations are included](term:included-activations) each month, how much history and analysis you can see, how far you can put your own brand in front of Vera's, and how finely you can divide access across a team. A plan that is no longer sold is marked retired; a company already on one keeps it until it moves.",
    "seeAlso": [
      "subscription",
      "included-activations",
      "white-label",
      "seat"
    ]
  },
  {
    "id": "plan-tier",
    "term": "Plan tier",
    "aliases": [
      "subscription tier",
      "plan level"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "Which subscription plan a company is on, as the plan catalog names it. Distinct from an activation price tier, which is a volume threshold on usage pricing.",
    "long": "The plan tier is a property of the subscription: it decides the recurring amount, the included activations and the included seats. An activation price tier is something else entirely — a threshold a company crosses partway through a period, after which further activations cost less. A company sits on exactly one plan tier for a whole period and may cross several activation price tiers within it.",
    "seeAlso": [
      "activation-price-tier"
    ]
  },
  {
    "id": "product",
    "term": "Product",
    "aliases": [
      "products"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "The model an item is made to — the design a brand makes many of. Every individual unit of it has its own certificate.",
    "long": "Product and [certificate](term:certificate) are the pair people most often read as the same thing, and the difference is one of counting. A product is the design: one entry, however many are made. A certificate is one physical unit of it, so a hundred handbags made to the same design are one product and a hundred certificates. That is why the product details on a certificate — the description, the images, the manufacture date — look the same on every unit, while the owner, the [provenance](term:provenance) and the scan history belong to the single item in your hands.",
    "seeAlso": [
      "certificate",
      "brand"
    ]
  },
  {
    "id": "product-lifespan",
    "term": "Product lifespan",
    "aliases": [
      "observed lifespan"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "The typical time from an item first existing to its most recent scan. Measured from the manufacture date where there is one, and from certificate creation where there is not.",
    "long": "⚠️ Not a durability figure, and the difference matters. Vera has no record of an item being discarded, so anything still in circulation keeps adding to this number every time it is scanned — for a healthy catalogue the figure grows over time, and that is the measure working rather than drifting.\n\n**It is measured from two different starting points, and the card says how many fall on each side.** A [product](term:product) with a manufacture date is measured from that date. One without is measured from when its [certificate](term:certificate) was created, which is when the item entered Vera rather than when it was made. Those answer different questions — how old the item is, versus how long it has been on the system — so read the line under the figure before comparing the number to anything.\n\nThe second starting point runs low. Items added to Vera in bulk long after they were made all carry the date they were imported, so the oldest pieces get the newest starting point and the figure understates them. Filling in manufacture dates on your products moves them onto the first basis and makes the number truer.",
    "formula": "Median days from the manufacture date — or, where there is none, the earliest certificate creation date — to the latest scan, over items scanned in the period.",
    "seeAlso": [
      "product",
      "scan",
      "certificate"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "provenance",
    "term": "Provenance",
    "aliases": [
      "chain of ownership",
      "history"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "The full ownership history of an item, from the brand through every completed transfer.",
    "long": "Provenance reads newest-first: one entry per completed [transfer](term:transfer), each with its date and how that owner acquired the item, ending at the [brand](term:brand) where the certificate was created. Every entry also records how the handover was proven, because this is the part of a certificate a resale buyer actually reads and \"it happened\" is weaker evidence than \"the recipient accepted it and scanned the tag\". Only completed hops are entries — a cancelled or still-pending transfer never appears, and nothing is removed once it does.",
    "seeAlso": [
      "owner",
      "transfer",
      "certificate"
    ]
  },
  {
    "id": "provisioning-status",
    "term": "Provisioning status",
    "aliases": [
      "provisioning"
    ],
    "kind": "column",
    "audience": "internal",
    "short": "How far a company's automated setup got: pending, succeeded, or failed. A company still on pending has not finished being built.",
    "long": "Provisioning creates the things a company cannot work without — its WorkOS organisation, its first admin, its default settings. A company stuck on pending will typically show zero users and zero certificates, which reads as an empty company rather than an unfinished one; the status is what tells the two apart. Failed means setup stopped partway and left the company incomplete, not that the company is invalid.",
    "seeAlso": [
      "company-kind"
    ]
  },
  {
    "id": "reader",
    "term": "Reader",
    "aliases": [
      "vera reader",
      "hardware reader",
      "desk reader"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Vera's desk hardware for encoding and checking tags in bulk, faster than tapping each one with a phone.",
    "long": "A reader plugs into the computer running Vera and is reached through the Vera Reader Helper, so the platform can [encode](term:encode) or check a [tag](term:tag) as quickly as you can place one on it. A phone does the same work one tap at a time, which is right for a spot check and wrong for a box of five hundred. When the platform says the reader is not connected, either nothing is plugged in or the helper is not running.",
    "seeAlso": [
      "encode",
      "tag",
      "scan"
    ]
  },
  {
    "id": "recovery-code",
    "term": "Recovery code",
    "aliases": [
      "recovery codes",
      "backup code",
      "backup codes"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "A single-use code that signs you in when you cannot reach your authenticator app or phone. Shown once when you turn on two-factor authentication.",
    "long": "Recovery codes are your way back in if your phone is lost, broken or replaced. Save them somewhere you can reach without that phone — a password manager, or printed and put away.\n\nThey are shown exactly once and cannot be retrieved later: Vera stores only a one-way hash, so nobody, including support, can read them back to you. Each code works once. If you think they have been seen by someone else, or you simply run low, you can replace the whole set with a fresh one — which immediately invalidates every code from the old set.",
    "seeAlso": [
      "two-factor-authentication",
      "authenticator-app"
    ],
    "docs": [
      "settings/settings-security"
    ]
  },
  {
    "id": "remove-from-group",
    "term": "Remove from group",
    "aliases": [
      "remove a certificate from a group",
      "unlink from group"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Taking one member out of a group. The group carries on with whatever members remain, even if only one is left.",
    "long": "Removing is the single-member counterpart to splitting, and it only ever detaches what you asked it to. A group with one child left is a normal state, not a broken one, so swapping a part out and a replacement in never destroys the group in between. Removing the last child does empty the group, which dissolves it and archives the parent, and the confirmation says so before you commit. On an indivisible group the action is available only while every member is still unactivated and unowned. Removing a child that is already active is a billable event, charged as a new activation, unless it is the last one leaving, which carries the group's original charge instead.",
    "seeAlso": [
      "split-group",
      "certificate-group",
      "group-member",
      "parent-certificate"
    ],
    "docs": [
      "certificates/certificates"
    ]
  },
  {
    "id": "repeat-scan-rate",
    "term": "Repeat scan rate",
    "aliases": [
      "repeat rate"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "Of the items scanned at all in the period, the share scanned more than once. Counted over items, not over scans.",
    "long": "The denominator is ITEMS, which is the usual misreading: a hundred scans of one item is one item scanned, and reads as 100%. The card always prints its base for that reason — a rate over two items is arithmetically correct and tells you nothing.\n\n⚠️ The same rate appears on the Scans by country map and the two will NOT reconcile, by design. There it is computed inside each country — of the items scanned in France, how many were scanned in France more than once — so an item scanned once in France and once in Japan is a repeat here and a single scan in both countries. Neither figure is wrong, and no aggregation of the country ones produces this one.",
    "formula": "Certificates scanned two or more times ÷ certificates scanned at all, in range.",
    "seeAlso": [
      "scan",
      "certificate",
      "countries-reached"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "reported-lost",
    "term": "Reported lost",
    "aliases": [
      "lost reports"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "How many certificates were reported lost during the period. It counts the report as a dated event, so it moves with the date filter.",
    "long": "Counts the report rather than the number of items currently in a lost state — a status has no date, so only the event can be scoped to a period.\n\nLost and [stolen](term:certificate-status-stolen) are recorded separately because they mean different things to someone reading an item's history later: one is a misplacement, the other an allegation. [Lost](term:certificate-status-lost) is what the state does to a certificate.",
    "formula": "Count of lost reports logged in range.",
    "seeAlso": [
      "certificate-status-lost",
      "reported-stolen",
      "certificate"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "reported-stolen",
    "term": "Reported stolen",
    "aliases": [
      "stolen reports"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "How many certificates were reported stolen during the period. It counts the report as a dated event, so it moves with the date filter.",
    "long": "Counts the report, not the number of items sitting in a stolen state right now. Those are different figures, and only this one can be asked about a period at all — a status carries no date, so counting statuses would produce a number that ignored the date filter while sitting beside figures that honour it.\n\nSo a certificate reported stolen last year and never recovered appears in last year's figure and not in this month's. [Stolen](term:certificate-status-stolen) is what the state itself does to a certificate.",
    "formula": "Count of stolen reports logged in range.",
    "seeAlso": [
      "certificate-status-stolen",
      "reported-lost",
      "certificate"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "reporting-currency",
    "term": "Reporting currency",
    "aliases": [],
    "kind": "column",
    "audience": "internal",
    "short": "The three-letter currency a company's figures are reported in. Defaults to USD.",
    "seeAlso": []
  },
  {
    "id": "resales",
    "term": "Resales",
    "aliases": [
      "completed resales"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "How many ownership transfers completed as a sale in the period. Gifts and the brand's first handoff to an owner are not counted.",
    "long": "A resale is a [transfer](term:transfer) the sender marked as a [sale](term:sell), so this reflects how items were declared to have changed hands rather than any payment Vera observed. [Gifts](term:gift) are excluded, as is the first handoff out of the brand — that one is the original sale, not a resale of it.\n\nNot every resale carries a declared price; [Average resale price](term:average-resale-price) covers what happens to the ones that do not.",
    "formula": "Count of completed sale transfers in range.",
    "seeAlso": [
      "sell",
      "gift",
      "transfer",
      "average-resale-price",
      "resales-per-resold-item"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "resales-per-resold-item",
    "term": "Resales per resold item",
    "aliases": [
      "resales per item"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "How many times an item that has been resold changes hands on average. Items never resold are excluded, so this measures resale behaviour.",
    "long": "The denominator is deliberately items that HAVE resold rather than the whole catalogue. That is what separates \"how lively is the secondary market for these\" from \"how much of my catalogue resells at all\" — dividing by every certificate would answer the second question under the first one's name.\n\nA figure near 1 means resold items typically move once. Well above 1 means the same items keep circulating.",
    "formula": "Completed resales ÷ distinct items with at least one resale.",
    "seeAlso": [
      "resales",
      "average-resale-price",
      "sell"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "return-fraud-prevented",
    "term": "Return fraud prevented",
    "aliases": [
      "return fraud value"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "The part of your own stated annual return-fraud loss that Vera eliminates, prorated to the period and scaled by how much of your catalogue is certified.",
    "long": "Rests on one figure you supply — your annual return-fraud loss — and the card asks for it rather than assuming one. Until you enter it the figure is withheld instead of guessed.\n\nWhat it is NOT is an estimate of detection. A certified item either verifies as the one that was sold or it does not, so on covered stock the check is deterministic rather than probabilistic. That is exactly why it is scaled by [certificate coverage](term:certificate-coverage) instead of claimed in full: nothing was there to verify on uncertified goods, and claiming to have stopped fraud on items Vera never touched is the kind of number a brand notices.\n\nIt counts toward [Value delivered](term:value-delivered), and it is the one input-driven metric where supplying the number actually changes the figure.",
    "formula": "Your stated annual return-fraud loss, prorated to the period, × certificate coverage.",
    "seeAlso": [
      "certificate-coverage",
      "value-delivered",
      "return-on-vera",
      "verify"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "return-on-vera",
    "term": "Return on Vera",
    "aliases": [
      "roi multiple",
      "return multiple"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "Value delivered divided by what you have spent with Vera, over your whole history. The headline figure on Analytics.",
    "long": "⚠️ Measured over ALL TIME, not over the page's date range, and that is forced by the denominator rather than chosen: spend is settled invoices, which land in lumps on invoice dates, so a thirty-day window falling between two invoices divides by zero. The page's date picker sits BELOW this card for that reason — everything beneath it moves with the range, this does not.\n\nIt is calculated once, as one sum of [value delivered](term:value-delivered) over one [spend](term:vera-spend) figure, rather than per card. Per-card multiples are not additive, and a reader shown six of them will add them.\n\nThe page's other filters — owner, product, certificate, location — do still apply, and they narrow the numerator while the denominator stays company-wide. So filtering to a single product reports that product's value against your entire bill.\n\n⚠️ One more caveat if your [spend](term:vera-spend) settled in a currency other than your reporting one: the two figures are divided as numbers, so the multiple mixes currencies rather than converting between them. Read it as approximate whenever the hero shows two different symbols.",
    "formula": "Value delivered ÷ Spend with Vera, both over the company's whole history.",
    "seeAlso": [
      "value-delivered",
      "vera-spend",
      "invoice",
      "subscription"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "role",
    "term": "Role",
    "aliases": [
      "roles"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "A named set of permissions you assign to a user. Editing the role changes what everyone holding it can do.",
    "long": "Roles exist so access is granted by job rather than by person: you decide once what Finance may do, and every finance hire inherits it. The consequence worth remembering is that a role is shared — widening one to unblock a single person widens it for everyone who holds it, which is usually the moment to create a role instead of editing one. A user holds exactly one role at a time, so the role is the whole answer to what they can do.",
    "seeAlso": [
      "permission"
    ]
  },
  {
    "id": "rollover",
    "term": "Rollover",
    "aliases": [
      "activation rollover",
      "carry over"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Included activations you do not use are added to your balance and carried forward. There is no cap, and they do not expire while you stay subscribed.",
    "long": "Rollover is what stops an allowance from punishing an uneven year: a quiet quarter is not lost, it is banked against the busy one. The balance is a single pool — unused monthly [allowance](term:included-activations) and any activations Vera [grants](term:granted-activations) you accumulate together, with nothing expiring first. It is spent before you are charged, so an activation covered by the balance is not billed at all rather than billed and credited back, which is why it never appears as a line on an [invoice](term:invoice).",
    "seeAlso": [
      "included-activations",
      "granted-activations",
      "activation"
    ],
    "docs": [
      "company-settings/settings-billing-overview"
    ]
  },
  {
    "id": "scan",
    "term": "Scan",
    "aliases": [
      "scans",
      "tap"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "One read of a tag by a phone, recorded with its time and place so owners can see where an item has been checked.",
    "long": "Every read is written down, including the ones that fail, so a [certificate](term:certificate)'s scan list is where the exceptions show up next to the ordinary taps. That is why a scan carries an outcome of its own rather than being a simple count: what the [tag](term:tag) proved varies with the tag and with the tap. Opening a certificate's web link without tapping anything is also recorded here, but marked as a link event, so the two kinds of evidence are never added together.",
    "seeAlso": [
      "tag",
      "certificate",
      "scan-status-verified"
    ],
    "docs": [
      "certificates/certificate-scans",
      "integrations/settings-installation"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "scan-location-accuracy",
    "term": "Scan location accuracy",
    "aliases": [
      "location resolution"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "The share of scans where a location could actually be resolved. A data-quality measure about your scan records. Not computed yet.",
    "long": "A scan carries a location only when the device and the network gave one up, so this says how complete the geography on this page is — not anything about how your items are doing.\n\nIt is the figure to check first when [Countries reached](term:countries-reached) or the map looks thinner than your scan count suggests it should. ⚠️ The card is a placeholder today and renders a dash.",
    "formula": "Scans with a resolved location ÷ all scans in range.",
    "seeAlso": [
      "countries-reached",
      "scan"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "scan-status-connected",
    "term": "Connected",
    "aliases": [
      "connected scan",
      "connect tag scan"
    ],
    "kind": "status",
    "audience": "brand",
    "short": "A tag was tapped and opened the certificate. The tag is sealed, but carries no per-tap proof, so a scan cannot tell it apart from a copy of its address.",
    "long": "Connect tags are sealed against rewriting but cannot answer a security challenge, so a scan confirms the certificate was opened by a [tag](term:tag) rather than confirming which tag. This is the expected outcome for these tags, not a failure: it is reported separately from [Verified](term:scan-status-verified) so a tap that proved nothing about the chip is never recorded as one that did, and separately from [Invalid](term:scan-status-invalid) so a legitimate tap on a tag you chose is never logged as suspicious.",
    "seeAlso": [
      "scan-status-verified",
      "scan-status-invalid",
      "tag"
    ],
    "docs": [
      "certificates/certificate-scans"
    ]
  },
  {
    "id": "scan-status-invalid",
    "term": "Invalid",
    "aliases": [
      "invalid scan"
    ],
    "kind": "status",
    "audience": "brand",
    "short": "A tag was tapped but did not verify. It may be a copy of a real tag, or a genuine tag that is damaged or misprogrammed.",
    "long": "Invalid is evidence, not a verdict. Something was physically tapped and its proof did not check out, which is consistent with a copied [tag](term:tag) but equally with a genuine one that is damaged, was programmed wrong, or was read badly. Read a single invalid scan as something to look into rather than a confirmed counterfeit, and read it against the certificate's other [scans](term:scan). It is ranked below states that are confirmed rather than inferred, and it is a different kind of thing from the link statuses, where nothing was tapped at all.",
    "seeAlso": [
      "scan",
      "tag",
      "scan-status-verified",
      "scan-status-invalid-link"
    ],
    "docs": [
      "certificates/certificate-scans"
    ]
  },
  {
    "id": "scan-status-invalid-link",
    "term": "Invalid link",
    "aliases": [
      "invalid link",
      "malformed link"
    ],
    "kind": "status",
    "audience": "brand",
    "short": "A malformed certificate link was opened. No tag was tapped, so nothing about a physical item was checked.",
    "long": "The address itself did not parse — truncated by a messaging app, mistyped, or mangled by a link preview — so there was nothing to check against. Like the other link statuses it involves no tap, so it is silent about the item; unlike [Suspicious link](term:scan-status-suspicious-link), there is no sign of anyone trying to forge anything. Despite the name it is unrelated to an [Invalid](term:scan-status-invalid) scan, which is a real tag that failed verification. It is logged for completeness rather than as something to act on.",
    "seeAlso": [
      "scan",
      "certificate",
      "scan-status-stale-link",
      "scan-status-invalid"
    ],
    "docs": [
      "certificates/certificate-scans"
    ]
  },
  {
    "id": "scan-status-opened",
    "term": "Opened",
    "aliases": [
      "tampered",
      "tamper",
      "opened seal"
    ],
    "kind": "status",
    "audience": "brand",
    "short": "The tag reports that its seal was opened. On a closure or seal tag that is the expected outcome once the owner opens the item.",
    "long": "A fact about the seal, not a problem with the [scan](term:scan). A scan can verify perfectly and still report an opened seal, which is why such a scan keeps the normal [verified](term:scan-status-verified) marker rather than being flagged. Only a scan that failed verification is [Invalid](term:scan-status-invalid).\n\nThe certificate page carries the same word as a pill beside the certificate's status once any scan has reported it, so a certificate can be Active and Opened at the same time. What the hardware detects is that the seal's circuit was broken. It cannot tell who broke it or why, and the ordinary cause is the owner opening the item they bought. Read it alongside the rest of the scan history rather than as evidence of interference.",
    "seeAlso": [
      "scan",
      "tag",
      "scan-status-verified"
    ],
    "docs": [
      "certificates/certificate-scans"
    ]
  },
  {
    "id": "scan-status-stale-link",
    "term": "Stale link",
    "aliases": [
      "stale link",
      "expired link"
    ],
    "kind": "status",
    "audience": "brand",
    "short": "An expired certificate link was reopened. Usually harmless, such as an old link in a message or a stale browser tab.",
    "long": "[Certificate](term:certificate) links are short-lived by design, so an old one stops working rather than staying open indefinitely. Reopening one lands here, and it is nearly always innocent: a link forwarded in a message, a tab left open overnight, a page revisited from history. The distinction from [Suspicious link](term:scan-status-suspicious-link) is what happened to the token — one simply aged out, the other did not hold up to checking.",
    "seeAlso": [
      "scan",
      "certificate",
      "scan-status-suspicious-link",
      "scan-status-invalid-link"
    ],
    "docs": [
      "certificates/certificate-scans"
    ]
  },
  {
    "id": "scan-status-suspicious-link",
    "term": "Suspicious link",
    "aliases": [
      "suspicious link"
    ],
    "kind": "status",
    "audience": "brand",
    "short": "Someone opened this certificate's link using a forged or mismatched token. No tag was tapped, so this is probing rather than a scan.",
    "long": "The three link statuses all describe someone opening a certificate's web address rather than tapping its [tag](term:tag), so none of them says anything about the physical item. This is the one worth attention: the address carried a token whose signature did not hold up, or one issued for a different purpose or a different certificate — a pattern that looks like someone testing what the links will accept, rather than a person following an [old link](term:scan-status-stale-link). It is recorded against the certificate so that probing is visible.",
    "seeAlso": [
      "scan",
      "certificate",
      "scan-status-stale-link",
      "scan-status-invalid-link"
    ],
    "docs": [
      "certificates/certificate-scans"
    ]
  },
  {
    "id": "scan-status-verified",
    "term": "Verified",
    "aliases": [
      "verified scan"
    ],
    "kind": "status",
    "audience": "consumer",
    "short": "A tag was tapped and confirmed genuine. This is the normal outcome, so it carries no badge in the scan list.",
    "long": "The chip answered a challenge only that chip could answer, so the tap identified the physical [tag](term:tag) rather than merely proving that a link opened. It is the overwhelmingly common outcome, which is why it is the one status with no chip: badging every ordinary row would carry no information and bury the exceptions, so the absence of a badge is itself the signal. Only tags whose family can prove identity on each tap can reach this state — a sealed tag with no per-tap proof is reported as Connected instead.",
    "seeAlso": [
      "scan",
      "tag",
      "scan-status-invalid",
      "scan-status-connected",
      "scan-status-opened"
    ],
    "docs": [
      "certificates/certificate-scans"
    ]
  },
  {
    "id": "seat",
    "term": "Seat",
    "kind": "term",
    "audience": "brand",
    "short": "One person on your team with their own login. Your plan is sold with a set number of them, and you cannot invite past that number.",
    "long": "A seat is a person, not a [role](term:role): someone with several responsibilities still occupies one, and a role can be held by any number of people. Both people who have accepted and people you have invited but who have not signed in yet occupy a seat — otherwise a single batch of invites could take you well past your [plan](term:plan) without ever appearing to. Removing or archiving someone frees theirs immediately. If you are already above your plan's number, nobody is removed and nothing stops working; you simply cannot add anyone else until you upgrade or free a seat.",
    "seeAlso": [
      "plan",
      "role",
      "seats-used"
    ],
    "aliases": [
      "seats",
      "team seat",
      "user seat"
    ]
  },
  {
    "id": "seats-over-limit",
    "term": "Over your seat limit",
    "aliases": [
      "over limit",
      "over seat limit",
      "more seats than my plan"
    ],
    "kind": "status",
    "audience": "brand",
    "short": "Your team is larger than the number of seats your plan sells. Everyone keeps working. You cannot invite anyone new until you free a seat or upgrade.",
    "long": "Reads as something like “8 of 5 seats”, which looks like an error and is not one. It happens when [seat](term:seat) limits began applying to an account that was already larger than its [plan](term:plan), or when a plan changes to one that sells fewer seats. Nobody is removed, suspended or signed out to make the arithmetic work — the only thing that changes is that new invites are refused. Archiving or removing people brings the figure down; it is not held against you in any other way, and there is no separate charge for the extra people.",
    "seeAlso": [
      "seat",
      "seats-used",
      "plan"
    ]
  },
  {
    "id": "seats-used",
    "term": "Seats used",
    "aliases": [
      "seats in use",
      "seat usage"
    ],
    "kind": "formula",
    "audience": "brand",
    "formula": "people on your team who have accepted + people you have invited who have not",
    "unit": "seats",
    "short": "How many of your plan's seats are taken. Counts everyone on your team plus anyone invited who has not signed in yet.",
    "long": "Shown on the Users page as “4 of 5 seats”. Pending invites count from the moment you send them, so the figure moves when you invite rather than when the person accepts — that is what stops a single batch of invites carrying you past your [plan](term:plan). Archived people do not count, which is why archiving is the quickest way to free one. Nor do Vera staff attached to your account, so the figure can read one lower than the list of names beneath it. Withdrawing an invite frees its [seat](term:seat) as soon as you remove the row.",
    "seeAlso": [
      "seat",
      "plan",
      "subscription"
    ]
  },
  {
    "id": "sell",
    "term": "Sell",
    "aliases": [
      "selling",
      "sale",
      "resell",
      "resale"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "Handing an item to a buyer as part of a sale, recording the price alongside the handover so it becomes part of the item's history.",
    "long": "Selling differs from [gifting](term:gift) in exactly one respect that outlives the handover: a price is recorded. That figure is what makes a [provenance](term:provenance) useful to a later buyer, since a chain of documented sales is evidence of what an item has actually been worth rather than what it is asked for. Selling through Vera is not yet available in the app; until it is, an item sold privately is handed over as a [transfer](term:transfer), which moves ownership correctly but records no price.",
    "seeAlso": [
      "gift",
      "transfer",
      "provenance"
    ]
  },
  {
    "id": "shipment",
    "term": "Shipment",
    "kind": "term",
    "audience": "internal",
    "short": "One box of an order: the packages that travel together, under one label, in one box size. An order can have several.",
    "long": "The shipment is the unit fulfilment actually operates on, which is why it exists separately from both the order and the package. An [order](term:order) is what was bought, a [package](term:package) is what a brand assigns to a product, and a shipment is what goes in a box — so a shipment buys its own label and fulfils independently of its siblings.",
    "seeAlso": [
      "order",
      "package"
    ]
  },
  {
    "id": "sibling-certificate",
    "term": "Sibling certificate",
    "aliases": [
      "sibling",
      "siblings",
      "sibling certificates",
      "related certificate",
      "related certificates"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Another child of the same parent. Siblings are peers in one group, not parts of each other.",
    "long": "The other children of a certificate's own parent. A sibling is a peer, which is what separates it from a parent (above it) and a child (below it) — nothing contains a sibling, so no hierarchy is drawn between them. Whether siblings can be sold apart is not a property of the sibling relationship but of the group holding them: in a [divisible group](term:divisible-group) each sibling is independently sellable, and in an [indivisible group](term:indivisible-group) they go live and sell as one unit.",
    "seeAlso": [
      "child-certificate",
      "parent-certificate",
      "certificate-group",
      "divisible-group",
      "indivisible-group"
    ]
  },
  {
    "id": "source-health",
    "term": "Source health",
    "aliases": [
      "feed health",
      "healthy source",
      "quiet source"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "Whether a market source is polling and producing. Four states: Healthy, Quiet, Failing, Paused.",
    "long": "Health is derived from three facts about a source, not stored: whether it is enabled, whether its recent polls errored, and whether it has ever produced any content.\n\n**Healthy** — enabled, no consecutive failures, and it has produced content.\n\n**Quiet** — enabled and erroring on nothing, but it has never produced a single item. This is the state the column exists for: a feed whose URL has been 404ing for a week produces nothing, and \"nothing\" looks identical to a slow news week unless something says otherwise. Quiet is how a silently dead source becomes visible.\n\n**Failing** — recent polls have errored. The row shows the last error and how many times running it has failed.\n\n**Paused** — polling is switched off. The source and everything it has already produced are untouched; it is simply not fetched.\n\nOnly RSS and sitemap sources are polled at all. A social or other source is registered so pasted content can name its origin, and never shows anything but its enabled state."
  },
  {
    "id": "space",
    "term": "Space",
    "aliases": [
      "spaces",
      "vera space",
      "company space"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "A brand's own view of the platform, entered by Vera staff to work as that brand. Your permissions become what the brand grants Vera support.",
    "long": "Entering a space is a change of identity rather than a change of page: the internal navigation disappears, internal routes bounce you out, and what you may do comes from the brand's [Vera support access](term:vera-support-access) instead of your own [role](term:role). That is deliberate. It means you are seeing what the brand lets support see, which is the point of going in, and it is why a page can be empty or refused inside one brand's space and fine in another's: the banner says what this brand has granted. Because it is invisible to the brand, actions taken in a space are still yours and still attributed to you.",
    "seeAlso": [
      "vera-support-access",
      "role",
      "permission"
    ]
  },
  {
    "id": "split-group",
    "term": "Split group",
    "aliases": [
      "split a group",
      "splitting a group",
      "dissolve a group"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Breaking a whole group apart. Every member becomes a standalone certificate, and the parent certificate it leaves empty is archived.",
    "long": "Splitting destroys no record. It severs a relationship: every former child carries on as an ordinary standalone certificate, and the parent, now holding nothing, is archived so it stops listing as an empty group. The action is offered on the parent, and it is always available on a divisible group. On an indivisible group it is available only while every member is still unactivated and unowned, because after that the group is permanent. Separating members that are already active is a billable event: one of them carries the group's original charge and the rest are charged as new activations.",
    "seeAlso": [
      "remove-from-group",
      "certificate-group",
      "indivisible-group",
      "parent-certificate"
    ],
    "docs": [
      "certificates/certificates"
    ]
  },
  {
    "id": "studio",
    "term": "Studio",
    "aliases": [
      "branding editor"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "The editor where you set how your certificates look — theme, logo, colours and layout — without writing any code.",
    "long": "Studio styles the page a customer reaches when they tap an item, which for most brands is the only Vera surface a customer ever sees. It changes presentation and nothing else: no wording of the record, no ownership, no [provenance](term:provenance). Changes apply to every [certificate](term:certificate) at once rather than being versioned per item, so what a customer sees is always the current design, including on certificates issued long before it.",
    "seeAlso": [
      "certificate",
      "installation"
    ],
    "docs": [
      "certificates/studio"
    ]
  },
  {
    "id": "studio-primary-color",
    "term": "Primary color",
    "aliases": [
      "primary colour",
      "studio primary color"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Your main brand colour. It sets headings and primary buttons, but also field focus outlines, the pulsing rings on the scan screen, menu highlights and count badges.",
    "long": "Studio presents it as one colour, but the customer app derives an eleven-step scale from it and reads that scale in roughly a hundred places — so this control reaches every screen a customer can land on, not only the ones with a button. Page titles and section headings, primary buttons, the outline and soft glow when someone taps into a field, the row under the pointer in any menu or picker, the pulsing rings on the tag-[scan](term:scan) screen, the success tick after a claim or transfer, and the number badge on Filter and Sort all come from here.\n\nIn email it means far less: it fills buttons, and nothing else. Email headings follow your [text colour](term:studio-text-color) instead. And a recipient reading in dark mode sees none of your colours — the message switches to a fixed dark palette that overrides this, your text colour, your [border colour](term:studio-border-color) and your [background colour](term:studio-background-color). Fonts, [corner rounding](term:studio-border-radius) and your logo survive both appearances.",
    "seeAlso": [
      "studio",
      "studio-text-color",
      "studio-background-color"
    ],
    "docs": [
      "certificates/studio"
    ]
  },
  {
    "id": "studio-border-color",
    "term": "Border color",
    "aliases": [
      "border colour",
      "divider color",
      "divider colour"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "The colour of dividers and panel borders on your certificate pages, and of the header rule, footer rule and divider lines in the emails your customers receive.",
    "long": "This is the one control whose email half has always been the louder one. In email it draws the rule under the header, the rule above the footer, the dividers between sections and the border around a verification code — and that has worked since the token existed. In the app it draws dividers and the outline of bordered panels on the certificate page.\n\nIt does not affect disabled buttons, despite what this control claimed for a long time: a disabled button keeps its own colour at reduced opacity. Like every colour in [Studio](term:studio), it is discarded when the email is read in dark mode.",
    "seeAlso": [
      "studio",
      "studio-primary-color"
    ],
    "docs": [
      "certificates/studio"
    ]
  },
  {
    "id": "studio-text-color",
    "term": "Text color",
    "aliases": [
      "text colour",
      "body text color",
      "body text colour"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "The colour of body and paragraph text across your certificate pages and your emails. Large headings follow your primary colour instead, unless you have not set one.",
    "long": "Beyond paragraphs it colours button labels at rest, the selected item in the navigation bar and the selected row in a menu. It is worth checking against your [background colour](term:studio-background-color): a pale text colour on a pale surface has produced genuinely unreadable text on this app before.\n\nIn email it colours body copy **and** headings — the split with [primary colour](term:studio-primary-color) that exists in the app does not exist there. Small muted sub-labels are a fixed grey in both surfaces and are not yours to set.",
    "seeAlso": [
      "studio",
      "studio-primary-color",
      "studio-background-color"
    ],
    "docs": [
      "certificates/studio"
    ]
  },
  {
    "id": "studio-background-color",
    "term": "Background color",
    "aliases": [
      "background colour",
      "page background"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "The page surface behind your certificate pages, and the outer wall around your emails. Not the email card itself, which stays white in light mode.",
    "long": "In the app it is the surface behind the certificates list, the [certificate](term:certificate) page and the share page, and it decides whether the scan map draws in its light or dark style. It does not reach sign-in, forgot-password or the onboarding screens.\n\nCards, bottom sheets and toasts stay white on top of it, so a dark background gives you a dark page carrying white cards. That is a current limit of the theme rather than a setting you have missed. In email it paints only the wall around the message — the card itself stays white in light mode and near-black in dark, and neither is brandable.",
    "seeAlso": [
      "studio",
      "studio-text-color"
    ],
    "docs": [
      "certificates/studio"
    ]
  },
  {
    "id": "studio-title-font",
    "term": "Title font",
    "aliases": [
      "heading font"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "The typeface for large headings on your certificate pages and for the headline in your emails. Small section captions use the body font, not this one.",
    "long": "It applies to the four largest heading sizes. The smallest heading — the short caption above a group of rows — is not included and takes the [body font](term:studio-body-font), which is worth knowing if you have chosen a display face and cannot find it where you expected.\n\nIn email it applies to the headline only; everything else is the body font. Fonts are one of the three things that survive a recipient reading in dark mode, along with [corner rounding](term:studio-border-radius) and your logo.",
    "seeAlso": [
      "studio",
      "studio-body-font"
    ],
    "docs": [
      "certificates/studio"
    ]
  },
  {
    "id": "studio-body-font",
    "term": "Body font",
    "aliases": [
      "paragraph font"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "The typeface for everything that is not a large heading. Body copy, buttons, labels and navigation, across your certificate pages and your emails.",
    "long": "It is also the fallback for headings: leave the [title font](term:studio-title-font) unset and headings take this one, so a brand using a single typeface only has to set this.\n\nIn email it is loaded as a web font and applied to the whole message, which makes it the typographic choice that travels most reliably into an inbox — it survives dark mode, unlike every colour in [Studio](term:studio).",
    "seeAlso": [
      "studio",
      "studio-title-font"
    ],
    "docs": [
      "certificates/studio"
    ]
  },
  {
    "id": "studio-border-radius",
    "term": "Border radius",
    "aliases": [
      "corner radius",
      "corner rounding"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "How rounded your buttons are. Form fields follow the same setting but stop at 6px, so a very round value gives round buttons and only gently rounded fields.",
    "long": "Past buttons it rounds the top corners of bottom sheets, the navigation items, and menu panels at half the value you set. The cap on fields is deliberate: a 24px radius on a 48px-tall input deforms it, so a field takes the smaller of your value and 6px. Zero still means square everywhere, so choosing sharp corners gets you sharp corners.\n\nIn email it reaches buttons only — the card's own corners are fixed. It is one of the three things that survive dark mode in an inbox, along with your fonts and your logo.",
    "seeAlso": [
      "studio",
      "studio-primary-color"
    ],
    "docs": [
      "certificates/studio"
    ]
  },
  {
    "id": "subdomain",
    "term": "Subdomain",
    "aliases": [
      "vera subdomain",
      "brand address"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Your address on Vera, in the form yourbrand.vera.inc. It is where certificates open when you have no installation of your own.",
    "long": "The subdomain is issued when your company is created and is the default destination for every [scan](term:scan), so [certificates](term:certificate) are reachable before you have integrated anything. Changing it is not cosmetic: tags already in customers' hands were [encoded](term:encode) pointing at the old address, and the address written to a tag is fixed at the moment it is encoded. Treat a rename as a public change of address rather than a setting, and expect to be asked to confirm it.",
    "seeAlso": [
      "installation",
      "certificate",
      "encode"
    ],
    "docs": [
      "integrations/settings-installation"
    ]
  },
  {
    "id": "subscription",
    "term": "Subscription",
    "kind": "term",
    "audience": "brand",
    "short": "Your recurring agreement with Vera. It sets which plan you are on, what it costs, and when the next period starts.",
    "long": "The subscription is the [plan](term:plan) you hold, not the whole of what you pay: [activations](term:activation) are billed on top of it, against the [allowance the plan includes](term:included-activations). Its state governs access as well as billing, so a subscription that has lapsed is worth resolving before it reaches the point where the platform stops opening.",
    "seeAlso": [
      "plan",
      "invoice",
      "included-activations"
    ]
  },
  {
    "id": "subscription-status-active",
    "term": "Active",
    "kind": "status",
    "audience": "brand",
    "short": "The subscription is paid and running. This is the only state in which nothing about billing needs your attention.",
    "seeAlso": [
      "subscription"
    ]
  },
  {
    "id": "subscription-status-canceled",
    "term": "Canceled",
    "kind": "status",
    "audience": "brand",
    "short": "The subscription has ended. Certificates already issued are unaffected; what stops is the plan and everything it included.",
    "seeAlso": [
      "subscription"
    ]
  },
  {
    "id": "subscription-status-incomplete",
    "term": "Incomplete",
    "kind": "status",
    "audience": "brand",
    "short": "The very first payment on a new subscription has not gone through yet. Nothing has started, and the card usually needs confirming.",
    "long": "Incomplete only ever describes a subscription's opening payment, which is why it is a different state from [Past due](term:subscription-status-past-due): there is no previous period that was paid for. If the first payment is never completed the subscription [expires](term:subscription-status-incomplete-expired) rather than lapsing, and starting again means a new subscription.",
    "seeAlso": [
      "subscription",
      "subscription-status-incomplete-expired"
    ]
  },
  {
    "id": "subscription-status-incomplete-expired",
    "term": "Incomplete expired",
    "kind": "status",
    "audience": "brand",
    "short": "A new subscription whose first payment was never completed in time. It never started, so there is nothing to resume.",
    "seeAlso": [
      "subscription",
      "subscription-status-incomplete"
    ]
  },
  {
    "id": "subscription-status-past-due",
    "term": "Past due",
    "kind": "status",
    "audience": "brand",
    "short": "A payment failed and is being retried. The subscription still works. Update the payment method before the retries run out.",
    "long": "Past due is the warning window, and it is the cheapest moment to fix the problem: nothing has been cut off, and a working card resolves it with no further action. If the retries are exhausted the [subscription](term:subscription) moves on to [Unpaid](term:subscription-status-unpaid), which is the state that costs you access.",
    "seeAlso": [
      "subscription",
      "subscription-status-unpaid"
    ]
  },
  {
    "id": "subscription-status-paused",
    "term": "Paused",
    "kind": "status",
    "audience": "brand",
    "short": "Collection has been stopped deliberately. The subscription is not cancelled and is not being charged while it stays this way.",
    "seeAlso": [
      "subscription"
    ]
  },
  {
    "id": "subscription-status-trialing",
    "term": "Trialing",
    "kind": "status",
    "audience": "brand",
    "short": "The subscription is inside its trial period. It has not been charged yet, and it will convert to a paid period when the trial ends.",
    "seeAlso": [
      "subscription"
    ]
  },
  {
    "id": "subscription-status-unpaid",
    "term": "Unpaid",
    "kind": "status",
    "audience": "brand",
    "short": "Every retry of a failed payment has been exhausted. This is the end of the dunning window, not the beginning of it.",
    "seeAlso": [
      "subscription",
      "subscription-status-past-due"
    ]
  },
  {
    "id": "support-request",
    "term": "Support request",
    "aliases": [
      "ticket"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "A message you raise with Vera. You choose what kind it is; the reply, and the state it moves through, come back on the same thread.",
    "long": "A request carries two separate things: a [TYPE](term:support-request-type), which you pick once when you raise it and which does not change, and a STATUS, which only Vera moves as the request is worked. Replying to a request that had been [resolved](term:support-status-resolved) reopens it rather than starting a new one, so a thread stays one conversation.",
    "seeAlso": [
      "support-request-type"
    ]
  },
  {
    "id": "support-request-type",
    "term": "Request type",
    "aliases": [
      "support type"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "What kind of request this is: a question, something broken, or something you would like built. You choose it once and it does not change.",
    "long": "Type is a classification and status is a lifecycle, which is why they are shown differently: a square badge for the kind of thing it is, a rounded chip for where it has got to. A feature request can end up linked to a public roadmap item; a bug and a question cannot.",
    "seeAlso": [
      "support-request"
    ]
  },
  {
    "id": "support-error-reference",
    "term": "Error reference",
    "aliases": [
      "ref",
      "reference id"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "A short code, eight letters and numbers, that names one failure. Quote it to Vera and we can find exactly what went wrong.",
    "long": "Every time something fails on Vera's side, the failure is recorded under its own reference. When you report a problem from the error message, the reference is attached for you, so you never need to copy it. It identifies the failure, not you or your data, and it is different every time.",
    "seeAlso": [
      "support-request",
      "support-automatic-report"
    ]
  },
  {
    "id": "support-automatic-report",
    "term": "Automatic report",
    "aliases": [
      "auto report",
      "automatic error report",
      "system-generated report"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "A bug report the app files by itself when a page crashes or shows an error that looks like a fault on Vera's side, so Vera hears about it even if nobody writes in.",
    "long": "It carries only the facts of the failure: what was shown, what failed, its [reference](term:support-error-reference), the page, your browser and app version. Never what you typed. It does not appear in your request list unless you add your own words to it, which the crash message offers. If you write in about the same error instead, your [request](term:support-request) is linked to it.",
    "seeAlso": [
      "support-request",
      "support-error-reference"
    ]
  },
  {
    "id": "support-report-source",
    "term": "Source (support queue)",
    "aliases": [
      "report source",
      "auto (logged only)",
      "auto + user details",
      "user report"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "Where a request in the internal Support queue came from: an automatic report nobody has written about, an automatic report a person also told us about, or a person's own report.",
    "long": "**Auto (logged only)** is an [automatic report](term:support-automatic-report) with no person attached; the error details and the logs are all there is. **Auto + user details** is one incident with a person behind it: an automatic report that someone's request links to or that they added a note to, and that person's own linked request. **User report** is a request a person wrote with no automatic report involved. Automatic rows show their [reference](term:support-error-reference) and how many times the error happened; open one to copy the reference or search the logs for it.",
    "seeAlso": [
      "support-automatic-report",
      "support-error-reference",
      "support-request"
    ]
  },
  {
    "id": "support-status-closed",
    "term": "Closed",
    "kind": "status",
    "audience": "brand",
    "short": "Filed away without a resolution to point at, usually because it was withdrawn or overtaken. Replying reopens it.",
    "seeAlso": [
      "support-request"
    ]
  },
  {
    "id": "support-status-in-progress",
    "term": "In progress",
    "kind": "status",
    "audience": "brand",
    "short": "Someone at Vera is working on it. Nothing is needed from you while it stays here.",
    "seeAlso": [
      "support-request"
    ]
  },
  {
    "id": "support-status-open",
    "term": "Open",
    "kind": "status",
    "audience": "brand",
    "short": "Raised and waiting on Vera. A request also returns here on its own if you reply to one that had been resolved or closed.",
    "seeAlso": [
      "support-request"
    ]
  },
  {
    "id": "support-status-resolved",
    "term": "Resolved",
    "kind": "status",
    "audience": "brand",
    "short": "Vera considers it answered or fixed. Replying reopens the same thread rather than starting a new request.",
    "seeAlso": [
      "support-request"
    ]
  },
  {
    "id": "support-status-waiting-on-you",
    "term": "Waiting on you",
    "aliases": [
      "waiting on customer"
    ],
    "kind": "status",
    "audience": "brand",
    "short": "Vera has replied and needs something back before it can go further. This is the one state where the request is stalled on you.",
    "long": "Vera staff see this same state described from their side, as waiting on the customer. It is one state with two readings, so that whoever is looking at it is told what THEY have to do.",
    "seeAlso": [
      "support-request"
    ]
  },
  {
    "id": "sync-mode",
    "term": "Sync mode",
    "aliases": [
      "product sync mode",
      "value sync"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Whether later edits in your store keep overwriting a product in Vera. Set per product: follow the store default, always sync, or never.",
    "long": "The question it answers is not how a product first arrives but what happens to it afterwards. Locked protects a product you have edited by hand in Vera, so a store update can never overwrite your wording; Always sync does the opposite and takes the store's values every time; Follow store default defers to the connection's own \"Sync value updates\" setting. A manual Sync now is an explicit instruction and runs whatever the mode says. The control appears only on [products](term:product) that came from a [connected store](term:integration).",
    "seeAlso": [
      "field-mapping"
    ],
    "docs": [
      "integrations/settings-field-mapping"
    ]
  },
  {
    "id": "tag",
    "term": "Tag",
    "aliases": [
      "tags",
      "nfc tag"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "The Vera tag attached to an item, which a phone reads to open that item's certificate.",
    "long": "A tag carries no product information of its own. It holds its own identity, and Vera resolves that identity to whichever [certificate](term:certificate) the tag is paired with. The pairing is a record held by Vera rather than something written onto the tag, so moving a tag onto a different object does not move the certificate with it — the tag simply keeps opening the certificate it was paired to. Some tags can also prove on each tap that they are the original and not a copy, which is what separates a [verified](term:scan-status-verified) scan from one that only shows a link was opened.",
    "seeAlso": [
      "certificate",
      "encode",
      "scan",
      "package"
    ],
    "docs": [
      "integrations/settings-installation",
      "tags/assign-product-to-package"
    ]
  },
  {
    "id": "tag-family",
    "term": "Tag family",
    "aliases": [
      "chip family",
      "tag_family",
      "chip"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "The chip model behind a tag variant (ntag424_dna, st25ta_e, st25tv), which determines how it is encoded and verified.",
    "long": "Set per Shopify variant via the `custom.tag_family` metafield and enforced at Vera's read boundary. NEVER surfaced to buyers: they choose by capability and availability, not by chip. [Encoding](term:encode) is blocked rather than guessed when it is missing, because the wrong family breaks encode and [verify](term:verify).",
    "seeAlso": [
      "tag",
      "encode"
    ]
  },
  {
    "id": "claim-on-scan",
    "term": "Claim on scan",
    "aliases": [
      "claim on tap",
      "business card claim",
      "scan to claim"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "A per-product switch that lets whoever taps the item become its owner, once. The first person to verify their email wins, and the item then behaves like any owned item.",
    "long": "Built for items handed to strangers, such as Vera's own business cards. Each card is a certificate owned by a Vera account until someone taps it. The visitor proves the tap is fresh, confirms their email by Google or a one-time code, and ownership moves to them as a scan-claimed [transfer](term:transfer). The previous owner is not emailed. Only Vera platform admins can switch it on, because it lets anyone who holds the item take ownership. It never applies once the item has passed between two people.",
    "seeAlso": [
      "certificate",
      "owner",
      "transfer",
      "tag"
    ]
  },
  {
    "id": "takeover",
    "term": "Takeover",
    "kind": "term",
    "audience": "internal",
    "short": "The moment a demo becomes a real customer: the company stops being a demo and is billed for the first time.",
    "long": "Takeover is one event, stamped once, and the database refuses to let a company stop being a [demo](term:demo) without recording it — because the exclusions that keep a demo out of billing and analytics are keyed on being a demo, so an untracked conversion would silently take them all off. It also cannot be stamped until a real person from the brand has joined; staff visiting the company do not count.",
    "seeAlso": [
      "demo",
      "console-trial",
      "subscription"
    ]
  },
  {
    "id": "time-to-first-resale",
    "term": "Time to first resale",
    "aliases": [
      "days to first resale"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "The typical time, in days, from a certificate being created to the first time that item was resold. A median, over items that were resold.",
    "long": "Measured from creation rather than from the first sale, so it includes however long the item sat before reaching its first owner — Vera does not hold the date a brand sold it, and inventing one would make this figure look precise while being wrong.\n\nItems never resold are left out rather than counted as a very long wait. So it describes resale behaviour among items that resold, and says nothing about how many did not. [Gifts](term:gift) and the brand's first handoff to an owner are not resales.",
    "formula": "Median days from certificate creation to the item's first completed resale.",
    "seeAlso": [
      "resales",
      "sell",
      "certificate",
      "time-to-first-scan"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "time-to-first-scan",
    "term": "Time to first scan",
    "aliases": [
      "days to first scan"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "The typical gap, in days, between a certificate being created and the first time it was scanned. A median, not an average.",
    "long": "A median rather than a mean, so one certificate that sat dormant for a year cannot drag the figure past every real observation — against a long tail of slow-moving stock, an average reports a typical wait that almost nothing actually experienced.\n\nCertificates never scanned are excluded rather than counted as an infinite wait. So this describes the items that did get scanned, and says nothing about how many did not.",
    "formula": "Median days from certificate creation to its first scan.",
    "seeAlso": [
      "certificate",
      "scan",
      "time-to-first-resale"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "transfer",
    "term": "Transfer",
    "aliases": [
      "transfers",
      "transfer out",
      "transfer in"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "Moving a certificate from one owner to the next; it is pending until the receiving owner confirms.",
    "long": "A transfer is the only thing that changes a certificate's [owner](term:owner). The sender starts it and the item stays with them until the recipient accepts, so an unclaimed transfer can be cancelled and a mistyped address never costs anyone the item. How each one completed is recorded and shown on the [provenance](term:provenance): accepted from the emailed link, or accepted with a scan of the physical tag as well. For most brands, the first transfer out of the brand is also what makes the certificate [active](term:certificate-status-active).",
    "seeAlso": [
      "owner",
      "provenance",
      "activation-trigger"
    ]
  },
  {
    "id": "transfer-completion-rate",
    "term": "Transfer completion rate",
    "aliases": [
      "completion rate"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "The share of ownership transfers started in the period that went on to complete. Not computed yet; the card shows a dash.",
    "long": "Both halves are counted by the date the transfer was STARTED, so this is one cohort split by where it got to rather than two independently scoped figures — which is what makes the ratio mean anything at all. One consequence worth knowing: a recent window understates the rate, because the transfers in it have had less time to be accepted.\n\n[Completed transfers](term:transfers-completed) and [In progress transfers](term:transfers-in-progress) are the two counts behind it, and both are live on the KPI row today.",
    "formula": "Completed transfers ÷ all transfers started in range.",
    "seeAlso": [
      "transfer",
      "transfers-completed",
      "transfers-in-progress",
      "transfer-completion-time"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "transfer-completion-time",
    "term": "Transfer completion time",
    "aliases": [
      "time to complete transfer"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "The typical time, in days, from an ownership transfer being started to it being accepted. A median, so one stalled transfer cannot skew it.",
    "long": "⚠️ Only transfers that COMPLETED are in this figure, and only ones that completed after Vera began stamping a completion time. Historical rows were deliberately not backfilled with a guess, so the figure fills in as new transfers finish rather than starting out complete and wrong — a thin number early on is the ramp, not a fault.\n\nTransfers still waiting are excluded rather than counted as long ones, which means a period full of unaccepted transfers can show a short and healthy-looking time. Read it beside [In progress transfers](term:transfers-in-progress).",
    "formula": "Median days from transfer start to transfer completion, over transfers that completed.",
    "seeAlso": [
      "transfer",
      "transfers-completed",
      "transfers-in-progress",
      "transfer-completion-rate"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "transfers-completed",
    "term": "Completed transfers",
    "aliases": [
      "completed transfers count"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "Transfers started in the period that have since completed. Counted by the date the transfer began, not the date it was accepted.",
    "long": "⚠️ The date range picks transfers by when they were STARTED, so this is not \"transfers completed in the period\". One begun last month and accepted this month is counted in last month; one begun this month and still unaccepted is not counted at all until it completes, at which point it appears in this period retroactively. That is what makes it comparable with [In progress transfers](term:transfers-in-progress) beside it — the two split one cohort of started transfers by where it has got to, rather than counting two differently scoped populations. A [transfer](term:transfer) completes only when the receiving owner confirms, so a request nobody opened is never counted here however long it has waited.",
    "formula": "Count of transfers created inside the date range whose status is now completed.",
    "seeAlso": [
      "transfer",
      "transfers-in-progress",
      "transfer-completion-rate",
      "transfer-completion-time"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "transfers-in-progress",
    "term": "In progress transfers",
    "aliases": [
      "in progress transfers count",
      "open transfers"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "Transfers started in the period that are still open — sent, and not yet accepted, cancelled or expired.",
    "long": "The date range picks transfers by when they were started, but the status is read as it stands NOW — so this figure falls over time without the range changing, as the transfers in it are accepted and move into [Completed transfers](term:transfers-completed). The two therefore split one cohort rather than counting two, which is what lets them sit side by side. Nothing here says how long a transfer has been waiting; [Transfer completion time](term:transfer-completion-time) is the figure for that, and it only ever sees the ones that finished.",
    "formula": "Count of transfers created inside the date range whose status is still pending.",
    "seeAlso": [
      "transfer",
      "transfers-completed",
      "transfer-completion-time"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "two-factor-authentication",
    "term": "Two-factor authentication",
    "aliases": [
      "2fa",
      "two-factor",
      "two-step verification"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "A second step when you sign in. After your password or emailed code, you enter a short code from an authenticator app or a text message.",
    "long": "One factor is something you know (a password, or a code sent to your email). Two-factor authentication adds something you have — your phone. Someone who learns your password still cannot sign in without it.\n\nIt is optional for most people and can be turned on from your security settings. Once it is on, every sign-in asks for the second code; there is no way to skip it, which is the point. If you lose access to your second factor, a recovery code gets you back in.",
    "seeAlso": [
      "authenticator-app",
      "recovery-code"
    ],
    "docs": [
      "settings/settings-security"
    ]
  },
  {
    "id": "unattributed-invalid-scans",
    "term": "Unattributed invalid scans",
    "aliases": [
      "unattributed scans"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "Rejected scans that matched no certificate at all — a tag presented to Vera that Vera never issued. Reported beside the invalid count, never inside it.",
    "long": "These are the highest-signal rejections there are: not a misread of your [tag](term:tag), but something claiming to be one. They cannot join [Invalid scans](term:invalid-scans) because every filter on that figure reaches through the link to a [certificate](term:certificate), which a certificate-less scan does not have — so they are counted separately rather than lost.\n\n⚠️ For the same reason the count is reported as zero, not estimated, whenever you filter the page by owner, product or certificate. A scan with no certificate cannot satisfy a condition about one, and the honest answer under that filter is that the figure is not addressable — not a count that quietly ignores the filter you set.",
    "formula": "Count of invalid scans in range carrying no certificate, scoped to your company.",
    "seeAlso": [
      "invalid-scans",
      "invalid-scan-rate",
      "tag",
      "scan-status-invalid"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "value-delivered",
    "term": "Value delivered",
    "aliases": [
      "roi value delivered"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "The money value Vera can show it produced for you, added up across every metric with a measured figure behind it.",
    "long": "Only metrics Vera can actually MEASURE are added in. One it cannot compute is left out of the sum entirely rather than counted as zero, and the card reports how many sources went into the figure — so a modest total is visibly a small number of sources rather than a poor result.\n\nMetrics resting on a figure you supply, such as [Return fraud prevented](term:return-fraud-prevented), join the sum once you supply it and are excluded until then. The total never quietly assumes a number on your behalf, which means real value Vera cannot see understates this figure. That is the direction it should err in: it is the numerator of [Return on Vera](term:return-on-vera), and an overstated one would be the more flattering mistake.",
    "formula": "Sum of the currency metrics that contribute to return, over all time.",
    "seeAlso": [
      "return-on-vera",
      "vera-spend",
      "return-fraud-prevented",
      "counterfeit-deflections"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "vera-spend",
    "term": "Spend with Vera",
    "aliases": [
      "vera spend"
    ],
    "kind": "formula",
    "audience": "brand",
    "short": "What you have paid Vera — subscription plus billable activations — read from settled invoices across your whole history.",
    "long": "Read from your [invoices](term:invoice) rather than recomputed, so this is money that actually changed hands. Charges accrued since the last invoice are added on top and named separately in the breakdown, because they are real spend that simply has not been billed yet — leaving them out would make a company mid-billing-period look cheaper than it is and push [Return on Vera](term:return-on-vera) up.\n\nWhere the invoice history is incomplete for the range, the card says so rather than reporting a confident total. An understated denominator overstates the return, so silence there would flatter rather than merely mislead.\n\n⚠️ Shown in the currency your invoices were actually settled in, which is not necessarily your reporting currency. Value delivered is in your reporting currency, so when the two differ the hero shows two different currencies side by side. That is deliberate: restating a settled amount under another currency's symbol would be the same silent relabelling that read-time conversion was rejected for, and showing both makes the divergence visible instead of hiding it.",
    "formula": "Subscription charges plus activation charges across settled invoices, all time.",
    "seeAlso": [
      "invoice",
      "subscription",
      "activation-total-cost",
      "return-on-vera"
    ],
    "tags": [
      "metric"
    ]
  },
  {
    "id": "vera-support-access",
    "term": "Vera support access",
    "aliases": [
      "vera support",
      "vera support grant",
      "vera support column"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "What Vera's support staff may see and do while working inside your account. You set it in the Vera support column; new companies start with Ask Vera only.",
    "long": "It is the **Vera support** column in Settings → Roles & permissions. Nobody is assigned to it: it applies only to Vera staff while they are inside your account to help you, and it replaces their own access rather than adding to it, so they see exactly what you switched on and nothing more. New companies start with **View AI** switched on, so support can use Ask Vera while helping you; everything else starts off. Ask Vera still only reads data the rest of the column allows. Switch any permission on or off at any time, View AI included. An empty column means Vera support cannot see anything in your account.",
    "seeAlso": [
      "permission",
      "role"
    ]
  },
  {
    "id": "verify",
    "term": "Verify",
    "aliases": [
      "verified",
      "verification",
      "authenticate",
      "authenticity"
    ],
    "kind": "term",
    "audience": "consumer",
    "short": "Checking that an item is genuine by tapping its tag with a phone. What is checked is the tag answering for itself, not a code or image that could be copied.",
    "long": "The distinction that matters is between reading something and challenging something. A printed code, a QR square or a photograph can be duplicated perfectly, so reading one proves only that a copy exists somewhere. A [tag](term:tag) that can verify is asked a question on each tap that only that individual tag can answer, and a copy of it cannot. This is why opening a [certificate](term:certificate) by following a link is recorded differently from opening it by tapping: both show you the certificate, but only one of them proves the physical item was present.",
    "seeAlso": [
      "scan",
      "tag",
      "certificate",
      "scan-status-verified"
    ]
  },
  {
    "id": "white-label",
    "term": "White label",
    "aliases": [
      "powered by vera",
      "hide powered by"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Removing Vera's name from what your customers see: the badge on a public certificate, the attribution in your emails, and the sender line on your SMS.",
    "long": "White label is only about the ATTRIBUTION. [Putting your own logo, colours and typeface](term:studio) on a [certificate](term:certificate) is not part of it and is not gated: every plan can do that, because a certificate carrying Vera's styling would misrepresent whose item it is. What white label removes is the line saying the page is powered by Vera — on certificate pages, in email footers, and (VRG-487) at the front of every SMS, which otherwise leads with \"{your name} (via vera.inc)\" instead of just your name.",
    "seeAlso": [
      "plan",
      "studio",
      "certificate-view"
    ]
  },
  {
    "id": "email-requirement",
    "term": "Email requirement",
    "aliases": [
      "require email to view",
      "email gate",
      "collect email"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Whether a visitor must give their email address before a certificate's details open. Set for the whole company, or overridden for one product or one certificate.",
    "long": "A gate, not a prompt: with it on, a visitor who does not enter an email does not see the [certificate](term:certificate)'s details. What they always see is the product preview, so a [scan](term:scan) never dead-ends on a blank page — the item is identified, and the detail behind it is what the email buys. Addresses collected this way are stored against the view, which makes the setting a privacy decision as much as a lead-capture one, so it belongs in whatever privacy notice you publish. The company default is set under Settings, and a single [product](term:product) or a single certificate can override it — useful when a limited run warrants the friction and the rest of the catalogue does not. The most specific setting wins: certificate, then product, then the company default. It resolves independently of the [location requirement](term:location-requirement); either, both or neither can apply to the same certificate.",
    "seeAlso": [
      "location-requirement",
      "certificate",
      "scan",
      "activation-trigger"
    ],
    "docs": [
      "company-settings/settings-features"
    ]
  },
  {
    "id": "location-requirement",
    "term": "Location requirement",
    "aliases": [
      "require a location to view",
      "location gate",
      "collect location"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "Whether a visitor must share their location before a certificate's details open. Set for the whole company, or overridden for one product or one certificate.",
    "long": "This is a gate and not a request, which is the part worth reading twice. A visitor who declines their browser's location prompt — or who is stood indoors with no usable signal, an ordinary way to tap a tag — does not see the [certificate](term:certificate)'s details. The product preview stays visible either way, so the item is still identified. Turning it on trades reach for data: every [scan](term:scan) that yields a location is one you can place on a map, and every visitor whose device cannot produce one is a reader you turned away. Positions are stored against the scan, so your privacy notice needs to cover them. The company default is set under Settings, and a single [product](term:product) or a single certificate can override it. The most specific setting wins: certificate, then product, then the company default. It resolves independently of the [email requirement](term:email-requirement).",
    "seeAlso": [
      "email-requirement",
      "scan",
      "certificate",
      "scan-location-accuracy"
    ],
    "docs": [
      "company-settings/settings-features"
    ]
  },
  {
    "id": "item-manufacture-date",
    "term": "Item manufacture date",
    "aliases": [
      "item manufactured",
      "item made date"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "When one individual item was made, recorded on its own certificate. Distinct from the product's manufacture date, which applies to the whole model.",
    "long": "A [product](term:product) carries one manufacture date for the design; a [certificate](term:certificate) can carry its own for the single unit it stands for. The two sit side by side on a certificate and answer different questions — when this line was introduced, and when this particular piece came off the bench — so a run made two years after the model launched reads correctly instead of inheriting the model's date. Nothing writes it automatically: it is entered by hand on the certificate, and reads Not set until someone does, which is why leaving it empty is safer than approximating it.",
    "seeAlso": [
      "certificate",
      "product",
      "product-manufacture-date",
      "product-lifespan"
    ],
    "docs": [
      "certificates/certificate-scans"
    ]
  },
  {
    "id": "product-manufacture-date",
    "term": "Product manufacture date",
    "aliases": [
      "product manufactured",
      "model manufacture date"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "When the model was made, recorded on the product and shared by every certificate for it. An item with no manufacture date of its own falls back to this in its provenance.",
    "long": "Set on the [product](term:product) and not on the certificate: the row on a certificate is a read-only view of the model's date, so changing it changes what every certificate for that product shows. That is the point of it. A hundred units of one design share one date, and recording it once is the only way that stays true as units are added.\n\nThe relationship with the [item's own date](term:item-manufacture-date) runs one way. Where an item has no date of its own, its [provenance](term:provenance) origin row shows the product's, so a brand that tracks dates only at the model level still gets a dated origin. Where the item does have one, the item's wins and the product's goes on describing the model. The certificate page resolves nothing between them: it shows both rows and labels each with what it belongs to.\n\nIt is also the preferred starting point for [product lifespan](term:product-lifespan), which falls back to certificate creation dates for products that have none. Filling it in is what makes that figure measure an item's age rather than its time on the system.",
    "seeAlso": [
      "item-manufacture-date",
      "product",
      "provenance",
      "product-lifespan"
    ],
    "docs": [
      "products/products",
      "certificates/certificates"
    ]
  },
  {
    "id": "certificate-creation-date",
    "term": "Certificate creation date",
    "aliases": [
      "creation date",
      "certificate created"
    ],
    "kind": "term",
    "audience": "brand",
    "short": "When this certificate record was created in Vera, not when the item was made or sold. Certificates are usually minted in bulk, before the item they stand for is finished.",
    "long": "Vera stamps this when the record is written and nothing changes it afterwards. It is the only date on a certificate that describes the record rather than the object, which is why it is the wrong number to reach for when someone asks how old an item is.\n\nWhat it is most often read as is [activation](term:activation), a different moment and the one Vera bills for. A certificate can sit created and inactive for months, so for a brand that activates on sale, the gap between the two dates is roughly how long the item sat in stock.\n\nIt does stand in for age in one place. [Product lifespan](term:product-lifespan) measures from a product's manufacture date where there is one and from the earliest certificate creation date where there is not, so a catalogue imported long after it was made reads younger than it is. Filling in manufacture dates is what moves those products onto the truer basis.",
    "seeAlso": [
      "certificate",
      "activation",
      "item-manufacture-date",
      "product-lifespan"
    ],
    "docs": [
      "certificates/certificates"
    ]
  },
  {
    "id": "became-client",
    "term": "Became a client",
    "aliases": [
      "became_client",
      "became client",
      "client conversion"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "The moment a company paid its first invoice (above $0, Stripe live mode): the same moment its deal is Won. Stamped once; demo, internal, test and example companies never count.",
    "long": "It is the last step of the website funnel, and the moment vera-2 sends a `became_client` event to the website's PostHog so the marketing funnel ends at the deal. It is read by the same rule as [Won](term:sales-stage-paid-client), from the same recorded invoice, and dated at that invoice. It is stamped once and never moves: a later invoice, a plan change or a resubscription does not stamp it again. A comped company counts once an invoice above $0 is paid. A test-mode invoice never counts. A [demo](term:demo) never counts while it is one.\n\nUntil 2026-10-20 (VRG-727) it was stamped when the company's own subscription first turned active; stamps made before then keep their date. Companies that were already paying when it first shipped are marked pre-existing instead and never fire, because their real date is unknown and is not guessed. What happened to the event itself is recorded separately, on the [PostHog status](term:became-client-posthog-status).",
    "seeAlso": [
      "became-client-tracked",
      "became-client-posthog-status",
      "subscription",
      "demo",
      "sales-stage-paid-client"
    ]
  },
  {
    "id": "became-client-tracked",
    "term": "Tracked",
    "aliases": [],
    "kind": "status",
    "audience": "internal",
    "short": "This company's became_client event reached the website's PostHog, so the marketing funnel shows the conversion against the visitor who became the client.",
    "long": "Only this outcome is ever shown. No chip means the event was not sent, and usually on purpose: vera-2 never creates a PostHog person, so a client whose analytics were never on at vera.inc, or who later withdrew or opted out, is skipped and stays skipped. That is the visitor's privacy choice, not something to fix. The full set of outcomes is on the [PostHog status](term:became-client-posthog-status).",
    "seeAlso": [
      "became-client",
      "became-client-posthog-status",
      "lead-in-posthog"
    ]
  },
  {
    "id": "became-client-posthog-status",
    "term": "became_client PostHog status",
    "aliases": [
      "became_client_posthog_status"
    ],
    "kind": "column",
    "audience": "internal",
    "short": "What happened to a company's became_client event: sent, skipped, disabled, failed or pre-existing. Only sent is shown in the platform, as Tracked.",
    "long": "Empty means queued: the company [became a client](term:became-client) and the per-minute worker has not reached it yet.\n\n**sent** — captured against the first candidate email (the founder, then the company's admins, then its signups) whose PostHog person exists and has not withdrawn consent. Shown as [Tracked](term:became-client-tracked).\n\n**skipped_no_person** — no candidate has a PostHog person, meaning nobody from the company had analytics on and then submitted a form on vera.inc. Nothing sent; final.\n\n**skipped_withdrawn** — the only people found had withdrawn analytics consent. Nothing sent; final.\n\n**disabled** — the API was running without PostHog configured (development always, production before its PostHog settings landed). Nothing sent. Clearing the status re-queues the row once the settings are in place.\n\n**failed** — the lookup or the send errored. Not retried automatically; clearing the status re-queues it, and the event's fixed id means a resend cannot double-count.\n\n**pre_existing** — already a paying client when the event shipped. Never fires.\n\n**sending** — a worker has claimed the row. One stuck for more than 15 minutes is reclaimed.",
    "seeAlso": [
      "became-client",
      "became-client-tracked"
    ]
  },
  {
    "id": "website-all-visitors",
    "term": "All visitors (Vercel)",
    "aliases": [
      "all visitors",
      "vercel visitors"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "Everyone who loaded vera.inc, counted without cookies by Vercel whatever their consent choice. Counts only: no person, email or journey behind them.",
    "long": "The larger of the two website populations and the only complete one, because it does not depend on anyone's analytics choice. The price is that it is aggregate: Vercel reports visitors, pageviews and custom-event counts, never a person, so nothing on the page that names or follows an individual comes from here, and campaign (UTM) breakdowns are not available on the current plan.\n\nVisitors are unique within one query, so adding days or slices together counts a returning visitor twice. A range reaching past Vercel's 366-day reporting window is summed from the [snapshot](term:website-analytics-snapshot) and labelled as a sum. Its custom events (form opened, submitted, ROI started, calculated) count events rather than people. Its counterpart is [visitors with analytics on](term:website-consented-visitors).",
    "seeAlso": [
      "website-consented-visitors",
      "website-analytics-snapshot"
    ]
  },
  {
    "id": "website-consented-visitors",
    "term": "Visitors with analytics on (PostHog)",
    "aliases": [
      "consented visitors",
      "posthog visitors"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "Visitors PostHog may record, as distinct people. Outside the US (EU, UK, elsewhere): only after they accept. In the US: by default, unless they opt out or send GPC.",
    "long": "A subset of all visitors, and smaller by design. Only this population can be followed as individuals: sessions, first source, landing page, city, recordings and, once someone submits a form, their email. So Live now, Visitor history, campaign attribution and the first-touch columns of a [lead journey](term:lead-journey) all come from here, and all of them undercount. A visitor whose analytics are off (declined outside the US; opted out or sent Global Privacy Control in the US) is still in [all visitors](term:website-all-visitors) but invisible here; one who withdraws later contributes nothing further to lead journeys. Session recordings exist only for visitors who allowed recording. Local, preview and test-account traffic is excluded.",
    "seeAlso": [
      "website-all-visitors",
      "lead-journey",
      "lead-in-posthog"
    ]
  },
  {
    "id": "lead-journey",
    "term": "Lead journey",
    "aliases": [
      "lead journeys",
      "website lead",
      "website leads",
      "website leads card"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "vera-2's own record of one website lead, by email: where they first came from, and when they reached each step from the form to becoming a client.",
    "long": "One row per email, refreshed nightly from signups, demos and companies in vera-2 and, for [visitors with analytics on](term:website-consented-visitors), their first touch and form and ROI-calculator steps from PostHog. It exists because neither vendor keeps data forever. First touch and step times only ever move earlier and last contact only later, so a refresh never rewrites history.\n\nIt is personal data and is deleted under [lead-journey retention](term:lead-journey-retention). Not to be confused with a brand's [lead](term:lead), a stranger who scanned one of their certificates.\n\nIt is not Sales → Leads (the people sales is working, by account and stage) and not People (who has access or was invited) either. All three key on the same email with no link between them, so Analytics → Website → Visitors shows the lead journeys and links each one to the same person in People and in Sales → Leads when they are there.",
    "seeAlso": [
      "lead-journey-retention",
      "website-consented-visitors",
      "became-client",
      "lead"
    ]
  },
  {
    "id": "lead-journey-retention",
    "term": "Lead-journey retention",
    "aliases": [
      "lead journey purge",
      "LEAD_JOURNEY_RETENTION_DAYS"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "Lead journeys are deleted 3 years after the lead's last contact, by a nightly purge. A lead who became a client is kept for as long as the account is.",
    "long": "Three years is the CNIL reference period for prospect data. It is a setting (`LEAD_JOURNEY_RETENTION_DAYS`, default 1095); a zero, negative or unreadable value falls back to the default rather than meaning \"purge everything\". Last contact is the latest thing the lead did: a visit, a form, a signup, a demo, a company.\n\nThe purge never deletes a lead who [became a client](term:became-client), one linked to a client company, or one who is a member of a client company, and the nightly refresh does not re-create a journey already past retention. It covers the [lead journey](term:lead-journey) only: the signups record for the same email is kept under its own rules.",
    "seeAlso": [
      "lead-journey",
      "became-client"
    ]
  },
  {
    "id": "website-analytics-snapshot",
    "term": "Website analytics snapshot",
    "aliases": [
      "daily snapshot",
      "nightly snapshot",
      "website snapshot"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "The nightly job that copies each day's website counts into vera-2, so the funnel's history outlives Vercel's and PostHog's own retention.",
    "long": "It runs at 02:30 UTC over the trailing seven days and fills only days that are missing or failed. It stores counts, never people: a daily total plus one row per country, referrer, page and device, and vera-2's own signup-to-client counts. Re-running a day replaces it rather than adding to it, and only one API instance can take a given day.\n\nVercel keeps 366 days on the current plan and PostHog one year, so any older day exists only here. Development fixture data is never stored. Vercel allows at most two breakdowns per query, so the record is a set of one-dimension slices, not a country × source × page cube. The same run refreshes each [lead journey](term:lead-journey).",
    "seeAlso": [
      "website-all-visitors",
      "lead-journey"
    ]
  },
  {
    "id": "website-funnel-step-rate",
    "term": "Funnel share (website)",
    "aliases": [
      "funnel step rate",
      "funnel step conversion",
      "step conversion",
      "funnel sankey"
    ],
    "kind": "formula",
    "audience": "internal",
    "formula": "People on a band ÷ people at the stage it leaves. A node's share is of everyone who visited.",
    "short": "The share of a funnel stage that moved on to the next one, in people. Hover a band for its share of the stage it leaves; under 10 people it is greyed as a small sample.",
    "long": "The funnel answers one question, whether the website is turning visitors into leads, as a Sankey of [visitor paths](term:website-visitor-paths): Visited, then [Engaged](term:website-funnel-engaged) (opened Get started, started the ROI calculator, or both), then Lead (submitted the form, calculated ROI, or both), then [Joined waitlist](term:website-waitlist-joined), with a Left band at each stage. Every person follows exactly one path, so what enters a stage leaves it, and the shares are of people, never of events.\n\nA later step implies the earlier one: someone who submitted the form with no \"opened\" event (analytics was turned on mid-form) is placed at Opened, and the card says how many were placed that way. Under 10 people a share is greyed as a small sample, never hidden. What happens after the waitlist (access granted, demos, companies, clients) is on the Sales pipeline, which the funnel links to.",
    "seeAlso": [
      "website-visitor-paths",
      "website-funnel-engaged",
      "website-waitlist-joined",
      "website-all-visits"
    ]
  },
  {
    "id": "website-funnel-engaged",
    "term": "Engaged (website funnel)",
    "aliases": [
      "engaged visitors",
      "funnel engaged"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "Visitors who opened the Get started form or started the ROI calculator: interest short of a lead. The funnel's second phase, before Lead.",
    "long": "Opening Get started and starting the ROI calculator are different ways out of a visit, and one person can do both, so the funnel shows three Engaged stages: the form, the calculator, and both. Each carries on into Lead (submitting the form, calculating ROI), so the gap between a stage and the Lead stages after it is where that path loses people. Counted per person from the site's own events, for [visitors with analytics on](term:website-consented-visitors) only, because only they have a path.",
    "seeAlso": [
      "website-funnel-step-rate",
      "website-visitor-paths",
      "website-form-submitted"
    ]
  },
  {
    "id": "website-all-visits",
    "term": "All visits (Vercel)",
    "aliases": [
      "vercel visits",
      "visits in total",
      "total visits"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "Every visit to vera.inc, counted without cookies, whatever the visitor chose about analytics. It can't follow a person between pages, so it gives totals, never paths.",
    "long": "Vercel identifies a visitor by a hash of the request that it discards after 24 hours, so someone who comes back on another day is, by Vercel's own description, counted again. That is why the funnel's coverage line calls this figure visits, not visitors, and why its percentage compares it with the daily visits of the [visitors with analytics on](term:website-visitor-paths) rather than with their number of people.\n\nBecause it needs no cookie it includes everyone, whatever their analytics choice, which makes it the only complete count. The price is that Vercel never sees a person: it cannot say which pages one visitor read, or whether the same visitor went on to submit a form. Its source figure is [all visitors](term:website-all-visitors).",
    "seeAlso": [
      "website-visitor-paths",
      "website-all-visitors"
    ]
  },
  {
    "id": "website-visitor-paths",
    "term": "Visitor paths (PostHog)",
    "aliases": [
      "visitors with analytics on, shown below",
      "funnel population",
      "analytics-on paths"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "Only people who allowed analytics (PostHog), but their actual paths are known: what each one opened, sent and joined. That is what the funnel chart shows.",
    "long": "The funnel chart can only draw people it can follow from visit to waitlist, and only [visitors with analytics on](term:website-consented-visitors) can be followed. So the chart is a subset of [all visits](term:website-all-visits), and the coverage line above it says how big a subset, as an approximate share.\n\nThe share is approximate on purpose. It compares like with like, these people's daily visits against Vercel's visits, but the two tools identify people differently (a cookie against a daily hash of the request), and an ad blocker can hide a visitor from one and not the other. It is left out for Today, because Vercel counts the whole UTC day and PostHog counts from your local midnight. Each person is counted once per stage of the [funnel](term:website-funnel-step-rate).",
    "seeAlso": [
      "website-all-visits",
      "website-consented-visitors",
      "website-funnel-step-rate"
    ]
  },
  {
    "id": "website-source-signup-rate",
    "term": "Signups / visitors",
    "aliases": [
      "source signup rate"
    ],
    "kind": "formula",
    "audience": "internal",
    "formula": "Signups attributed to this source ÷ this source's visitors (all visitors, Vercel).",
    "short": "Signups attributed to a source, over its visitors. A floor, not an estimate: only visitors with analytics on carry a first source to attribute.",
    "long": "The two halves come from different populations. Visitors are [everyone](term:website-all-visitors); signups are only those whose first referrer or landing page is known, which takes [analytics being on](term:website-consented-visitors). Every unattributable signup is missing from the top and none from the bottom, so the true rate is at least this. On the Page view, visitors count every page viewed, not only landing pages.",
    "seeAlso": [
      "website-all-visitors",
      "website-consented-visitors"
    ]
  },
  {
    "id": "website-form-submitted",
    "term": "Forms submitted (website)",
    "aliases": [
      "request access submitted",
      "website form submissions"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "Submissions of the vera.inc request-access form, counted by Vercel as a custom event for all visitors. Events, not people: one person submitting twice counts twice.",
    "long": "Counted cookieless, so it includes visitors whose analytics are off, and it is the same `request_access_submitted` event the funnel's Submitted the form step reads under [all visitors](term:website-all-visitors). Who submitted is only known for [visitors with analytics on](term:website-consented-visitors), and from vera-2's own signups once the form creates one.",
    "seeAlso": [
      "website-all-visitors",
      "website-funnel-step-rate"
    ]
  },
  {
    "id": "website-roi-submitted",
    "term": "ROI submitted",
    "aliases": [
      "ROI models sent",
      "ROI senders",
      "ROI submissions"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "ROI models sent from the vera.inc calculator in the range, from vera-2's own records (one per email). Not part of Leads, which counts Get started forms.",
    "long": "Counted from the signups record each submission creates or updates (the same record People sorts first as ROI senders), dated by when vera-2 received it. Every route counts, whatever the visitor's analytics choice. One per email: a later model from the same address replaces the earlier one, so someone who sends twice in a range counts once, and a model re-sent after the range moves out of it.\n\nLeads is a different count: Get started forms submitted, an event Vercel counts for all visitors. The ROI calculator sends its own event and its own record, so ROI submissions are not inside Leads, though one person may have done both. Compare [forms submitted](term:website-form-submitted) and [waitlist joined](term:website-waitlist-joined).",
    "seeAlso": [
      "website-form-submitted",
      "website-waitlist-joined"
    ]
  },
  {
    "id": "website-waitlist-joined",
    "term": "Joined waitlist",
    "aliases": [
      "waitlist joined",
      "waitlist",
      "sign-ups",
      "signups"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "Waitlist requests vera-2 recorded in the range, by any route. Excludes ROI-calculator-only senders and people staff added, who are also in signups.",
    "long": "Counted from vera-2's signups records created in the range that are a waitlist request: the Get started form, or an older website signup. Two kinds of signups record are left out because they are not someone joining the waitlist: a visitor who only sent an ROI model (counted as [ROI submitted](term:website-roi-submitted)), and a person staff added by hand. It is dated by when the record was created, which is the first submission from that email.\n\nIn the funnel chart it is the last stage, joined to each visitor by their email, or by the PostHog person their lead journey recorded, so only people with an analytics-on visit in the range can be drawn there. Joins from anyone else are counted in a note under the chart rather than as a band, which is why the chart's Joined waitlist can be smaller than this figure. What happens to someone after they join is on the Sales pipeline.",
    "seeAlso": [
      "website-roi-submitted",
      "website-funnel-step-rate",
      "website-visitor-paths"
    ]
  },
  {
    "id": "website-time-on-page",
    "term": "Time on page",
    "aliases": [
      "time on site",
      "seconds on page",
      "page duration"
    ],
    "kind": "formula",
    "audience": "internal",
    "formula": "Seconds between a pageview and the next pageview or page-leave in the same tab, as PostHog records it. A session's time is the sum of the pages that have one.",
    "short": "How long a visitor stayed on a page, from PostHog. Unknown (—) when nothing recorded them leaving it, which is common for the last page of a visit and for bounces.",
    "long": "PostHog's browser library measures it, not vera-2: when a visitor moves to another page, or leaves the site, the event it sends carries the previous page's duration. vera-2 matches that to the page by PostHog's own page-view id, so two tabs in one visit never borrow each other's time.\n\nIt is unknown, never zero, when nothing closed the page: the visitor closed the tab or the browser before the leave event was sent (phones often do this), or they are still on it (\"on it now\" in Live now). A one-page visit with no leave event, a bounce, is unknown for that reason. A session's Time adds up only the pages that have a time and is marked + when some do not, so it is a floor. Only [visitors with analytics on](term:website-consented-visitors) have pages to time.",
    "seeAlso": [
      "website-consented-visitors"
    ]
  },
  {
    "id": "website-visitor-tracked-link",
    "term": "Visit's tracked link",
    "aliases": [
      "tracked link column",
      "vera_invite",
      "came through a tracked link"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "The tracked link a visit came through, matched by the link's code that the website stores in the visitor's analytics. Only visitors with analytics on can be matched.",
    "long": "When someone opens a vera.inc/invite/... link, the website remembers the code and attaches it to every event PostHog records for that browser, so a visit carries the code it arrived with. vera-2 matches that code exactly to its own tracked links, archived ones included. A code that matches no link (made elsewhere, or mistyped) is shown as the raw code.\n\nIt is never guessed from the referrer, and click counts cannot be joined to visits: a click is counted without cookies for everyone and stores no visitor, which is also why a link can show more clicks than matched visits. Only [visitors with analytics on](term:website-consented-visitors) can carry the code at all.",
    "seeAlso": [
      "website-consented-visitors",
      "website-visitor-journey"
    ]
  },
  {
    "id": "website-visitor-journey",
    "term": "Visitor journey",
    "aliases": [
      "journey",
      "visitor path",
      "site-map journey"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "Every page one visitor viewed, in order and grouped by visit, with how long they stayed on each and where they came from. Visitors with analytics on only.",
    "long": "Opened from a row of Visitor history. Each visit shows where it came from and the tracked link it came through, then each page with its [time on page](term:website-time-on-page), then the page they left from. Visits are newest first, and the 20 most recent in the range are shown.\n\nPages viewed before a visitor accepted analytics are not recorded, and a visitor whose analytics are off has no journey at all, so a journey is a floor on what someone looked at, never the whole of it. See [visitors with analytics on](term:website-consented-visitors).",
    "seeAlso": [
      "website-time-on-page",
      "website-visitor-tracked-link",
      "website-consented-visitors"
    ]
  },
  {
    "id": "website-no-recording",
    "term": "No recording",
    "aliases": [
      "session recording",
      "replay"
    ],
    "kind": "status",
    "audience": "internal",
    "short": "PostHog holds no session recording for this visit. The visitor didn't allow session recording, or declined it.",
    "long": "vera.inc records sessions only with permission: outside the US after a visitor accepts, and in the US only once they choose to allow recording (analytics alone does not turn it on). Anyone who declined is not recorded. vera-2 checks PostHog's replay table for each page of visitor history at once and shows Replay only where a recording exists.\n\nRecordings are kept 3 months, so an older visit with no recording may simply have expired. When the check itself fails, Replay is still shown and marked [may be unavailable](term:website-replay-unconfirmed) rather than hidden.",
    "seeAlso": [
      "website-replay-unconfirmed",
      "website-consented-visitors"
    ]
  },
  {
    "id": "website-replay-unconfirmed",
    "term": "Replay may be unavailable",
    "aliases": [
      "may be unavailable"
    ],
    "kind": "status",
    "audience": "internal",
    "short": "Replay is shown but not confirmed: PostHog could not be checked, or the visit was minutes ago and its recording may still be processing.",
    "long": "When vera-2 cannot tell whether a recording exists it keeps the Replay link rather than hiding one that may be there. Two cases: the check against PostHog failed, or the visitor was active in the last 10 minutes, before PostHog has finished ingesting the recording. Opening it may show nothing. Compare [no recording](term:website-no-recording), where PostHog has answered.",
    "seeAlso": [
      "website-no-recording"
    ]
  },
  {
    "id": "website-live-map",
    "term": "Live map",
    "aliases": [
      "on the site now",
      "live visitors",
      "live now"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "Visitors active on vera.inc in the last 5 minutes, by city. Only visitors with analytics on (PostHog) can be placed; anyone else is on the site but not shown.",
    "long": "Read from PostHog's sessions with an event in the last 5 minutes, placed by PostHog's GeoIP city, one pin per city. It refreshes every 30 seconds while the page is open and pauses while the tab is hidden. Because it can only see [visitors with analytics on](term:website-consented-visitors), \"No one on the site right now\" means no one with analytics on: a visitor who declined, or opted out in the US, can be browsing and not appear. The count beside it is the same figure, and a failed read shows \"unavailable\", never 0. The range figures below it do not depend on it.",
    "seeAlso": [
      "website-consented-visitors",
      "website-all-visitors"
    ]
  },
  {
    "id": "website-page-analysis",
    "term": "Pages (website)",
    "aliases": [
      "page analysis",
      "top pages",
      "most visited pages"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "vera.inc's pages ranked by page views in the range. Visitors and page views count everyone; time, entries and exit rate count only visitors with analytics on.",
    "long": "The table puts two populations side by side and never mixes them in one figure. Visitors and [page views](term:website-page-views) come from [all visitors](term:website-all-visitors), counted without cookies. Avg time, [entries](term:website-page-entries) and [exit rate](term:website-exit-rate) need each visit's pages in order, which only [visitors with analytics on](term:website-consented-visitors) carry, so they are a sample: compare them between pages, not against the Vercel columns.\n\nPublished pages (the vera.inc sitemap) are listed by name. Everything else, such as staged pages, old paths, invite links and Vercel's tail beyond its top 100 paths, folds into one Other paths row you can expand. Paths are tidied first: lower case, no query string or trailing slash, and an invite link's code replaced by `:code`, so one prospect's link never shows as its own row.",
    "seeAlso": [
      "website-page-views",
      "website-page-entries",
      "website-exit-rate",
      "website-time-on-page"
    ]
  },
  {
    "id": "website-page-views",
    "term": "Page views",
    "aliases": [
      "pageviews",
      "page view count"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "Times a page was loaded in the range, by everyone (Vercel). One visitor reading a page three times is three page views but one visitor.",
    "long": "Counted cookieless by Vercel, so it includes visitors whose analytics are off, and it is what the Pages table sorts by. Page views add up across pages and days; visitors do not, because one person can be on several pages or come back on several days. That is why the Other paths row and long ranges sum page views but not visitors. In the Pages table the exit rate uses PostHog's own page views of the same page, never these, so its two halves come from one population.",
    "seeAlso": [
      "website-all-visitors",
      "website-page-analysis"
    ]
  },
  {
    "id": "website-page-entries",
    "term": "Entries",
    "aliases": [
      "landing count",
      "landings",
      "entry page"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "Visits that started on this page: how often it was someone's first page on vera.inc. From visitors with analytics on only (PostHog), so a sample.",
    "long": "A page with many entries is a front door: search, a shared link or an ad lands people there. Compare it with [page views](term:website-page-views) to tell a page people arrive on from one they reach by clicking around. Counted from PostHog's record of each visit's first page, grouped in PostHog so no individual visit leaves it. Only [visitors with analytics on](term:website-consented-visitors) are counted, so read entries as a share between pages rather than a total.",
    "seeAlso": [
      "website-exit-rate",
      "website-site-flow",
      "website-consented-visitors"
    ]
  },
  {
    "id": "website-exit-rate",
    "term": "Exit rate",
    "aliases": [
      "exits",
      "exit percentage"
    ],
    "kind": "formula",
    "audience": "internal",
    "formula": "Visits whose last page was this one ÷ this page's views, both from visitors with analytics on (PostHog). A dash when the page had no such views.",
    "short": "The share of this page's views that were the last page of a visit. From visitors with analytics on only (PostHog), so a sample.",
    "long": "A high exit rate is not automatically bad: a contact page or the end of the ROI calculator is a natural last page. It matters on pages meant to lead somewhere, like Tags or Pricing. Both halves come from [visitors with analytics on](term:website-consented-visitors): dividing PostHog's exits by Vercel's page views would mix two populations and understate the rate. A bounce, a one-page visit, counts as an entry and an exit of the same page. The exit count itself shows when you hover the rate.",
    "seeAlso": [
      "website-page-entries",
      "website-site-flow"
    ]
  },
  {
    "id": "website-site-flow",
    "term": "Site flow (Sankey)",
    "aliases": [
      "flow chart",
      "sankey",
      "user flow",
      "paths through the site"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "The most common paths through vera.inc: entry page, the next two pages, then where the visit ended. Visitors with analytics on only, so a sample.",
    "long": "Read left to right. Each band is a group of visits moving from one page to the next, and its thickness is how many. Each column keeps its six busiest pages and folds the rest into Other pages. The outcomes sit on the right: Left after 1, 2 or 3 pages, or Viewed more than 3 pages. A reload of the same page is not a step.\n\nIt needs each visit's pages in order, which only [visitors with analytics on](term:website-consented-visitors) carry: outside the US after they accept, in the US by default unless they opt out or send Global Privacy Control. PostHog groups the visits by path before vera-2 sees them, so the chart carries pages and counts, never a visit or a person, and invite-link codes appear as `:code`.",
    "seeAlso": [
      "website-page-entries",
      "website-exit-rate",
      "website-consented-visitors"
    ]
  },
  {
    "id": "lead-in-posthog",
    "term": "In PostHog",
    "aliases": [],
    "kind": "status",
    "audience": "internal",
    "short": "This lead has a PostHog person: analytics were on for them at vera.inc (accepted, or the US default) and they submitted a form, so their sessions are linked to their email.",
    "long": "It is the precondition for [Tracked](term:became-client-tracked): the became_client event is only ever sent to a person who already exists. A dash means no person, usually because analytics were never on for the lead, and that is their choice rather than a gap to close.",
    "seeAlso": [
      "became-client-tracked",
      "website-consented-visitors"
    ]
  },
  {
    "id": "seo-keyword-difficulty",
    "term": "Keyword difficulty (KD)",
    "aliases": [
      "kd",
      "keyword difficulty"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "SE Ranking's 0–100 estimate of how hard it is to reach Google's top 10 for a keyword, from the strength of the pages already there. Higher is harder.",
    "long": "A vendor model, not a measurement: SE Ranking scores the backlink strength of the pages currently on page one, so two tools rarely agree on the number and it moves when those pages change. Read it as a rough ordering within one tool. It feeds the [gap score](term:seo-gap-score), which discounts volume by difficulty to rank which competitor keywords are worth chasing.",
    "seeAlso": [
      "seo-gap-score"
    ]
  },
  {
    "id": "seo-own-it",
    "term": "Own it (keyword)",
    "aliases": [
      "own-it",
      "owned keyword"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "A keyword Vera has decided to win outright. It is rank-tracked in every market it belongs to, whatever its priority.",
    "long": "A decision, not a metric. Priority (P1, P2…) comes from the keyword research and says how valuable a term looks; own-it says the team has committed to it. Marking a keyword own-it adds it to the rankings tracked set in each of its markets, including competitors-only ones, and every check spends SE Ranking credits — so it is a platform-admin switch."
  },
  {
    "id": "gsc-clicks",
    "term": "Clicks (Search Console)",
    "aliases": [
      "gsc clicks",
      "search clicks"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "Clicks from a Google Search result through to vera.inc in the range. Source: Google Search Console, synced nightly, 2–3 day lag.",
    "long": "Counted by Google, not by the website, so it includes visitors who decline analytics and never matches Vercel's referrer count exactly. Each click on a result counts once, whichever vera.inc page it lands on. Like every Search Console figure it reaches vera-2 through the nightly sync, and Google publishes each day [2–3 days late](term:gsc-data-lag).",
    "seeAlso": [
      "gsc-impressions",
      "gsc-ctr",
      "gsc-data-lag"
    ]
  },
  {
    "id": "gsc-impressions",
    "term": "Impressions (Search Console)",
    "aliases": [
      "gsc impressions",
      "search impressions"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "Times a vera.inc link appeared on a Google results page someone loaded, even if they never scrolled to it or clicked. Source: Google Search Console, synced nightly, 2–3 day lag.",
    "long": "One results page showing two vera.inc links counts once per link. Impressions are the denominator of [CTR](term:gsc-ctr) and the weight behind [average position](term:gsc-average-position). Google withholds rare queries, so query rows never add up to the page totals; see [anonymised queries](term:gsc-anonymised-queries).",
    "seeAlso": [
      "gsc-clicks",
      "gsc-ctr",
      "gsc-anonymised-queries",
      "gsc-data-lag"
    ]
  },
  {
    "id": "gsc-ctr",
    "term": "CTR (Search Console)",
    "aliases": [
      "gsc ctr",
      "search ctr",
      "click-through rate"
    ],
    "kind": "formula",
    "audience": "internal",
    "formula": "Clicks ÷ impressions over the same range.",
    "short": "Click-through rate: the share of vera.inc's Google Search impressions that became a click (clicks ÷ impressions). Source: Google Search Console, synced nightly, 2–3 day lag.",
    "long": "Low on a young site by construction: most impressions are for positions beyond the first page, where almost nobody clicks. Compare CTR between queries at a similar [average position](term:gsc-average-position), not across the whole table.",
    "seeAlso": [
      "gsc-clicks",
      "gsc-impressions",
      "gsc-average-position"
    ]
  },
  {
    "id": "gsc-average-position",
    "term": "Average position (Search Console)",
    "aliases": [
      "gsc position",
      "average position"
    ],
    "kind": "formula",
    "audience": "internal",
    "formula": "Impression-weighted mean of Google's daily average top position for vera.inc: Σ(position × impressions) ÷ Σ impressions.",
    "short": "Where vera.inc's highest link sat in Google results, averaged over every impression: 1 is the top, so lower is better. Source: Google Search Console, synced nightly, 2–3 day lag.",
    "long": "Google reports a daily average of the topmost vera.inc result per query or page; vera-2 weights those days by impressions so a busy day counts for more. It only covers searches where vera.inc appeared at all, so a rising number can mean new, deeper impressions rather than a lost ranking. A live rank check is a single sample from one city; this is Google's own average.",
    "seeAlso": [
      "gsc-impressions",
      "gsc-ctr",
      "seo-rank-gap"
    ]
  },
  {
    "id": "gsc-data-lag",
    "term": "Search Console lag",
    "aliases": [
      "gsc lag",
      "search console sync"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "Google publishes Search Console data 2–3 days late and vera-2 syncs it nightly, so the last days of any range read low until Google fills them in.",
    "long": "The figures come from vera-2's own copy (`gsc_daily`), not a live call to Google, and the nightly sync re-reads recent days so late data replaces the partial figure rather than adding to it. Compare a period against the previous one only once both have settled.",
    "seeAlso": [
      "gsc-clicks",
      "gsc-impressions"
    ]
  },
  {
    "id": "gsc-anonymised-queries",
    "term": "Anonymised queries (Search Console)",
    "aliases": [
      "anonymised impressions",
      "hidden queries"
    ],
    "kind": "formula",
    "audience": "internal",
    "formula": "1 − (impressions on query rows ÷ impressions on page totals), over the same range.",
    "short": "The share of vera.inc's search impressions Google does not attribute to any query, because the query was rare enough to identify someone. Source: Google Search Console.",
    "long": "Search Console withholds rare queries for privacy but still counts their impressions in each page's total. So the query table never adds up to the page table, and the gap is reported rather than hidden: a query list covering 90% of impressions is a sample of what people searched, not all of it. The share is larger on a young site, where most queries are rare."
  },
  {
    "id": "seo-rank-gap",
    "term": "Rank gap",
    "aliases": [
      "ranking gap",
      "gap"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "A tracked keyword where at least one tracked competitor is in Google's top 10 and vera.inc is not, in the latest check for that market.",
    "long": "The rankings matrix's to-do list: someone is winning attention Vera wants, on a term already judged worth tracking. It is computed from one live check per keyword per cycle, from one city's results, so a single gap can be noise; one that persists across cycles is not. Distinct from the gap finder, which looks for keywords Vera does not track at all."
  },
  {
    "id": "seo-gap-score",
    "term": "Gap score",
    "aliases": [
      "gap finder score",
      "winnable demand"
    ],
    "kind": "formula",
    "audience": "internal",
    "formula": "Monthly search volume × (100 − keyword difficulty) ÷ 100.",
    "short": "Search volume discounted by keyword difficulty: a rough measure of winnable demand for a keyword a competitor ranks for and Vera does not track.",
    "long": "A sorting aid for the gap finder, not a forecast. It ignores intent and buyer fit, which decide whether the traffic would ever become a lead, so a high score is a candidate to judge, not a keyword to adopt. It uses [keyword difficulty](term:seo-keyword-difficulty) as SE Ranking reports it.",
    "seeAlso": [
      "seo-keyword-difficulty"
    ]
  },
  {
    "id": "aeo-citation",
    "term": "Cited / mentioned (AI answers)",
    "aliases": [
      "aeo citation",
      "ai citation",
      "cited",
      "mentioned"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "Cited: the AI answer links vera.inc as a source. Mentioned: it names Vera in the text without linking. An answer can do both, either or neither.",
    "long": "Measured by Vera's own prompt runner, which sends a fixed set of buyer questions to each enabled engine with web search on and records the sources and text that come back. Citation is the stronger signal, because it sends the reader somewhere. One run is one sample: answers vary between runs, so the rate over several runs means more than any single answer."
  },
  {
    "id": "seo-tracking-rule",
    "term": "Tracking rule (rank checks)",
    "aliases": [
      "tracking rule",
      "tracked by rule"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "Which keywords get a live rank check: approved, buyer fit at least 0.6 and at least 30 monthly searches, or marked own-it, or in the market's own set.",
    "long": "Each check spends SE Ranking credits, so the rule decides the bill. Searches are US plus UK combined for English markets and the local market's volume elsewhere. A keyword with no measured fit or volume is not tracked by the rule, because unknown is not enough. [Own-it](term:seo-own-it) overrides the rule, and the FR/IT term sets are tracked because they are listed for their market. Both thresholds are settings; the Rankings and Keywords tabs show the estimated monthly credits for the rule in force.",
    "seeAlso": [
      "seo-own-it"
    ]
  },
  {
    "id": "seo-estimated-clicks",
    "term": "Estimated clicks (CTR curve)",
    "aliases": [
      "estimated clicks",
      "ctr curve",
      "clicks if ranked"
    ],
    "kind": "formula",
    "audience": "internal",
    "formula": "Monthly searches × click-through rate at a position: #1 28%, #2 15%, #3 10%, #4 7%, #5 5%, #6–10 2–3%, beyond 10 ≈ 0.",
    "short": "An estimate of monthly visits a keyword would send at a given Google position, from an industry CTR curve. A guide for sizing, not a forecast.",
    "long": "The curve comes from public industry click-through studies and is the same for every keyword, so it ignores the real page: an AI Overview, ads or a shopping carousel above the results all push clicks down, and branded queries behave differently. Use it to compare keywords and to size what ranking would be worth, never as a promise. 'Now' uses Vera's newest checked position, or the Search Console average when there is no check."
  },
  {
    "id": "seo-equivalent-ad-value",
    "term": "Equivalent ad value",
    "aliases": [
      "ad value",
      "traffic value"
    ],
    "kind": "formula",
    "audience": "internal",
    "formula": "Estimated clicks × CPC.",
    "short": "What the estimated organic clicks would cost if bought as Google ads at the keyword's CPC. An estimate of value; Vera pays nothing per organic click.",
    "long": "A way to put organic rankings and paid search on one scale. It inherits every weakness of the [estimated clicks](term:seo-estimated-clicks) it multiplies, and CPC is an auction price that moves with competition, so read it as an order of magnitude.",
    "seeAlso": [
      "seo-estimated-clicks",
      "seo-cpc"
    ]
  },
  {
    "id": "seo-cpc",
    "term": "CPC (cost per click)",
    "aliases": [
      "cpc",
      "cost per click"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "What advertisers pay Google per ad click — a signal of commercial value; Vera does not pay it for organic ranking.",
    "long": "Reported by SE Ranking from Google Ads data for the market. A high CPC means advertisers expect clicks on the keyword to turn into revenue, which makes it a useful proxy for buying intent even when Vera never runs an ad on it."
  },
  {
    "id": "refer-a-brand",
    "term": "Refer & earn",
    "kind": "term",
    "audience": "brand",
    "feature": "referrals",
    "aliases": [
      "Refer a brand",
      "refer and earn",
      "referral",
      "referrals",
      "referral programme",
      "referral link",
      "refer and earn",
      "refer & earn"
    ],
    "short": "Introduce another brand to Vera. When they join and pay, your company receives activations, and they get a discount on their base plan.",
    "long": "Every member of your company has their own referral link, and can invite a brand by email from the Refer & earn row in the sidebar. Nothing is credited when someone signs up: [referral credits](term:referral-credits) arrive only when the brand you referred pays Vera. The brand you refer gets 20% off their base plan for their first three months, or 5% off their first annual invoice. A brand that is already a Vera customer, or is already on the waitlist, does not count.",
    "seeAlso": [
      "referral-credits",
      "referral-status-paying"
    ],
    "docs": [
      "personal-settings/referrals"
    ]
  },
  {
    "id": "referral-credits",
    "term": "Referral credits",
    "kind": "formula",
    "audience": "brand",
    "feature": "referrals",
    "aliases": [
      "referral activations",
      "credits from referrals"
    ],
    "short": "Activations your company receives when a brand you referred pays Vera: worth 20% of what they pay in their first 12 months, at least 10 and up to 1,000 per brand.",
    "long": "They are credited each time the referred brand pays an invoice, starting with the first one that takes money, and stop after 12 months or at 1,000 per referral, whichever comes first. The first payment always brings at least 10. They join your company's activation balance and [roll over](term:rollover) like every other credit; they do not expire. If a payment is refunded, the credits it brought in are taken back, but never below a balance of zero. They are activations, not money, and are never paid out.",
    "seeAlso": [
      "refer-a-brand",
      "rollover",
      "activation"
    ],
    "docs": [
      "personal-settings/referrals"
    ],
    "formula": "Per paid invoice in the first 12 months: 20% of what the referred brand paid (after discounts and tax), at one activation per $125, with any remainder carried to the next invoice. The first paid invoice brings at least 10. Stops at 1,000."
  },
  {
    "id": "referral-status-sent",
    "term": "Sent",
    "kind": "status",
    "audience": "brand",
    "feature": "referrals",
    "short": "A referral that has been sent or shared but has not turned into a signup yet.",
    "long": "An invitation shows as Sent from the moment it is handled. That includes an address that will never become a referral, for example a brand that is already on Vera: Vera does not say who its customers are, so those read as Sent too.",
    "seeAlso": [
      "referral-status-signed-up",
      "refer-a-brand"
    ],
    "docs": [
      "personal-settings/referrals"
    ],
    "aliases": []
  },
  {
    "id": "referral-status-signed-up",
    "term": "Signed up",
    "kind": "status",
    "audience": "brand",
    "feature": "referrals",
    "short": "The brand you referred has asked to join Vera and is waiting to be approved.",
    "long": "Referred brands are not let in automatically. They wait with everyone else and are reviewed by Vera, though referred brands are reviewed first.",
    "seeAlso": [
      "referral-status-approved"
    ],
    "docs": [
      "personal-settings/referrals"
    ],
    "aliases": []
  },
  {
    "id": "referral-status-approved",
    "term": "Approved",
    "kind": "status",
    "audience": "brand",
    "feature": "referrals",
    "short": "Vera has approved the brand you referred, and they can now create their account.",
    "long": "The next step is theirs: once they set up their company on Vera the referral shows as [Joined](term:referral-status-joined).",
    "seeAlso": [
      "referral-status-joined"
    ],
    "docs": [
      "personal-settings/referrals"
    ],
    "aliases": []
  },
  {
    "id": "referral-status-joined",
    "term": "Joined",
    "kind": "status",
    "audience": "brand",
    "feature": "referrals",
    "short": "The brand you referred has set up its company on Vera but has not paid yet.",
    "long": "Nothing is credited until they pay. When their first invoice is paid the referral becomes [Paying](term:referral-status-paying).",
    "seeAlso": [
      "referral-status-paying",
      "referral-credits"
    ],
    "docs": [
      "personal-settings/referrals"
    ],
    "aliases": []
  },
  {
    "id": "referral-status-paying",
    "term": "Paying",
    "kind": "status",
    "audience": "brand",
    "feature": "referrals",
    "short": "The brand you referred is paying Vera, and each payment adds referral credits to your company.",
    "long": "Credits keep arriving with each paid invoice for 12 months from their first payment, or until the referral reaches 1,000.",
    "seeAlso": [
      "referral-credits",
      "referral-status-limit-reached",
      "referral-status-ended"
    ],
    "docs": [
      "personal-settings/referrals"
    ],
    "aliases": []
  },
  {
    "id": "referral-status-limit-reached",
    "term": "Limit reached",
    "kind": "status",
    "audience": "brand",
    "feature": "referrals",
    "short": "This referral has brought in the most it can: 1,000 activations.",
    "long": "The brand stays on Vera; your company simply receives no more credits from this referral.",
    "seeAlso": [
      "referral-credits"
    ],
    "docs": [
      "personal-settings/referrals"
    ],
    "aliases": []
  },
  {
    "id": "referral-status-ended",
    "term": "Ended",
    "kind": "status",
    "audience": "brand",
    "feature": "referrals",
    "short": "This referral's 12 months of credits have finished.",
    "long": "The 12 months start with the referred brand's first payment. The credits it brought in stay in your company's balance.",
    "seeAlso": [
      "referral-credits"
    ],
    "docs": [
      "personal-settings/referrals"
    ],
    "aliases": []
  },
  {
    "id": "referral-status-not-eligible",
    "term": "Not eligible",
    "kind": "status",
    "audience": "brand",
    "feature": "referrals",
    "short": "This referral does not count, because the address belongs to your own company.",
    "long": "A company cannot refer itself, so an invitation to someone at your own company, or on your company's email domain, is recorded but never earns credits.",
    "seeAlso": [
      "refer-a-brand"
    ],
    "docs": [
      "personal-settings/referrals"
    ],
    "aliases": []
  },
  {
    "id": "seo-recommendation",
    "term": "SEO recommendation",
    "aliases": [
      "recommendation",
      "seo recommendations"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "A rule-based suggestion on the SEO & AEO page, with its evidence and a suggested action, recomputed after every scheduled job. A platform admin approves, rejects or snoozes it.",
    "long": "Each rule reads Vera's own tables (Search Console, rank checks, keyword metrics, competitor rankings, AI answers) and never spends a credit. An item is open until someone decides. Approved becomes a [tracked action](term:seo-action); rejected stays rejected however often the rule fires again; snoozed comes back when its date passes if the rule still applies. An open item whose rule stops firing is resolved by the data. Thresholds are settings on the API (SEO_REC_*).",
    "seeAlso": [
      "seo-action",
      "seo-recommendation-score",
      "seo-review"
    ]
  },
  {
    "id": "seo-recommendation-score",
    "term": "Recommendation score",
    "aliases": [
      "clicks at stake",
      "recommendation order"
    ],
    "kind": "formula",
    "audience": "internal",
    "formula": "Estimated monthly clicks at stake from the CTR curve (for example impressions × CTR at #3 − clicks for a title rewrite), or a fixed weight for rules with no click estimate.",
    "short": "What orders the recommendations and picks the review's top five: an estimate of monthly clicks at stake, or a fixed weight where there is no click estimate. Not a forecast.",
    "long": "Rising terms are weighted by buyer fit, and a series with a one-month step jump counts a quarter, because a jump is usually a data or results-page change. AI answer changes, the review queue and the quarterly reset carry fixed weights. The score only orders the list; it is never shown as a promise of traffic. See [estimated clicks](term:seo-estimated-clicks).",
    "seeAlso": [
      "seo-recommendation",
      "seo-estimated-clicks"
    ]
  },
  {
    "id": "seo-action",
    "term": "Tracked SEO action",
    "aliases": [
      "tracked action",
      "seo action"
    ],
    "kind": "status",
    "audience": "internal",
    "short": "An approved recommendation followed to a result: approved, in progress, shipped, measured. Measured stores the same metric again beside its value at approval.",
    "long": "An action moves one step at a time, forward or back one step. Marking it measured reads the metric the recommendation was about: a page's Search Console impressions, clicks, CTR and position since it shipped, or a keyword's latest Vera position. That reading sits beside the baseline captured at approval, so whether a change worked is a comparison of two stored figures.",
    "seeAlso": [
      "seo-recommendation"
    ]
  },
  {
    "id": "seo-review",
    "term": "Biweekly SEO review",
    "aliases": [
      "seo review",
      "review email",
      "review history"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "Built after each completed US and UK rankings cycle (every other Thursday): what moved, top recommendations, wins and losses, and spend. Emailed and kept in Review history.",
    "long": "The review waits until both cycles have collected or expired every check and the slot's AI-answer run has finished, then recomputes the recommendations, stores the review and emails SEO_REVIEW_RECIPIENTS (and Slack when a webhook is set). A short written summary is added by Gemini on Vertex when it is configured; it is capped by the monthly LLM cap and the figures, not the summary, are the record. One review per cycle pair; a platform admin can also build one by hand.",
    "seeAlso": [
      "seo-recommendation",
      "seo-schedule-slot"
    ]
  },
  {
    "id": "seo-schedule-slot",
    "term": "SEO schedule slot",
    "aliases": [
      "seo slot",
      "ranking slot",
      "thursday slot"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "The fixed time rankings in every market and the AI-answer runner start: every other Thursday at 2:00 pm New York time, counted from Thursday 22 October 2026.",
    "long": "A slot is on the calendar, not a gap after the last run, so a late, failed or manual run never moves the next one. If the API is down at 2:00 pm the slot runs as soon as it is back that same New York day; a day missed entirely is skipped, never caught up. Each slot runs once per market however many API instances are up. Run now claims a separate manual period and leaves the schedule alone. The weekday, time, time zone, first date and interval are settings.",
    "seeAlso": [
      "seo-review"
    ]
  },
  {
    "id": "seo-rec-title-rewrite",
    "term": "Title rewrite (recommendation)",
    "aliases": [
      "title rewrite"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "A Vera page with enough Search Console impressions whose CTR is well below what its position should earn, or whose average position is 4 to 15.",
    "seeAlso": [
      "seo-recommendation",
      "gsc-ctr"
    ]
  },
  {
    "id": "seo-rec-losing",
    "term": "Losing ground (recommendation)",
    "aliases": [
      "losing ground",
      "pages losing"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "Vera dropped more than five places on a tracked term between two checks, fell out of the results, or an approved competitor moved above her. Also a page slipping in Search Console.",
    "seeAlso": [
      "seo-recommendation"
    ]
  },
  {
    "id": "seo-rec-rising-no-page",
    "term": "Rising term, no page (recommendation)",
    "aliases": [
      "rising term"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "An approved keyword whose searches rose at least 50% over 12 months and that no Vera page targets yet.",
    "seeAlso": [
      "seo-recommendation"
    ]
  },
  {
    "id": "seo-rec-gap-term",
    "term": "Competitor gap (recommendation)",
    "aliases": [
      "gap term"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "A term an approved competitor ranks for in the top 20 that is not in Vera's keyword master, ranked by gap score. Branded terms are left out.",
    "seeAlso": [
      "seo-recommendation",
      "seo-gap-score"
    ]
  },
  {
    "id": "seo-rec-cannibalisation",
    "term": "Cannibalisation (recommendation)",
    "aliases": [
      "cannibalisation",
      "keyword cannibalisation"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "Two Vera pages competing for one query in Search Console, or Google swapping which Vera page ranks for a tracked term between two checks.",
    "seeAlso": [
      "seo-recommendation"
    ]
  },
  {
    "id": "seo-rec-aeo-change",
    "term": "AI answer change (recommendation)",
    "aliases": [
      "aeo change"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "An AI answer now cites a competitor it did not cite in the previous run for the same prompt and engine, while Vera is not cited.",
    "seeAlso": [
      "seo-recommendation",
      "aeo-citation"
    ]
  },
  {
    "id": "seo-rec-review-queue",
    "term": "Awaiting review (recommendation)",
    "aliases": [
      "review queue"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "Proposed keywords or competitors waiting for a person to approve or reject them. Only approved ones are measured, ranked and mined.",
    "seeAlso": [
      "seo-recommendation"
    ]
  },
  {
    "id": "seo-rec-strategy-reset",
    "term": "Quarterly strategy reset (recommendation)",
    "aliases": [
      "strategy reset"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "Once a quarter: re-run keyword expansion, review the tracking rule and markets, refresh the French and Italian term sets, and revisit the competitor set.",
    "seeAlso": [
      "seo-recommendation",
      "seo-tracking-rule"
    ]
  },
  {
    "id": "seo-page-title",
    "term": "Page title (search)",
    "aliases": [
      "title tag",
      "<title>",
      "seo title"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "A page's <title>: the headline of its Google result. The website's build refuses one over 60 characters, and the SEO page flags the same.",
    "long": "Read live from the published page, never from a stored copy, and counted after HTML entities are decoded, so it is the same length the website's own guard in `content/meta.ts` measures. The 60 includes the \" — Vera\" suffix. A longer title is not an error to Google, which simply cuts it off; the budget exists so the whole headline shows. Edited in vera-website `content/meta.ts`, nowhere in vera-2.",
    "seeAlso": [
      "seo-meta-description",
      "seo-link-preview"
    ]
  },
  {
    "id": "seo-meta-description",
    "term": "Meta description",
    "aliases": [
      "description",
      "meta description",
      "search snippet"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "A page's meta description: the text under its Google result. The website's build requires 70 to 160 characters, the band a snippet shows in full.",
    "long": "Google may write its own snippet instead, so this is the text Vera offers, not a guarantee of what appears. Under 70 reads as a fragment; over 160 is cut mid-sentence. Edited in vera-website `content/meta.ts`.",
    "seeAlso": [
      "seo-page-title"
    ]
  },
  {
    "id": "seo-link-preview",
    "term": "Link title and description (og)",
    "aliases": [
      "og:title",
      "og:description",
      "open graph",
      "share copy"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "og:title and og:description: the headline and text a shared vera.inc link shows in WhatsApp, iMessage, Slack or LinkedIn. Not the search title.",
    "long": "Deliberately different from the search copy: a share is read by a person beside a card that already names the site, so the headline is the page's own H1 and the description its lead. Edited in vera-website `content/share.ts`.",
    "seeAlso": [
      "seo-link-preview-image",
      "seo-page-title"
    ]
  },
  {
    "id": "seo-link-preview-image",
    "term": "Link preview image (og:image)",
    "aliases": [
      "og:image",
      "share card",
      "preview image"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "The picture a shared link shows, cropped to 1.91:1. Above about 300 KB WhatsApp shows the link with no image, so the SEO page flags anything heavier.",
    "long": "300 KB is the practical WhatsApp limit, not a published one. The size comes from the image's Content-Length, or from counting its bytes when the server does not send one. vera.inc draws these cards on demand (`lib/og.tsx`), so the first request after a deploy can be slow.",
    "seeAlso": [
      "seo-link-preview"
    ]
  },
  {
    "id": "affiliates-referrals-sent",
    "term": "Referrals sent",
    "aliases": [
      "referrals sent"
    ],
    "kind": "formula",
    "audience": "internal",
    "tags": [
      "metric"
    ],
    "formula": "Brand referrals created in the range: invitation emails that were delivered, plus signups through a referral link. Ineligible and superseded referrals are excluded.",
    "short": "Brand referrals created in the range that reached a prospect: delivered invitation emails plus signups through a referral link. Ineligible and superseded ones are not counted.",
    "long": "A shared link leaves no record until someone signs up through it, so a link referral enters the count at its signup. An invitation enters when the email is delivered; one held back (its region's sending is switched off, or the send failed) is left out until the prospect progresses some other way. Insider prospects are never counted here: they belong to [Vera Insiders](term:vera-insiders).",
    "seeAlso": [
      "refer-a-brand",
      "affiliates-funnel-stage"
    ]
  },
  {
    "id": "affiliates-funnel-stage",
    "term": "Referral funnel stage",
    "aliases": [
      "affiliates funnel",
      "referral stage count"
    ],
    "kind": "formula",
    "audience": "internal",
    "formula": "For referrals created in the range: the number that have reached this stage OR any later one, as of now.",
    "short": "How many referrals created in the range have reached this stage or a later one by now. A paying referral counts at signed up, granted and became a client too.",
    "long": "The funnel is a cohort: it follows referrals created in the selected range to wherever they are today, so its figures reconcile with People → Referrals for the same range. A referral can skip a stage (an allowlisted link signup goes straight from signed up to company created), which is why each stage counts \"this or later\" rather than the current status.\n\nBecame a client uses the referred company's own [became a client](term:became-client) date. Capped or ended means the referral hit the 1,000-activation limit or finished its 12-month window.",
    "seeAlso": [
      "affiliates-step-rate",
      "affiliates-referrals-sent",
      "became-client"
    ]
  },
  {
    "id": "affiliates-step-rate",
    "term": "Referral step rate",
    "aliases": [
      "referral step conversion"
    ],
    "kind": "formula",
    "audience": "internal",
    "formula": "Count at this stage ÷ count at the stage before it. A dash when the stage before is zero.",
    "short": "The share of the stage before that reached this one. A dash when the stage before is zero, because 0 of 0 is not a rate.",
    "seeAlso": [
      "affiliates-funnel-stage"
    ]
  },
  {
    "id": "affiliates-sent-to-paying",
    "term": "Sent to paying",
    "aliases": [
      "referral conversion rate"
    ],
    "kind": "formula",
    "audience": "internal",
    "tags": [
      "metric"
    ],
    "formula": "Referrals that reached paying ÷ referrals sent, both for referrals created in the range.",
    "short": "The share of referrals sent in the range that have become paying brands. Recent referrals have had less time to pay, so a short range reads low.",
    "seeAlso": [
      "affiliates-referrals-sent",
      "affiliates-funnel-stage"
    ]
  },
  {
    "id": "referral-credits-granted",
    "term": "Referral credits granted",
    "kind": "formula",
    "audience": "internal",
    "tags": [
      "metric"
    ],
    "formula": "Activations granted to referring companies for referrals created in the range, including the 10-activation floor and credits restored after a reversal.",
    "short": "Activations granted to referring companies by the referrals created in the range: 20% of what each referred brand paid, at $25 an activation, floor and cap applied.",
    "long": "Counted from the per-invoice reward records, so it reconciles with the activation ledger. Each referral earns at least 10 activations on its first paid invoice and at most 1,000 in total, over 12 months from that invoice. See [referral credits](term:referral-credits) for the rule as brands see it.",
    "seeAlso": [
      "referral-credits",
      "referral-credits-reversed",
      "referral-credits-outstanding"
    ]
  },
  {
    "id": "referral-credits-reversed",
    "term": "Referral credits reversed",
    "kind": "formula",
    "audience": "internal",
    "tags": [
      "metric"
    ],
    "formula": "Activations taken back from referring companies, for referrals created in the range: refund and credit-note clawbacks plus reversals made by staff.",
    "short": "Activations taken back because the payment that earned them was refunded or credited, or because staff reversed them by hand.",
    "seeAlso": [
      "referral-credits-forgiven",
      "referral-credits-granted"
    ]
  },
  {
    "id": "referral-credits-forgiven",
    "term": "Referral credits forgiven",
    "kind": "formula",
    "audience": "internal",
    "tags": [
      "metric"
    ],
    "formula": "Activations a reversal should have taken back but could not, because the company's balance would have gone below zero.",
    "short": "Activations a reversal could not take back because the company had already used them. A reversal never takes a balance below zero.",
    "seeAlso": [
      "referral-credits-reversed"
    ]
  },
  {
    "id": "referral-credits-outstanding",
    "term": "Outstanding referral credits",
    "aliases": [
      "referral credit exposure",
      "credit exposure"
    ],
    "kind": "formula",
    "audience": "internal",
    "tags": [
      "metric"
    ],
    "formula": "Summed over every referring company: its current activation balance, but never more than the referral credits it has net-earned (each floored at 0).",
    "short": "The most referral activations still unspent, now. Each referring company counts its balance, capped at the referral credits it has earned. Not limited to the range.",
    "long": "Referral credits pool into the company's one activation balance and roll over like any other credit, so the ledger does not record which unspent activations came from a referral. This figure assumes referral credits are spent last, which makes it an upper bound: the most the programme could still owe in activations. Its nominal value is the figure times $25, though an activation costs Vera close to nothing to honour.",
    "seeAlso": [
      "referral-credits-granted",
      "referral-credits-reversed"
    ]
  },
  {
    "id": "vera-insiders",
    "term": "Vera Insiders",
    "aliases": [
      "insiders",
      "insider programme",
      "insider program"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "The affiliate programme for individuals: an Insider introduces a brand and is paid a cash commission, through FirstPromoter. Separate from Refer & earn.",
    "long": "An Insider's link sets a FirstPromoter cookie on vera.inc, which the waitlist form passes to vera-2 as the prospect's Insider reference. vera-2 records the prospect, and when the prospect's company is created it reports the signup to FirstPromoter, whose own Stripe integration then works out the commission. Commissions and payouts live in FirstPromoter, not in vera-2.\n\nThe first recorded touch wins across both programmes, so a prospect an Insider introduced cannot also earn [referral credits](term:referral-credits) for the brand that referred them later.",
    "seeAlso": [
      "insider-prospect",
      "refer-a-brand"
    ]
  },
  {
    "id": "insider-prospect",
    "term": "Insider prospect",
    "aliases": [
      "insider-attributed prospect"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "A prospect vera-2 recorded as introduced by a Vera Insider, because the waitlist form arrived carrying an Insider reference before any other referral touch.",
    "long": "Counted from vera-2's own referral records, so these figures exist before FirstPromoter is connected. A brand referral that arrives for a prospect an Insider already holds is kept as ineligible, with the reason \"insider attributed\"; the Affiliates page counts those as brand touches lost to Insiders.",
    "seeAlso": [
      "vera-insiders"
    ]
  },
  {
    "id": "active-insiders",
    "term": "Active Insiders",
    "kind": "formula",
    "audience": "internal",
    "tags": [
      "metric"
    ],
    "formula": "FirstPromoter promoters whose state is accepted and who are not archived, counted now.",
    "short": "Insiders FirstPromoter shows as accepted and not archived, counted now rather than for the range. Applications still awaiting review are shown separately.",
    "seeAlso": [
      "vera-insiders"
    ]
  },
  {
    "id": "insider-leads",
    "term": "Insider leads",
    "kind": "formula",
    "audience": "internal",
    "tags": [
      "metric"
    ],
    "formula": "FirstPromoter's referrals count for the range: leads registered against an Insider.",
    "short": "Leads FirstPromoter registered against an Insider in the range. It counts what FirstPromoter saw, which can differ from the prospects vera-2 recorded.",
    "seeAlso": [
      "insider-prospect",
      "vera-insiders"
    ]
  },
  {
    "id": "insider-commission-owed",
    "term": "Insider commission owed",
    "kind": "formula",
    "audience": "internal",
    "tags": [
      "metric"
    ],
    "formula": "FirstPromoter's due payouts now: the next payout, overdue payouts and custom payouts, including tax.",
    "short": "Cash commission FirstPromoter shows as due to Insiders now: the next payout, anything overdue and custom payouts, tax included. Not limited to the range.",
    "seeAlso": [
      "insider-commission-paid",
      "vera-insiders"
    ]
  },
  {
    "id": "insider-commission-paid",
    "term": "Insider commission paid",
    "kind": "formula",
    "audience": "internal",
    "tags": [
      "metric"
    ],
    "formula": "FirstPromoter's promoter paid amount for the range; earned is its promoter earnings amount for the same range.",
    "short": "Cash commission FirstPromoter recorded as paid to Insiders in the range, beside the commission they earned in it.",
    "seeAlso": [
      "insider-commission-owed",
      "vera-insiders"
    ]
  },
  {
    "id": "gtm",
    "term": "GTM",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "go-to-market",
      "go to market"
    ],
    "short": "GTM (go-to-market): how Vera finds and wins customers. Marketing, sales and partnerships.",
    "long": "The internal sidebar's GTM group holds a page for each channel Vera operates on (SEO & AEO, Articles, Sales, Trade shows, Affiliates) and the Channels overview of all nineteen [Traction channels](term:source-channel). It is gated by its own permission, View GTM, separate from Analytics: Analytics is how the business performs, GTM is how customers are found.",
    "seeAlso": [
      "gtm-channel-status-focus",
      "source-channel",
      "permission-view-gtm"
    ]
  },
  {
    "id": "permission-view-gtm",
    "term": "View GTM",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "internal_gtm_view",
      "gtm permission"
    ],
    "short": "The permission that opens every GTM page and the data behind it. Seeded to Vera Admin.",
    "long": "Added in VRG-684. Until then the GTM pages borrowed View analytics as a placeholder; they now have their own grant, so a future sales hire can hold GTM without holding Analytics, and the reverse. Writes on GTM pages (campaigns, approvals, source channels) are still Vera Admin only, by role.",
    "seeAlso": [
      "gtm"
    ]
  },
  {
    "id": "gtm-channel-status-focus",
    "term": "Focus",
    "kind": "status",
    "audience": "internal",
    "aliases": [
      "focus channel"
    ],
    "short": "A channel Vera is building now. At most a couple at a time.",
    "seeAlso": [
      "gtm-channel-status-active",
      "gtm"
    ]
  },
  {
    "id": "gtm-channel-status-active",
    "term": "Active",
    "kind": "status",
    "audience": "internal",
    "aliases": [
      "active channel"
    ],
    "short": "A channel that is running, but is not the main focus.",
    "seeAlso": [
      "gtm-channel-status-focus"
    ]
  },
  {
    "id": "gtm-channel-status-planned",
    "term": "Planned",
    "kind": "status",
    "audience": "internal",
    "aliases": [
      "planned channel"
    ],
    "short": "A channel Spencer has chosen that has not started yet.",
    "seeAlso": [
      "gtm-channel-status-proposed"
    ]
  },
  {
    "id": "gtm-channel-status-proposed",
    "term": "Proposed",
    "kind": "status",
    "audience": "internal",
    "aliases": [
      "proposed channel"
    ],
    "short": "A channel recommended but not yet chosen by Spencer.",
    "seeAlso": [
      "gtm-channel-status-planned"
    ]
  },
  {
    "id": "gtm-channel-status-not-now",
    "term": "Not now",
    "kind": "status",
    "audience": "internal",
    "aliases": [
      "not now channel"
    ],
    "short": "A channel considered and deliberately skipped, kept on the map so the decision stays visible.",
    "seeAlso": [
      "gtm-channel-status-proposed"
    ]
  },
  {
    "id": "trade-show-fit",
    "term": "Fit",
    "kind": "column",
    "audience": "internal",
    "aliases": [
      "trade show fit"
    ],
    "short": "The researcher's judgement of who a show suits: Small brands (small and mid-size founder-led brands can attend or exhibit), Mixed, or Large houses.",
    "long": "Large houses means mostly large houses or invitation-only. A judgement from the research, not a rule the organizer publishes.",
    "seeAlso": [
      "trade-show-date-status"
    ]
  },
  {
    "id": "trade-show-date-status",
    "term": "Date status",
    "kind": "column",
    "audience": "internal",
    "aliases": [
      "confirmed date",
      "unconfirmed date"
    ],
    "short": "Confirmed: the date is on the organizer's own page. Unconfirmed: the date came from another source; check before booking.",
    "seeAlso": [
      "trade-show-date-approx"
    ]
  },
  {
    "id": "trade-show-date-approx",
    "term": "Approximate date",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "approx date",
      "~ date"
    ],
    "short": "Approx (~): the date is not pinned to the organizer's page, or sources disagree. Undated shows give the research's own words.",
    "seeAlso": [
      "trade-show-date-status"
    ]
  },
  {
    "id": "source-channel",
    "term": "Source channel",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "acquisition channel",
      "traction channel"
    ],
    "short": "The go-to-market channel that brought a person or a company to Vera, one of the 19 Traction channels. Sales (#13) includes outreach: email outreach and field visits.",
    "long": "Set by hand on People and on the Company page; GTM → Sales counts the pipeline by it. A company with none inherits the channel of its earliest signup. Existing records were not backfilled: they read [Not recorded](term:source-channel-not-recorded) until someone sets them.",
    "seeAlso": [
      "source-channel-not-recorded",
      "sales-stage-rate",
      "outreach-campaign"
    ]
  },
  {
    "id": "source-channel-not-recorded",
    "term": "Not recorded",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "no source channel"
    ],
    "short": "No channel has been set for this person or company. Not the same as direct: nobody has said where they came from.",
    "seeAlso": [
      "source-channel"
    ]
  },
  {
    "id": "sales-stage-rate",
    "term": "Stage rate",
    "kind": "formula",
    "audience": "internal",
    "aliases": [
      "sales stage conversion",
      "pipeline stage rate"
    ],
    "short": "A stage's count divided by the previous stage's count in the same date range. Not a cohort rate, so it can pass 100%.",
    "formula": "Count at this stage in the range ÷ count at the previous stage in the same range. A dash when the previous stage is zero.",
    "seeAlso": [
      "source-channel"
    ]
  },
  {
    "id": "outreach-campaign",
    "term": "Outreach campaign",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "campaign",
      "outreach"
    ],
    "short": "A planned push to a short list of brands: email outreach, or a field visit (an in-person city trip). Each brand in it has a status.",
    "long": "Campaigns live on GTM → Sales → Campaigns (the Outreach tab until VRG-713). Nothing is sent from vera-2: statuses are recorded by whoever sent the email from their own inbox. A reply or a meeting can be linked to People or to a demo, which records the [source channel](term:source-channel) Outreach. A campaign can carry its trip cost, in the currency it was paid in.",
    "seeAlso": [
      "field-visit",
      "outreach-status-to-contact",
      "source-channel"
    ]
  },
  {
    "id": "field-visit",
    "term": "Field visit",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "city trip",
      "in-person outreach"
    ],
    "short": "An outreach campaign run in person: a founder visits a city and meets brands that replied. A campaign type, not a channel.",
    "seeAlso": [
      "outreach-campaign"
    ]
  },
  {
    "id": "outreach-status-to-contact",
    "term": "To contact",
    "kind": "status",
    "audience": "internal",
    "aliases": [
      "outreach to contact"
    ],
    "short": "On the list, not contacted yet.",
    "seeAlso": [
      "outreach-status-sent",
      "outreach-campaign"
    ]
  },
  {
    "id": "outreach-status-sent",
    "term": "Sent",
    "kind": "status",
    "audience": "internal",
    "aliases": [
      "outreach sent"
    ],
    "short": "The first email has gone out, from the sender's own inbox.",
    "seeAlso": [
      "outreach-status-to-contact",
      "outreach-status-replied",
      "outreach-campaign"
    ]
  },
  {
    "id": "outreach-status-replied",
    "term": "Replied",
    "kind": "status",
    "audience": "internal",
    "aliases": [
      "outreach replied"
    ],
    "short": "The brand answered. Link the contact to People so the reply counts as Outreach.",
    "seeAlso": [
      "outreach-status-sent",
      "outreach-status-meeting-booked",
      "outreach-campaign"
    ]
  },
  {
    "id": "outreach-status-meeting-booked",
    "term": "Meeting booked",
    "kind": "status",
    "audience": "internal",
    "aliases": [
      "outreach meeting booked"
    ],
    "short": "A meeting is in the calendar but has not happened yet.",
    "seeAlso": [
      "outreach-status-replied",
      "outreach-status-meeting-held",
      "outreach-campaign"
    ]
  },
  {
    "id": "outreach-status-meeting-held",
    "term": "Meeting held",
    "kind": "status",
    "audience": "internal",
    "aliases": [
      "outreach meeting held"
    ],
    "short": "The meeting took place.",
    "seeAlso": [
      "outreach-status-meeting-booked",
      "outreach-status-pilot",
      "outreach-campaign"
    ]
  },
  {
    "id": "outreach-status-pilot",
    "term": "Pilot",
    "kind": "status",
    "audience": "internal",
    "aliases": [
      "outreach pilot"
    ],
    "short": "The brand is trying Vera on a limited set of products before committing.",
    "seeAlso": [
      "outreach-status-meeting-held",
      "outreach-status-loi-signed",
      "outreach-campaign"
    ]
  },
  {
    "id": "outreach-status-loi-signed",
    "term": "LOI signed",
    "kind": "status",
    "audience": "internal",
    "aliases": [
      "outreach loi signed"
    ],
    "short": "The brand signed a letter of intent: the outcome an outreach campaign exists to produce.",
    "seeAlso": [
      "outreach-status-pilot",
      "outreach-status-declined",
      "outreach-campaign"
    ]
  },
  {
    "id": "outreach-status-declined",
    "term": "Declined",
    "kind": "status",
    "audience": "internal",
    "aliases": [
      "outreach declined"
    ],
    "short": "The brand said no. Can follow any other status.",
    "seeAlso": [
      "outreach-status-loi-signed",
      "outreach-campaign"
    ]
  },
  {
    "id": "article-status-published",
    "term": "Published (article)",
    "kind": "status",
    "audience": "internal",
    "aliases": [
      "published article",
      "article published"
    ],
    "short": "Live on vera.inc: listed on /insights or /learn and in the sitemap and llms.txt. Only Publish on GTM → Articles does this; everything else stays staged.",
    "long": "Since VRG-685 vera-2 owns which articles are published. Publish on GTM → Articles takes an article live within about a minute with no website deploy: the website reads the published list from vera-2 and is sent a signed refresh. Unpublish reverses it (the page returns not found and leaves the sitemap). Publishing does not require an approval; the confirm says when the words changed since one. Needs the [Publish articles](term:permission-publish-articles) permission.",
    "seeAlso": [
      "article-status-awaiting-publish",
      "article-website-refresh-pending",
      "permission-publish-articles",
      "gtm"
    ]
  },
  {
    "id": "article-website-refresh-pending",
    "term": "Website refresh pending",
    "kind": "status",
    "audience": "internal",
    "aliases": [
      "refresh pending"
    ],
    "short": "vera.inc did not confirm it heard about the last publish or unpublish. The change stands, and the website catches up on its own within about a minute.",
    "long": "After every publish or unpublish vera-2 sends vera.inc a signed refresh, up to three tries. If none is acknowledged (the site is down, the secret does not match, or the refresh is not configured in this environment) the row says so. Nothing is lost: the website re-reads the published list from vera-2 about once a minute. Retry website refresh re-sends it.",
    "seeAlso": [
      "article-status-published"
    ]
  },
  {
    "id": "permission-publish-articles",
    "term": "Publish articles",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "internal_articles_publish",
      "publish permission"
    ],
    "short": "The permission to publish and unpublish vera.inc articles from GTM → Articles. Seeded to Vera Admin only.",
    "long": "Added in VRG-685, separate from View GTM because publishing changes the public website within a minute. Without it, Publish, Unpublish and Retry website refresh show disabled with this permission named.",
    "seeAlso": [
      "article-status-published",
      "permission-view-gtm"
    ]
  },
  {
    "id": "article-status-needs-review",
    "term": "Needs review",
    "kind": "status",
    "audience": "internal",
    "aliases": [
      "article needs review"
    ],
    "short": "No approval is recorded for the article's current version. It is staged until someone publishes it on GTM → Articles.",
    "long": "An approval is optional: publishing does not require one. Approving records that this exact version may go out, so a later edit reads as changed since approval rather than inheriting the approval.",
    "seeAlso": [
      "article-status-awaiting-publish",
      "article-status-published"
    ]
  },
  {
    "id": "article-status-awaiting-publish",
    "term": "Awaiting publish",
    "kind": "status",
    "audience": "internal",
    "aliases": [
      "article awaiting publish"
    ],
    "short": "Approved, not yet published. The article stays staged until someone clicks Publish on GTM → Articles.",
    "long": "Until VRG-685 this meant waiting on a website deploy. Since then vera-2 owns publication, so the only step left is Publish on GTM → Articles, which takes it live on vera.inc within about a minute.",
    "seeAlso": [
      "article-status-changed",
      "article-status-published"
    ]
  },
  {
    "id": "article-status-changed",
    "term": "Changed since approval",
    "kind": "status",
    "audience": "internal",
    "aliases": [
      "article changed since approval"
    ],
    "short": "The article was approved, then its words changed. The approval does not carry over: approve the new version, or publish knowing it is not the approved one.",
    "long": "The approval stores a hash of what a reader sees at the moment of approval. When the website's article manifest shows a different hash, the approval is for words nobody approved. The Publish confirm says so before it goes live.",
    "seeAlso": [
      "article-status-awaiting-publish",
      "article-status-needs-review"
    ]
  },
  {
    "id": "sales-deal",
    "term": "Deal",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "sales deal",
      "deals board"
    ],
    "short": "A sale on the pipeline: its stage, predicted ARR, plan, owner and next step. A company can have several deals: an upsell or a rollout is a deal of its own.",
    "long": "vera-2 is the only place deals are written; Apollo stays enrichment and outreach. Every company on the pipeline gets its first deal on its own: waitlist signups, provisioned demos and real platform companies arrive by themselves, and staff add the rest. Add deal opens another at a company already on it; a second open deal that is not Won is warned about, never refused. Won stays an open deal at the Won stage. A company's first deal takes all of the company's evidence (grant, demo, platform company, paid invoice); a later deal only what happens after it opened. A deal is closed as [Lost](term:sales-deal-lost) or archived, never deleted, and its [stage history](term:sales-stage-history) is append-only.",
    "seeAlso": [
      "deal-anticipated-value",
      "sales-stage-lead",
      "sales-company",
      "sales-lead"
    ]
  },
  {
    "id": "sales-company",
    "term": "Company (sales)",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "prospect company",
      "sales account"
    ],
    "short": "A brand Vera is selling to, on GTM → Sales. Not a platform company: it links to its platform company, and to its demo, once they exist.",
    "long": "Linking is by website domain: a platform company or demo with the same domain links itself. A sales company can have many leads and several deals (VRG-701): its first deal, then upsells, rollouts or new attempts.",
    "seeAlso": [
      "sales-deal",
      "sales-lead",
      "demo"
    ]
  },
  {
    "id": "sales-lead",
    "term": "Sales lead",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "pipeline lead"
    ],
    "short": "A person on the sales pipeline, keyed by email. A waitlist signup becomes one on its own. A lead can belong to a sales company, or to none yet.",
    "long": "The email is the People key, so the lead, the signup and the People row are one person, and the lead's source channel is People's. Not the same as a brand's lead (someone who scanned a certificate).",
    "seeAlso": [
      "sales-company",
      "source-channel"
    ]
  },
  {
    "id": "sales-stage-lead",
    "term": "Lead (sales stage)",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "lead stage"
    ],
    "short": "The first sales stage: the brand is on the pipeline and nothing else has happened yet. Every deal starts here.",
    "seeAlso": [
      "sales-stage-access-granted",
      "sales-deal"
    ]
  },
  {
    "id": "sales-stage-access-granted",
    "term": "Access granted",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "access granted stage"
    ],
    "short": "A lead at this company was granted access from the waitlist. Vera sets this stage itself when the grant happens.",
    "seeAlso": [
      "sales-stage-lead",
      "sales-stage-account-created"
    ]
  },
  {
    "id": "sales-stage-account-created",
    "term": "Signed up",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "signed up stage",
      "account created",
      "account created stage",
      "account_created"
    ],
    "short": "The brand has signed up: a real platform company exists for it. Demo, test, example and internal companies never count. A demo that is taken over does.",
    "long": "Vera sets this stage itself when the linked platform company is real, dated at its creation or at the demo takeover. It was shown as Account created until 2026-10-20 (VRG-727); it is still stored as `account_created`, and the definition did not change. Not to be confused with *Joined waitlist* on the People tab, which is when the person's signup arrived.",
    "seeAlso": [
      "sales-stage-access-granted",
      "sales-stage-paid-client",
      "demo"
    ]
  },
  {
    "id": "sales-stage-paid-client",
    "term": "Won",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "first paid invoice",
      "paid client",
      "paid client stage",
      "won deal",
      "won stage"
    ],
    "short": "The brand paid its first invoice: above $0, in Stripe live mode, on a real company. Won never reverts. Shown as Won (stored as Paid client).",
    "long": "Vera sets this stage itself at the company's first paid invoice (Stripe's invoice.paid webhook). A comped or $0 invoice, a test-mode invoice, and any invoice on a demo, internal, test or example company never count. [Became a client](term:became-client) is stamped at the same moment by the same rule, so the two never disagree. Won never reverts: nothing automatic moves a deal out of Won, and a company that later stops paying stays Won and reads [Not paying](term:paying-now). Dragging a deal here without that invoice asks you to confirm and records it as set by hand. The column was called Paid client until 2026-10-13; live mode was made explicit on 2026-10-20 (VRG-727).",
    "seeAlso": [
      "sales-stage-account-created",
      "sales-won",
      "paying-now",
      "became-client"
    ]
  },
  {
    "id": "sales-stage-demo",
    "term": "Demo",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "demo stage",
      "demo provisioned",
      "demo lane"
    ],
    "short": "A demo has been provisioned for the brand. A stage between Access granted and Account created; a deal may skip it.",
    "long": "Vera sets it itself when a demo company is provisioned for the brand, dated at the demo. A deal does not need it to reach Account created, so moving past it by hand does not claim a demo happened. A taken-over demo goes on to Account created. It was a side lane until 2026-10-13.",
    "seeAlso": [
      "demo",
      "sales-stage-access-granted",
      "sales-stage-account-created"
    ]
  },
  {
    "id": "deal-anticipated-value",
    "term": "Predicted ARR",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "anticipated value",
      "deal value",
      "predicted MRR"
    ],
    "short": "What a deal is expected to bill Vera a year, USD: plan × 12 plus activations on the share of production expected to activate. A manual figure wins. No tags.",
    "long": "A manual figure always wins, then the deal's own [calculator inputs](term:deal-calc-inputs), then the company's ROI calculator, then the form (VRG-701). The calculator bases price the plan plus activations on production × the deal's [activation share](term:activation-share) (VRG-736), on the activation ladder, so they include metered activations a subscription does not commit to. Tag sales are NOT in it: tags are hardware, and putting them in ARR would overstate recurring revenue, so they are a separate [tag margin](term:tag-margin) line. Once a deal names a plan, the company-calculator basis is priced on that plan. It is compared with [Committed ARR](term:sales-committed-arr) once the brand subscribes, against the [snapshot](term:sales-prediction-snapshot) taken then.",
    "seeAlso": [
      "deal-value-basis",
      "sales-committed-arr",
      "sales-forecast-accuracy",
      "sales-weighted-value",
      "activation-share",
      "tag-margin",
      "predicted-annual-value"
    ]
  },
  {
    "id": "deal-value-basis",
    "term": "Value basis",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "basis chip",
      "calculator basis"
    ],
    "short": "Where a deal's predicted ARR comes from: Manual (always wins), Deal inputs (calculator inputs edited on the deal), Calculator (the company's), or Form (plan of interest × 12).",
    "seeAlso": [
      "deal-anticipated-value",
      "deal-calc-inputs"
    ]
  },
  {
    "id": "lead-source",
    "term": "Lead source",
    "kind": "term",
    "audience": "internal",
    "aliases": [],
    "short": "Where a sales company or deal came from: a source channel, a sub-source picked from that channel's list, and whether Vera or a person set it.",
    "long": "A source set by hand always wins. Otherwise Vera fills it in from what it records: the platform company's or a lead's channel, an outreach campaign, a referral, a tracked link, or the first visit for visitors with analytics on. Grouped on the board under the channels' own names: Website, Social media, Content, Events, Sales, Referral, PR / press, Partners, Email, SEM / AEM, Offline ads, Expansion, Other or Not recorded. Expansion is not a channel: it marks a later deal at a company that is already a client.",
    "seeAlso": [
      "source-channel",
      "source-channel-not-recorded"
    ]
  },
  {
    "id": "sales-open-pipeline",
    "term": "Open pipeline",
    "kind": "formula",
    "audience": "internal",
    "aliases": [],
    "short": "The predicted ARR of every open deal not yet on a subscription, summed. Deals with no value, or one in another currency, are left out and counted beside it.",
    "formula": "Σ predicted ARR (USD) of open deals without an active subscription. A deal that subscribes moves to Committed ARR.",
    "seeAlso": [
      "sales-weighted-value",
      "deal-anticipated-value",
      "sales-committed-arr",
      "pipeline-value"
    ]
  },
  {
    "id": "sales-weighted-value",
    "term": "Weighted pipeline",
    "kind": "formula",
    "audience": "internal",
    "aliases": [
      "weighted value"
    ],
    "short": "Each open-pipeline deal's predicted ARR times its stage's chance of closing, summed. Defaults: Lead 10%, Access granted 25%, Demo 35%, Account created 50%, Won 100%.",
    "formula": "Σ over open deals without an active subscription of predicted ARR (USD) × the stage probability.",
    "seeAlso": [
      "sales-open-pipeline"
    ]
  },
  {
    "id": "sales-won",
    "term": "Won",
    "kind": "formula",
    "audience": "internal",
    "aliases": [
      "won this period"
    ],
    "short": "Deals that were won (reached the Won stage, a first paid invoice) in the date range, and their predicted ARR.",
    "formula": "Count (and Σ USD value) of deals at Won whose Won date falls in the range.",
    "seeAlso": [
      "sales-stage-paid-client",
      "sales-days-to-paid"
    ]
  },
  {
    "id": "sales-days-to-paid",
    "term": "Avg days to Won",
    "kind": "formula",
    "audience": "internal",
    "aliases": [
      "days to paid",
      "days to won"
    ],
    "short": "The average number of days from a deal entering the pipeline to its first paid invoice (Won), for deals won in the range.",
    "formula": "Mean of (Won date − date the deal entered the pipeline), over deals won in the range.",
    "seeAlso": [
      "sales-won"
    ]
  },
  {
    "id": "sales-stage-history",
    "term": "Stage history",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "deal history"
    ],
    "short": "Every stage a deal moved through, when, and whether Vera or a person moved it. Append-only: nothing in it is ever edited or deleted.",
    "seeAlso": [
      "sales-deal"
    ]
  },
  {
    "id": "sales-deal-lost",
    "term": "Lost deal",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "closed deal"
    ],
    "short": "A deal closed because the brand said no (Lost) or set aside (Archived). It leaves the open pipeline totals, keeps its history, and can be reopened.",
    "seeAlso": [
      "sales-deal"
    ],
    "long": "Drop a card on the board's Lost column (collapsed by default; Show lost expands it) to mark it lost, with an optional reason; drag it back out onto a stage to reopen it. Both are recorded in the [stage history](term:sales-stage-history). Lost deals end at Lost on the flow chart. Archived deals are on no column."
  },
  {
    "id": "permission-manage-sales",
    "term": "Manage sales",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "internal_sales_manage"
    ],
    "short": "The permission to add and edit sales leads and companies, move deals, set a deal's value and change the stage probabilities. Seeded to Vera Admin only.",
    "long": "Added in VRG-691. Reading GTM → Sales needs View GTM; without Manage sales, Add lead, Add company, dragging and editing show disabled with this permission named.",
    "seeAlso": [
      "permission-view-gtm",
      "sales-deal"
    ]
  },
  {
    "id": "content-idea",
    "term": "Content idea",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "idea",
      "ideas queue",
      "content ideas queue"
    ],
    "short": "A piece Vera might write: an explainer, a perspective or an interview, with the search terms it targets, why it is worth writing, its research and where it stands.",
    "long": "Ideas live on GTM → Insights → Ideas. They arrive by hand, from the one-off import of the old article pipeline, and on their own after every SEO Recommendations recompute (see [origin](term:content-idea-origin)). An idea moves forward one step at a time: [Start brief](term:content-idea-status-brief) is the selection, and the one approval is on the [draft](term:content-idea-status-approved). An idea is not an article: the article is the website's, and publishing it is a separate permission on the Articles tab.",
    "seeAlso": [
      "content-idea-origin",
      "content-idea-score",
      "permission-manage-ideas"
    ]
  },
  {
    "id": "content-idea-status-idea",
    "term": "Idea (content idea status)",
    "kind": "status",
    "audience": "internal",
    "aliases": [
      "idea status"
    ],
    "short": "Not started. Every idea begins here, whether it was added by hand, imported or fed from SEO Recommendations.",
    "seeAlso": [
      "content-idea",
      "content-idea-status-brief"
    ]
  },
  {
    "id": "content-idea-status-brief",
    "term": "Brief (content idea status)",
    "kind": "status",
    "audience": "internal",
    "aliases": [
      "start brief",
      "brief status"
    ],
    "short": "Selected to be written: someone pressed Start brief and is shaping the angle and the research. This is the selection; there is no separate approve step for an idea.",
    "seeAlso": [
      "content-idea-status-draft"
    ]
  },
  {
    "id": "content-idea-status-draft",
    "term": "Draft (content idea status)",
    "kind": "status",
    "audience": "internal",
    "aliases": [
      "draft status"
    ],
    "short": "Being written. The draft link on the idea points at the working document.",
    "seeAlso": [
      "content-idea-status-approved"
    ]
  },
  {
    "id": "content-idea-status-approved",
    "term": "Draft approved",
    "kind": "status",
    "audience": "internal",
    "aliases": [
      "approved (content idea)",
      "approve draft"
    ],
    "short": "The draft has been read and approved. This is the only approval an idea gets. It does not publish anything.",
    "long": "Publishing an article is the website's act and its own permission (Publish articles, on the Articles tab). Once the article is live, the idea is marked [Published](term:content-idea-status-published) with the article it became.",
    "seeAlso": [
      "content-idea-status-published",
      "content-idea"
    ]
  },
  {
    "id": "content-idea-status-published",
    "term": "Published (content idea status)",
    "kind": "status",
    "audience": "internal",
    "aliases": [
      "mark published"
    ],
    "short": "The idea became a live article, and the idea records which one (section and slug). Marking it unpublished keeps the link.",
    "seeAlso": [
      "content-idea-status-approved"
    ]
  },
  {
    "id": "content-idea-status-rejected",
    "term": "Rejected (content idea status)",
    "kind": "status",
    "audience": "internal",
    "aliases": [
      "rejected idea"
    ],
    "short": "Someone decided not to write it, with a reason. Its terms are never fed again as a new idea. It can be reopened.",
    "seeAlso": [
      "content-idea-origin"
    ]
  },
  {
    "id": "content-idea-origin",
    "term": "Origin (content idea)",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "idea origin"
    ],
    "short": "Where an idea came from. Competitor gap, Rising term and AI answer, Vera not cited are fed on their own from SEO Recommendations; Manual is added by a person.",
    "long": "The feed runs after every SEO Recommendations recompute. It reads open, approved and snoozed recommendations, never rejected or resolved ones, and never a term whose keyword was dismissed, already on an idea, or already targeted by a page. Rising terms in one cluster share an idea, up to 8 terms. Competitor post, Regulation, Deal question and VERAfied follow-up are reserved: nothing feeds them yet.",
    "seeAlso": [
      "content-idea",
      "content-idea-score"
    ]
  },
  {
    "id": "content-idea-score",
    "term": "Score (content idea)",
    "kind": "formula",
    "audience": "internal",
    "aliases": [
      "idea score"
    ],
    "formula": "Sum of the SEO recommendation scores of the idea's terms (estimated monthly clicks for a #3 result, weighted by buyer fit or keyword difficulty; a fixed weight for an AI-answer prompt).",
    "short": "An estimate used only to order the queue, from the SEO recommendations behind the idea's terms. Empty for ideas added by hand. Never a traffic forecast.",
    "seeAlso": [
      "content-idea-origin"
    ]
  },
  {
    "id": "verafied-guest",
    "term": "VERAfied guest",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "guest list",
      "verafied guests"
    ],
    "short": "A person Vera might interview for VERAfied, with where outreach stands and the interview idea they became.",
    "long": "Kept apart from ideas because a guest is a person with an outreach state, while the interview is content with an editorial one. A guest can be linked to a VERAfied idea. There is ONE guest list: the second one (GTM → Articles → Guests, with its own suggested → contacted → booked ladder) was folded into this one and retired, so a guest has one record and one status.",
    "seeAlso": [
      "content-idea",
      "permission-manage-ideas"
    ]
  },
  {
    "id": "permission-manage-ideas",
    "term": "Manage ideas",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "internal_content_ideas_manage"
    ],
    "short": "The permission to add and edit content ideas and VERAfied guests and move an idea up to an approved draft. Seeded to Vera Admin and Vera Manager.",
    "long": "Added in VRG-740. Every write also needs [View GTM](term:permission-view-gtm), which Vera Manager does not hold today, so a manager cannot reach the page until it is granted (decided 2026-10-23: as built; a permission audit follows). Publishing an article is separate: Publish articles.",
    "seeAlso": [
      "permission-view-gtm",
      "content-idea"
    ]
  },
  {
    "id": "sales-committed-arr",
    "term": "Committed ARR",
    "kind": "formula",
    "audience": "internal",
    "aliases": [
      "committed MRR",
      "actual ARR",
      "actual MRR",
      "committed revenue"
    ],
    "short": "What the company's paying Stripe subscriptions bring Vera a year: list price, or the latest paid invoice when the price is $0. Test mode and $0 pilots never count. MRR is ÷ 12.",
    "formula": "Σ over PAYING subscriptions (live mode, active, not trialing, and a price above $0 or a paid invoice above $0 in the last 60 days): the plan's list price, monthly × 12 and annual as is; for one that pays only through invoices, its latest paid invoice annualised the same way. MRR = ARR ÷ 12. Coupons are not applied.",
    "long": "Read from vera-2's mirror of Stripe subscriptions and its ledger of paid invoices. A deal whose company has one leaves the [open pipeline](term:sales-open-pipeline) and counts here, with a Committed chip. The same rule decides a lead's actual value on the deal pipeline, so the two cannot disagree. A live $0 subscription with no paid invoice is a [pilot](term:sales-pilot-subscription), not committed. Metered activations are only in it through a paid invoice, and cash actually collected belongs to Finance.",
    "seeAlso": [
      "paying-subscription",
      "sales-pilot-subscription",
      "deal-anticipated-value",
      "sales-forecast-accuracy",
      "sales-open-pipeline"
    ]
  },
  {
    "id": "deal-pipeline",
    "term": "Deal pipeline",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "master pipeline",
      "master-pipeline",
      "whole funnel",
      "channels pipeline"
    ],
    "short": "GTM → Channels' whole-funnel chart of deals: each deal's source, how it entered, the furthest stage it reached, and whether it was won, lost or is still open.",
    "long": "It starts at the lead stage, not the visit: visits are consent-gated and UTM attribution is not set up, so the website funnel on Analytics → Website covers the steps before. The unit is the [deal](term:sales-deal), and a company can have several: a later sale to an existing client is [Expansion](term:sales-expansion), a source of its own, so the original channel is not credited twice. The company-lifetime view credits every deal to its company's first touch instead. It reads the same filtered slice as the Sales tab's flow chart, from one query, so the two pages always count the same deals. Archived deals are left out and counted beside the chart. In Revenue mode each deal carries one value through every column: its Stripe actual if it has a [paying subscription](term:paying-subscription), at any stage, otherwise its anticipated value. A Won deal with nothing paying therefore counts as predicted. It was called the master pipeline until VRG-701.",
    "seeAlso": [
      "first-touch-attribution",
      "pipeline-entry",
      "pipeline-value",
      "won-revenue",
      "small-sample",
      "sales-deal",
      "paying-subscription"
    ]
  },
  {
    "id": "first-touch-attribution",
    "term": "First-touch attribution",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "first touch",
      "first-touch"
    ],
    "short": "Credit for a deal goes to the source recorded for it: the channel that brought it in. Later touches do not move it. Deals with no source stay Not recorded.",
    "long": "The source is the deal's own, else its company's [source channel](term:source-channel): set by hand, or filled in by Vera from the platform company, People, an outreach campaign, a referral, a tracked link or the website visit. A later sale to an existing client is [Expansion](term:sales-expansion) instead, unless the company-lifetime view is chosen. Not recorded is never redistributed across the channels, so the channel figures only ever describe deals someone or something actually attributed.",
    "seeAlso": [
      "source-channel",
      "source-channel-not-recorded",
      "deal-pipeline"
    ]
  },
  {
    "id": "pipeline-entry",
    "term": "Pipeline entry",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "added directly",
      "entry (pipeline)"
    ],
    "short": "How a lead entered the pipeline. Joined waitlist: a website signup was the first thing Vera saw. Added directly: staff added it, or a demo or company came first.",
    "long": "Decided by first touch: a lead added by hand who signs up on the website a week later is still Added directly. Added directly covers sales, events and field visits.",
    "seeAlso": [
      "deal-pipeline",
      "first-touch-attribution"
    ]
  },
  {
    "id": "pipeline-value",
    "term": "Pipeline value",
    "kind": "formula",
    "audience": "internal",
    "aliases": [
      "channel pipeline value"
    ],
    "short": "The value of a channel's open deals in the range and filters, summed: each deal's predicted ARR, or its Stripe actual if it pays. Deals with no value are counted, not summed.",
    "formula": "Σ each Open deal's one value: the paying subscription's list-price ARR if it has one (rare for an open deal: one moved back out of Won by hand), else its predicted ARR (USD; manual > calculator > form). MRR = ÷ 12. Differs from Open pipeline, which leaves out every subscribed deal.",
    "seeAlso": [
      "sales-open-pipeline",
      "deal-anticipated-value",
      "paying-subscription",
      "won-revenue",
      "deal-pipeline"
    ]
  },
  {
    "id": "won-revenue",
    "term": "Won revenue",
    "kind": "formula",
    "audience": "internal",
    "aliases": [
      "won MRR",
      "won ARR"
    ],
    "short": "What a channel's Won deals pay Vera: their paying Stripe subscriptions' list-price ARR, summed. MRR is ÷ 12. A Won deal with nothing paying adds 0 and is counted.",
    "formula": "Σ list-price ARR (USD) of PAYING subscriptions (live mode, active, not trialing, above $0, not comped) over the channel's Won deals. Each subscription counts on one deal only (the deal it was automatically Won for, else the company's first Won deal). On the deal pipeline chart a Won deal with nothing paying carries its predicted value instead, shown as predicted.",
    "seeAlso": [
      "paying-subscription",
      "sales-committed-arr",
      "sales-stage-paid-client",
      "pipeline-value"
    ]
  },
  {
    "id": "win-rate",
    "term": "Win rate",
    "kind": "formula",
    "audience": "internal",
    "aliases": [
      "close rate"
    ],
    "short": "Of a channel's deals that have closed, the share that were Won: Won ÷ (Won + Lost). Open deals are not counted either way. Shown with n, the number closed.",
    "formula": "Won deals ÷ (Won + Lost deals), in the date range and filters, counted per deal (VRG-701: an upsell is a deal of its own). A dash when none have closed.",
    "seeAlso": [
      "won-revenue",
      "sales-deal-lost",
      "small-sample"
    ]
  },
  {
    "id": "small-sample",
    "term": "Small sample",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "low n",
      "small n"
    ],
    "short": "Fewer than 10 deals behind a figure. The percentage is still shown, greyed: one deal more or less moves it by 10 points or more, so read it as a hint.",
    "long": "Nothing is hidden for being small. Spencer's rule (2026-10-13) is that slicing must always work, so a filter that leaves three deals still draws the chart and still shows its shares, de-emphasised.",
    "seeAlso": [
      "deal-pipeline",
      "win-rate"
    ]
  },
  {
    "id": "sales-forecast-accuracy",
    "term": "Forecast accuracy",
    "kind": "formula",
    "audience": "internal",
    "aliases": [
      "revenue accuracy",
      "forecast accuracy (revenue)"
    ],
    "short": "Committed ARR ÷ predicted ARR. 1.00× is a perfect forecast; below 1 the inputs over-promised, above 1 they under-promised.",
    "formula": "Per company: committed ARR ÷ predicted ARR (the snapshot when there is one). Overall: Σ committed ÷ Σ predicted over the companies with both and a prediction above zero; n and the median company are shown beside it.",
    "long": "Predicted ARR on the calculator basis includes metered activations, which a subscription does not commit to, so a calculator-basis deal reads below 1 until usage is counted. The prediction compared is the [snapshot](term:sales-prediction-snapshot) taken when Vera first saw the outcome.",
    "seeAlso": [
      "deal-anticipated-value",
      "sales-committed-arr",
      "brand-roi-accuracy"
    ]
  },
  {
    "id": "brand-predicted-roi",
    "term": "Predicted ROI",
    "kind": "formula",
    "audience": "internal",
    "aliases": [
      "predicted brand ROI"
    ],
    "short": "The brand's expected net return a year: the ROI calculator's recoverable value minus what Vera is predicted to cost it (the deal's predicted ARR).",
    "formula": "roi_submission.total (recoverable value, USD) − predicted ARR (USD). Unknown when the calculator's units were the site's default, or it ran in another currency.",
    "long": "The brand's figure, not Vera's: there is no Vera ROI. The calculator's recoverable value is resale captured, return fraud avoided and acquisition saved; it leaves out counterfeit exposure, which is not cash.",
    "seeAlso": [
      "brand-actual-roi",
      "brand-roi-accuracy",
      "deal-anticipated-value"
    ]
  },
  {
    "id": "brand-actual-roi",
    "term": "Actual ROI",
    "kind": "formula",
    "audience": "internal",
    "aliases": [
      "actual brand ROI"
    ],
    "short": "The brand's measured net return a year: the Analytics ROI hero's value delivered minus its Vera spend, over the last 12 months, annualised when younger.",
    "formula": "(value delivered − Vera spend) × 365 ÷ days, over the trailing 365 days or since Account created. Needs 30 days, a client (paid invoice or subscription) and USD.",
    "long": "The same use-case the company's Analytics page renders, so the two cannot disagree. It changes as outcomes accrue. Value delivered counts what the platform measures and the inputs the brand has supplied, which is not the calculator's model, so part of any gap is the two models differing.",
    "seeAlso": [
      "brand-predicted-roi",
      "brand-roi-accuracy"
    ]
  },
  {
    "id": "brand-roi-accuracy",
    "term": "ROI accuracy",
    "kind": "formula",
    "audience": "internal",
    "aliases": [
      "ROI accuracy (brand)"
    ],
    "short": "The brand's actual ROI ÷ its predicted ROI. 1.00× means the calculator's inputs predicted the outcome; only shown when the prediction is above zero.",
    "formula": "Per company: actual ROI ÷ predicted ROI (the snapshot when there is one), predicted > 0. Overall: Σ actual ÷ Σ predicted over the companies with both; n and the median company beside it.",
    "seeAlso": [
      "brand-predicted-roi",
      "brand-actual-roi",
      "sales-forecast-accuracy"
    ]
  },
  {
    "id": "sales-prediction-snapshot",
    "term": "Prediction snapshot",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "snapshot",
      "frozen prediction"
    ],
    "short": "A deal's predicted ARR and ROI, frozen the first time Vera sees its outcome (a paid invoice or an active subscription), so accuracy compares against the earlier forecast.",
    "long": "Taken once per deal and never rewritten. Marked as a backfill when taken more than a day after the outcome, as for brands that were already clients when snapshots began. Editing a deal's manual value afterwards changes the live prediction only.",
    "seeAlso": [
      "sales-forecast-accuracy",
      "brand-roi-accuracy"
    ]
  },
  {
    "id": "paying-subscription",
    "term": "Paying subscription",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "paying",
      "paid subscription",
      "active paid subscription"
    ],
    "short": "A live-mode Stripe subscription that is active and not trialing, with a paid invoice above $0 in the last 60 days. Test mode, $0 pilots and a plan not yet invoiced never count.",
    "long": "Money has to have arrived (VRG-727, 2026-10-20): a priced plan with no paid invoice yet is not paying, and one whose last paid invoice is more than 60 days old has lapsed. The invoice can be the subscription's own or, naming no subscription, its company's, which covers a comped base fee with metered activations and a usage-billed plan whose fixed price reads $0. Vera reads the mode from each subscription's own Stripe events (and each invoice's); one no event has touched yet is read as the mode of the API's Stripe key (test on dev, live on prod). A subscription mirrored as active whose trial has not ended is trialing. It is the rule behind [Paying now](term:paying-now), [committed ARR](term:sales-committed-arr) and the deal pipeline's actual values. [Won](term:sales-stage-paid-client) is a different, one-time fact: the first paid invoice.",
    "seeAlso": [
      "paying-now",
      "sales-committed-arr",
      "sales-pilot-subscription",
      "sales-auto-won",
      "won-revenue",
      "deal-pipeline"
    ]
  },
  {
    "id": "paying-now",
    "term": "Paying now",
    "kind": "status",
    "audience": "internal",
    "aliases": [
      "not paying",
      "never paid",
      "paying now status",
      "lapsed"
    ],
    "short": "The company has a live-mode, active subscription with a paid invoice above $0 in the last 60 days. Past 60 days it lapses to Not paying; one that never paid is Never paid.",
    "long": "Shown as a chip on GTM → Sales (Companies, Deals and the company drawer) and on Accounts → Companies, with a filter for all three values; Never paid shows no chip. It is the [paying subscription](term:paying-subscription) rule applied to the company: paying now when any of its subscriptions is. It can lapse and come back; [Won](term:sales-stage-paid-client) cannot. Not paying means the company was Won (it paid a first invoice) and is not paying now: churned, or between invoices for more than 60 days. Demo, internal, test and example companies are always Never paid. Defined by Spencer on 2026-10-20 (VRG-727).",
    "seeAlso": [
      "paying-subscription",
      "sales-stage-paid-client",
      "sales-won-without-paying",
      "sales-committed-arr"
    ]
  },
  {
    "id": "sales-auto-won",
    "term": "Moved to Won automatically",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "auto won",
      "auto-won",
      "automatic won",
      "auto"
    ],
    "short": "Vera moved this deal to Won itself at a subscription's first paid invoice (live mode, above $0). Move it back by hand and Vera will not move it again for that subscription.",
    "long": "Runs on the Stripe webhooks and a nightly reconcile at 04:40 UTC, only while SALES_AUTO_WON_ENABLED is on. What it moves on is a subscription's FIRST paid invoice above $0 in live mode, dated at that invoice (VRG-727, 2026-10-20); whether the company is paying now does not matter, so an old first invoice still counts and a priced subscription with no invoice yet does not. With the switch off, an open deal still reaches Won from the Sales sync's own first-paid-invoice evidence; the switch adds reopening Lost and archived deals and winning a company's later deals. A Lost or archived deal is reopened only for a first paid invoice that came after it was closed. The nightly read of paid Stripe invoices runs either way (it only reads, so the dry run is complete). The stage history records the move with the subscription id and the invoice date; moving the deal out of Won, or closing it, records an override. Nothing moves a deal out of Won. With several deals at a company (VRG-701) the subscription moves ONE deal: the newest open deal that is not Won and opened before its first paid invoice, else the newest open deal that is not Won, else the most recent closed one, else none. A later deal moves only for a subscription first paid after it opened, and a subscription Vera already used at the company is never used again, so a plan upgrade on the same subscription is moved to Won by hand.",
    "seeAlso": [
      "sales-stage-paid-client",
      "paying-now",
      "paying-subscription",
      "sales-won-without-paying"
    ]
  },
  {
    "id": "sales-won-without-paying",
    "term": "Won without a paid subscription",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "won, but no active paid subscription",
      "won without paying"
    ],
    "short": "Won, but no active paid subscription: the deal is at Won while its company is not paying now (no paid invoice in 60 days, a trial, a $0 pilot, test mode, or cancelled).",
    "long": "Usually a company that paid once and lapsed (it reads [Not paying](term:paying-now)), a deal moved to Won by hand, or one whose subscription has since ended. It still counts as predicted on the deal pipeline, at its anticipated value. Vera never moves it out of Won on its own.",
    "seeAlso": [
      "paying-now",
      "paying-subscription",
      "sales-auto-won",
      "sales-stage-paid-client"
    ]
  },
  {
    "id": "sales-expansion",
    "term": "Expansion",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "expansion deal",
      "upsell",
      "existing client"
    ],
    "short": "The source of a later deal at a company that is already a client (it has a Won deal): an upsell, a rollout after a pilot. Not a channel; editable on the deal.",
    "long": "A new deal at a company with a Won deal starts as Expansion; a person can change it to a channel if the sale came from one. Expansion is a source of its own on the [deal pipeline](term:deal-pipeline) and the Sales flow, so the channel that brought the company in is never credited twice for one client. The company-lifetime view credits Expansion deals back to the company's [first touch](term:first-touch-attribution) instead.",
    "seeAlso": [
      "sales-deal",
      "lead-source",
      "first-touch-attribution",
      "deal-pipeline"
    ]
  },
  {
    "id": "sales-deal-plan",
    "term": "Plan (deal)",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "deal plan",
      "subscription plan",
      "vera product",
      "product interest"
    ],
    "short": "The subscription plan a deal is on or is buying: Starter, Individual, Business, Scale or Enterprise. From Stripe while a paying subscription sets it; otherwise set by hand.",
    "long": "Keyed by the plan catalog's stable key, never the Stripe title. Replaces the company-level Vera product field (the tag set), because activation pricing does not differ by tag type: every plan shares one graduated activation price. While the deal's [paying subscription](term:paying-subscription) sets it, the plan reads \"from Stripe\" and cannot be edited.",
    "seeAlso": [
      "sales-deal",
      "paying-subscription"
    ]
  },
  {
    "id": "sales-sub-source",
    "term": "Sub-source",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "sub-source",
      "sub source",
      "source detail"
    ],
    "short": "The finer origin under a lead source's channel: which trade show, outreach campaign, article or referrer, or a label saved for that channel. Picked from a list, not typed.",
    "long": "The list depends on the channel: Trade shows offers the trade-show calendar, Sales its outreach campaigns, Content and SEO the website's articles, Affiliate programs the referring brands and Insiders. Every channel also has a saved list that Add sub-source grows (Manage sales). It is stored as a reference with the label it had when picked, so deals group by it and a renamed campaign does not orphan them. The deal pipeline can split each channel by sub-source, and filter by one. Free-text sub-sources from before VRG-701 moved into the saved lists.",
    "seeAlso": [
      "lead-source",
      "source-channel",
      "deal-pipeline"
    ]
  },
  {
    "id": "client-tier",
    "term": "Tier",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "client tier",
      "company tier",
      "size tier",
      "tier 1",
      "tier 5"
    ],
    "short": "A brand's size as the business model counts it, Tier 1 (5,000+ employees, ~1M certificates a year) to Tier 5 (under 20). Annual production decides it; employees are a proxy.",
    "long": "Ranges by annual production, approved 2026-10-14: Tier 1 ≥ 300K a year, Tier 2 30K–300K (typical 100K; the deck's 10K is a typo), Tier 3 7K–30K, Tier 4 1K–7K, Tier 5 under 1K (typical ~250: individual artists and creators, a wide range). When the company's ROI calculator gives annual products, the form suggests the tier they fall in; it never sets one by itself. It replaced the free Size field; a size that fit one tier was mapped, the rest left unset. Demos use the same tiers.",
    "seeAlso": [
      "sales-company",
      "deal-calc-inputs"
    ]
  },
  {
    "id": "commerce-platform",
    "term": "Commerce platform",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "ecommerce platform",
      "storefront platform",
      "shop platform",
      "store platform"
    ],
    "short": "What a prospect's online shop runs on: Shopify, WooCommerce, Squarespace and so on. Detected when a demo is made; staff can correct it.",
    "long": "Read from the pages the Create demo step already fetches (markup such as cdn.shopify.com, response headers such as x-shopid, and whether the store's product catalogue answers), so detecting it costs the prospect's site no extra request. It is stored on the [sales company](term:sales-company) as one value from a fixed list, so ROI can later be grouped by it. Custom / other means the site was read and matched none of the listed platforms; Unknown means it could not be told. A value a person picked, on the Create demo modal or the company form, is never replaced by a later detection; clearing it hands it back to detection.",
    "seeAlso": [
      "sales-company",
      "client-tier"
    ]
  },
  {
    "id": "deal-calc-inputs",
    "term": "Calculator inputs (deal)",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "deal calculator inputs",
      "deal inputs",
      "products a year"
    ],
    "short": "The ROI-calculator figures a deal is priced on: products a year, average price and CAC. From the company's calculator unless edited on the deal, and marked which.",
    "long": "They default from the newest calculator submission of any of the company's people, ignoring the site's seeded 25,000 units. Editing them on the deal makes its value basis Deal inputs, which ranks after a manual figure and before the company's calculator. They price the deal's [potential subscription](term:potential-subscription-revenue) and [potential activation](term:potential-activation-revenue) revenue.",
    "seeAlso": [
      "deal-value-basis",
      "potential-subscription-revenue",
      "potential-activation-revenue",
      "client-tier"
    ]
  },
  {
    "id": "potential-subscription-revenue",
    "term": "Potential subscription revenue",
    "kind": "formula",
    "audience": "internal",
    "aliases": [
      "potential subscription",
      "subscription potential"
    ],
    "short": "What a deal's plan would bill a year: the plan's monthly list price × 12. Enterprise has no list price and is priced as Scale, a floor.",
    "formula": "The plan's monthly price from the Stripe plan catalog × 12. The plan is, in order: the paying subscription's (from Stripe), the deal's, the waitlist form's plan of interest, else the tier's default plan (approved 2026-10-14: Tier 1–2 Enterprise, 3 Scale, 4 Business, 5 Individual). A plan set on the deal always overrides the tier's. Priced as Scale, an Enterprise figure is shown as \"at least\" it.",
    "seeAlso": [
      "potential-activation-revenue",
      "sales-deal-plan",
      "deal-calc-inputs"
    ]
  },
  {
    "id": "potential-activation-revenue",
    "term": "Potential activation revenue",
    "kind": "formula",
    "audience": "internal",
    "aliases": [
      "potential activations",
      "activation potential"
    ],
    "short": "What a deal's expected activations would bill a year: production × activation share, ÷ 12 a month on the graduated per-activation price, × 12.",
    "formula": "Products a year × activation share = expected activations; ÷ 12 = monthly activations, priced on the graduated activation schedule synced from Stripe (each band at its own rate), × 12. Needs products a year; the same model as the Signups grid's potential revenue (which prices every unit).",
    "seeAlso": [
      "potential-subscription-revenue",
      "deal-calc-inputs",
      "activation-share"
    ]
  },
  {
    "id": "activation-share",
    "term": "Activation share",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "share of production activated",
      "activation penetration",
      "penetration"
    ],
    "short": "The share of a deal's annual production expected to activate. Defaults by tier: 20% for Tier 1–2 (a pilot), 100% for Tier 3–5. Editable per deal.",
    "long": "A large house pilots Vera on a slice of its range before rolling it out; a smaller brand tags everything. So a deal's predicted activations are its production × this share, not its whole production. The defaults are the investor model's penetration assumption (Tier 1/2 20% as a pilot, Tier 3/4 full adoption; Tier 5 set to full too). A deal with no [client tier](term:client-tier) uses the default of the tier its production suggests. Set it on Edit deal; blank goes back to the default.",
    "seeAlso": [
      "deal-anticipated-value",
      "potential-activation-revenue",
      "tag-margin",
      "client-tier"
    ]
  },
  {
    "id": "tag-margin",
    "term": "Tag margin",
    "kind": "formula",
    "audience": "internal",
    "aliases": [
      "tag margin per year",
      "tags margin",
      "+ tags"
    ],
    "formula": "Expected activations (production × activation share) × tag unit cost × tag markup, a year, USD. Tag cost and markup are Sales settings; the cost is a placeholder until the supply price list.",
    "unit": "USD",
    "short": "What Vera expects to make a year selling a deal's tags: expected tagged units × tag unit cost × markup. A separate line, never inside predicted ARR.",
    "long": "Each activation needs a tag, so the tagged units are the deal's expected activations. Tags are hardware: margin on goods sold, not recurring revenue, so adding them to [predicted ARR](term:deal-anticipated-value) would overstate it. They are shown beside it, as \"+ tags\" under the pipeline tiles, and summed with it only as [predicted annual value](term:predicted-annual-value). The tag unit cost is a placeholder until the supply price list lands (VRG-143); both settings sit in Sales → Tag economics.",
    "seeAlso": [
      "predicted-annual-value",
      "activation-share",
      "deal-anticipated-value"
    ]
  },
  {
    "id": "predicted-annual-value",
    "term": "Predicted annual value",
    "kind": "formula",
    "audience": "internal",
    "aliases": [
      "annual value",
      "deal annual value"
    ],
    "formula": "Predicted ARR (USD) + tag margin a year. None when the predicted ARR is unknown or not in USD.",
    "unit": "USD",
    "short": "Everything a deal is expected to be worth to Vera a year: predicted ARR plus tag margin. The pipeline totals stay ARR; this is per deal.",
    "long": "It answers \"what is this deal worth in total\" while keeping the recurring part and the hardware part apart: [predicted ARR](term:deal-anticipated-value) is subscription plus activations, [tag margin](term:tag-margin) is the tags. A manual ARR figure overrides only the ARR half; tag margin is still calculated from the deal's inputs.",
    "seeAlso": [
      "deal-anticipated-value",
      "tag-margin"
    ]
  },
  {
    "id": "sales-pilot-subscription",
    "term": "Pilot ($0)",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "pilot",
      "$0 pilot",
      "pilot plan",
      "free pilot"
    ],
    "short": "The company is on a live, active $0 subscription (a $0 plan or a comped base fee) with no paid invoice in the last 60 days. Not committed and not paying.",
    "long": "Shown as its own chip on the deal card instead of Committed. It becomes paying, and the deal can move to Won automatically, as soon as a paid invoice above $0 lands, for example for metered activations. A usage-billed plan with no recent invoice is not called a pilot.",
    "seeAlso": [
      "paying-subscription",
      "sales-committed-arr",
      "sales-auto-won"
    ]
  },
  {
    "id": "contact-card",
    "term": "Contact card",
    "kind": "term",
    "audience": "brand",
    "aliases": [
      "business card details",
      "vcard",
      "save contact"
    ],
    "short": "A person's details (name, title, company, phone, email, website, photo and social links) that a product carries and a viewer with access can save to their phone.",
    "long": "Set on the product page, in the Contact card section, and shared by every [certificate](term:certificate) of that [product](term:product). It is not part of the public certificate page: a viewer only receives it once they have access. Where the product asks for an email first, that is after they share theirs; where it does not, anyone who opens the certificate; and the [owner](term:owner) always. The certificate page then shows a Contact section with a Save contact button that adds the person, photo and social links included, to the phone's contacts. Built for business cards, where the tag is the card and the contact card is the person on it. Leave every field blank for no contact card.",
    "seeAlso": [
      "certificate",
      "product",
      "owner",
      "email-requirement"
    ]
  },
  {
    "id": "founders-list",
    "term": "Founder's List",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "founders list",
      "design partner",
      "design partner terms",
      "founding brand"
    ],
    "short": "The early-partner programme: brands Spencer selects and manages himself, who get a waived base fee and a bank of activations for named deliverables.",
    "long": "A trade, not a discount. The two things Vera gives up are the base fee for a term and a block of activations, and neither touches the published activation rate — the only sanctioned rate concession is a negotiated Enterprise contract, and \"your first hundred are on me\" and \"your rate is $15\" are the same money with opposite anchors. What Vera receives is ranked: placement and durability feedback from a real production run first, photography of the visible mark on high-end work second, a case study and quote in a segment Vera has none in third, and the name and imagery rights that make the rest usable. The programme caps itself, because Spencer personally manages every account on it — which is why membership is recorded as a field on the [sales company](term:sales-company) at all: without a count, nothing shows how full the list is. The terms actually applied sit on the deal: the [waived base](term:founders-list-waived-base) and the [activation bank](term:founders-list-activation-bank). Externally the programme is \"Founder's List\"; internally it is still \"design partner\", which is the word the pricing canon and investors use. Never name a brand on the list publicly without that brand's written permission.",
    "seeAlso": [
      "founders-list-founding-member",
      "founders-list-member",
      "founders-list-invited",
      "founders-list-alumni",
      "sales-company"
    ]
  },
  {
    "id": "founders-list-founding-member",
    "term": "Founding member (Founder's List)",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "founding member",
      "one of the first five"
    ],
    "short": "One of the first five brands on the Founder's List. A permanent attribute, and the state the term sheet prints.",
    "long": "Scarcity by time, which is real while it lasts and expires by design: the waived base and the activation bank close when pricing goes public, and after that a brand joining the list is a [member](term:founders-list-member) rather than a founding one. Five is a judgement call, not a measurement — it matches the field-visit target of at least one named pilot per city across the first cities — and the [programme](term:founders-list) no longer depends on it, since the list caps itself. It is distinguished from Member everywhere it is shown, because that distinction is the whole of what \"founding\" buys.",
    "seeAlso": [
      "founders-list",
      "founders-list-member"
    ]
  },
  {
    "id": "founders-list-member",
    "term": "Member (Founder's List)",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "founders list member"
    ],
    "short": "A brand on the Founder's List that joined after the founding five. Occupies a seat: Spencer is managing the account personally.",
    "long": "The [programme](term:founders-list) is deliberately named for the founder rather than for the moment, so it outlives \"founding\": a brand selected later is a member of the same list without being one of the first five. Members and founding members are the two states that occupy a seat, so they are what a count of how full the list is adds up — an [invitation](term:founders-list-invited) has not been accepted and an [alumnus](term:founders-list-alumni) has handed their seat back, and neither costs founder calendar.",
    "seeAlso": [
      "founders-list",
      "founders-list-founding-member",
      "founders-list-alumni"
    ]
  },
  {
    "id": "founders-list-invited",
    "term": "Invited (Founder's List)",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "invited to the founders list"
    ],
    "short": "A brand that has been offered Founder's List terms and has not signed. Not a seat on the list yet.",
    "long": "The state that exists before anything is agreed, and the reason membership is recorded on the [sales company](term:sales-company) rather than on a platform tenant — a brand under discussion has no tenant row to carry it. An invitation is counted separately from a seat, because an offer nobody has accepted costs no founder time. Do not offer the terms unprompted: offering before there is an objection tells the brand the price was soft.",
    "seeAlso": [
      "founders-list",
      "founders-list-member"
    ]
  },
  {
    "id": "founders-list-alumni",
    "term": "Alumni (Founder's List)",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "founders list alumni",
      "alumnus"
    ],
    "short": "A brand whose Founder's List term has ended. Still named as part of the list; no longer occupying a seat.",
    "long": "The badge is permanent by design — it is the cheapest thing on the give side and the one most often wanted — so a finished term does not remove a brand from the list's history, and permissions already granted for published material survive the term ending. What ends is the [waived base](term:founders-list-waived-base) and the founder-managed seat, which is why an alumnus is not counted against the cap.",
    "seeAlso": [
      "founders-list",
      "founders-list-member",
      "founders-list-waived-base"
    ]
  },
  {
    "id": "founders-list-waived-base",
    "term": "Waived base (Founder's List)",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "waived base fee",
      "base fee waived",
      "waiver"
    ],
    "short": "Months of subscription base fee not charged under Founder's List terms. The base only — the activation rate is untouched.",
    "long": "The preferred lever of the two, and for anchoring rather than cost: the subscription is a 1–5% revenue floor, so waiving it forgoes deferred margin on a small line while keeping the activation rate — 95–99% of revenue — entirely off the table. Six months is the usual term. Its failure mode is a cliff: a base-fee invoice appears on a date in month 7, the classic churn moment, which is why the term end date is recorded on the [deal](term:sales-deal) and surfaced before it arrives, and why the [activation bank](term:founders-list-activation-bank) is timed to land as the waiver expires. The term is set per deal, because a second deal at the same brand has its own agreement or none.",
    "seeAlso": [
      "founders-list",
      "founders-list-activation-bank",
      "sales-deal"
    ]
  },
  {
    "id": "founders-list-activation-bank",
    "term": "Activation bank (Founder's List)",
    "kind": "term",
    "audience": "internal",
    "aliases": [
      "activation bank",
      "bank of activations",
      "released on delivery"
    ],
    "short": "A block of extra activations granted under Founder's List terms, released when the partner delivers the named items rather than at signing.",
    "long": "Sized to the brand's first production run and never to a round number, because a round number invites a round-number counter. Released on completion rather than up front, which is what turns a gift into a trade: the partner controls the outcome and no clawback conversation is needed. Timing it to land as the [waived base](term:founders-list-waived-base) expires is deliberate — unused activations roll over while the brand is subscribed and are forfeited on cancellation, so month 7 arrives at a brand holding credit rather than at a brand getting its first invoice. It is additional to the [included activations](term:included-activations) every plan already ships, and the release date is recorded on the deal.",
    "seeAlso": [
      "founders-list",
      "founders-list-waived-base",
      "included-activations"
    ]
  },
  {
    "id": "content-recommendation",
    "term": "Recommendation card",
    "aliases": [
      "content recommendation",
      "recommendation cards",
      "suggest 5 more"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "A proposed article for one content idea: title, summary, type, terms, planned graphics, research and outline. \"Write this\" selects it.",
    "long": "\"Suggest 5 more\" takes the top-scored ideas that have no card yet, picks five that are varied, and builds a card for each from templates and the database. No model is called. The [variety rules](term:content-variety-rules) decide which five. Choosing \"Write this\" sets the idea's type and title from the card and moves the idea to [Brief](term:content-idea-status-brief), which is the selection. The only approval comes later, on the draft. Rejecting one needs a reason, and the idea's terms are never proposed again. Writing the draft from a chosen card is a later step and is not built yet."
  },
  {
    "id": "content-variety-rules",
    "term": "Variety rules",
    "aliases": [
      "batch variety",
      "relaxed rule",
      "relaxation order"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "What keeps a batch of five recommendations varied: different primary terms and industries, at least three solutions, and a mix of explainers and perspectives.",
    "long": "The rules are: no two cards share a primary industry or a primary term, within the batch or with pieces approved or published in the last 90 days; the batch spans at least three solutions; it holds at least one explainer and one perspective; and a term Vera already owns is never a primary. When the pool is too small to meet them all, rules are relaxed one at a time in a fixed order, and the batch states which ones were relaxed and why. Graphics have their own rules, so no five cards plan only one kind of graphic."
  },
  {
    "id": "content-corpus-feed",
    "term": "Corpus idea feed",
    "aliases": [
      "corpus feed",
      "feed run",
      "run feed"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "After each source poll, new corpus items that match a tracked SEO term become content ideas or add to one. Database only, no model calls.",
    "long": "Matching is lexical: titles and bodies are stemmed and compared with the approved keywords, and the strongest match sets the score. An item matching a term with an open idea adds to that idea rather than starting a new one. A term Vera already ranks for, or one with a closed idea, is skipped. An item that matches no term is counted as an [unmapped item](term:content-unmapped-item). Each run is logged with the items seen, ideas created and merged, guests suggested, the unmapped count and the [backfill items](term:backfill-item) it held back. A competitor's feed or a regulatory source works the same way, and its ideas are labelled by origin."
  },
  {
    "id": "content-unmapped-item",
    "term": "Unmapped item",
    "aliases": [
      "unmapped items",
      "unmapped"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "A corpus item that matched no tracked SEO term, so it produced no idea. Listed with its candidate keywords for review.",
    "long": "A high unmapped count usually means the keyword list is missing terms the market is writing about, not that the items are irrelevant. The candidate keywords are phrases pulled from the title and are only for review: nothing is added to the keyword master from this list. Items stay unmapped until a later run finds a term for them."
  },
  {
    "id": "content-needs-chart-block",
    "term": "Needs chart block",
    "aliases": [
      "chart block"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "A planned bar or line chart that the article page cannot render yet. The graphic can be planned, but a chart block must be built first.",
    "long": "Article pages render timelines, comparisons, flows and number panels today. Bar and line charts are allowed in a plan when the research has the data for them, since the editorial rules call for infographics mixed with charts, but the card flags them so nobody writes a piece that the page cannot show. At most a third of a piece's graphics are charts."
  },
  {
    "id": "competitor-feed",
    "term": "Competitor feed",
    "aliases": [
      "competitor feeds",
      "add feed",
      "feed count",
      "found automatically"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "A market source linked to a tracked SEO competitor, usually found and added automatically, so its posts arrive in the corpus and can become content ideas.",
    "long": "When a competitor is approved, the [feed finder](term:competitor-feed-finder) looks for its blog and adds every feed it finds. The feed is enabled, polled on the same schedule as any other source, and never calls a model, because auto-extract stays off. Feeds added this way are marked \"Found automatically\" on the Sources tab. One can also be added by hand with \"Add feed\", where \"Find feed\" suggests a URL and saves nothing. A feed URL is registered once, ignoring http or https, www and a trailing slash."
  },
  {
    "id": "competitor-feed-finder",
    "term": "Feed finder",
    "aliases": [
      "feed discovery",
      "re-scan feeds",
      "no feed found"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "Looks for an approved competitor's blog feed and adds what it finds: RSS first, then a sitemap of blog, news or insights pages.",
    "long": "It runs when a competitor is approved, on the market poller's half-hourly pass for competitors that are due, and on \"Re-scan feeds\". It reads the feeds a site declares, then common feed addresses, then robots.txt and the sitemap. It keeps a sitemap only when it lists posts under a blog, news or insights path, and the poller then takes only those pages. Every request goes through the same guarded fetcher as the poller: public addresses only, robots.txt respected, the VeraBot user agent, one request a second per site. A competitor with nothing found shows \"No feed found\" and the date, and is retried no more than weekly. Re-scanning a single competitor by hand can look again sooner. New sources start with auto-extract off, so the finder never costs a model call. See [competitor feed](term:competitor-feed)."
  },
  {
    "id": "backfill-item",
    "term": "Backfill item",
    "aliases": [
      "backfill",
      "backlog item",
      "reference only"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "A corpus item kept for reference only: published more than 90 days before Vera fetched it, or undated on its source's first poll.",
    "long": "A new feed usually brings its whole archive on the first poll. Without a limit, one competitor's blog would flood the idea list and the review queue with years-old posts. A backfill item stays in the corpus, so it can be searched and cited, but it never becomes a content idea or a VERAfied guest suggestion, and it never appears in the review queue. Each feed run reports how many items it held back this way. Auto-extract never runs on a backfill item."
  },
  {
    "id": "vera-feature-shipped",
    "term": "Shipped (feature matrix)",
    "aliases": [
      "shipped feature"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "Vera's cell in the Competitor research feature matrix: a customer can use this on Vera today.",
    "long": "Shipped is the stricter of the two values Vera's column can hold. A capability that is built but not yet released is planned, not shipped, even where the website already describes it. Only a shipped or planned row is ever written back to the website's comparison page.",
    "seeAlso": [
      "vera-feature-planned"
    ]
  },
  {
    "id": "vera-feature-planned",
    "term": "Planned (feature matrix)",
    "aliases": [
      "planned feature"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "Vera's cell in the Competitor research feature matrix: announced, but not something a customer can use on Vera today.",
    "long": "Planned covers capabilities that are built and awaiting release as well as ones announced and not yet built. The note on the row says which, and who announced it when.",
    "seeAlso": [
      "vera-feature-shipped"
    ]
  },
  {
    "id": "competitor-price-point",
    "term": "Price point",
    "aliases": [
      "competitor price",
      "competitor pricing"
    ],
    "kind": "term",
    "audience": "internal",
    "short": "One competitor plan or price, as their own page states it, with that page and the date it was read.",
    "long": "A price change is recorded as a new price point; the one it replaces is kept as history rather than edited, so Competitor research can show what a competitor charged and when. A plan with no public figure is recorded with no price.",
    "seeAlso": []
  }
] as const
